// NDIS Price Guide Data and Lookup Functions
// Based on NDIS Price Guide 2025-26

export const PRICE_GUIDE_VERSION = '2025-26';
export const EFFECTIVE_DATE = '2025-07-01';

// Geographic regions with multipliers
export const REGIONS = {
    'metro': {
        name: 'Metropolitan',
        description: 'Major metropolitan areas',
        multiplier: 1.0
    },
    'regional': {
        name: 'Regional',
        description: 'Regional centres and surrounding areas',
        multiplier: 1.0
    },
    'remote': {
        name: 'Remote',
        description: 'Remote areas of Australia',
        multiplier: 1.4
    },
    'very-remote': {
        name: 'Very Remote',
        description: 'Very remote areas of Australia',
        multiplier: 1.65
    }
};

// Support categories
export const SUPPORT_CATEGORIES = {
    'core-supports': {
        name: 'Core Supports',
        description: 'Supports to help with everyday activities',
        color: '#C4654A',
        icon: 'heart',
        subcategories: {
            'daily-activities': {
                name: 'Daily Activities',
                description: 'Assistance with daily personal activities',
                budgetKey: 'coreSupports.categories.dailyActivities'
            },
            'consumables': {
                name: 'Consumables',
                description: 'Everyday items participants may need',
                budgetKey: 'coreSupports.categories.consumables'
            },
            'social-community': {
                name: 'Social & Community Participation',
                description: 'Support to participate in community activities',
                budgetKey: 'coreSupports.categories.socialCommunity'
            },
            'transport': {
                name: 'Transport',
                description: 'Transport to access the community',
                budgetKey: 'coreSupports.categories.transport'
            }
        }
    },
    'capacity-building': {
        name: 'Capacity Building',
        description: 'Supports to build independence and skills',
        color: '#7A9A7E',
        icon: 'trending-up',
        subcategories: {
            'support-coordination': {
                name: 'Support Coordination',
                description: 'Help to understand and use NDIS plans',
                budgetKey: 'capacityBuilding.categories.supportCoordination'
            },
            'improved-daily-living': {
                name: 'Improved Daily Living',
                description: 'Therapy and training for daily living skills',
                budgetKey: 'capacityBuilding.categories.improvedDailyLiving'
            },
            'improved-living-arrangements': {
                name: 'Improved Living Arrangements',
                description: 'Help to find and maintain housing',
                budgetKey: 'capacityBuilding.categories.improvedLivingArrangements'
            },
            'employment': {
                name: 'Finding & Keeping a Job',
                description: 'Employment-related supports',
                budgetKey: 'capacityBuilding.categories.findingAndKeepingAJob'
            },
            'health-wellbeing': {
                name: 'Improved Health & Wellbeing',
                description: 'Supports to improve health and wellbeing',
                budgetKey: 'capacityBuilding.categories.improvedHealthAndWellbeing'
            },
            'relationships': {
                name: 'Improved Relationships',
                description: 'Supports to build positive relationships',
                budgetKey: 'capacityBuilding.categories.improvedRelationships'
            },
            'learning': {
                name: 'Improved Learning',
                description: 'Supports for learning and education',
                budgetKey: 'capacityBuilding.categories.improvedLearning'
            }
        }
    },
    'capital-supports': {
        name: 'Capital Supports',
        description: 'Investment in assistive technology and modifications',
        color: '#4A7C6F',
        icon: 'tool',
        subcategories: {
            'assistive-tech': {
                name: 'Assistive Technology',
                description: 'Equipment and technology to increase independence',
                budgetKey: 'capitalSupports.categories.assistiveTechnology'
            },
            'home-mods': {
                name: 'Home Modifications',
                description: 'Changes to the home to improve accessibility',
                budgetKey: 'capitalSupports.categories.homeModifications'
            },
            'vehicle-mods': {
                name: 'Vehicle Modifications',
                description: 'Changes to vehicles for accessibility',
                budgetKey: 'capitalSupports.categories.vehicleModifications'
            }
        }
    },
    'sda': {
        name: 'Specialist Disability Accommodation',
        description: 'Specialist housing for eligible participants',
        color: '#5C5650',
        icon: 'home',
        subcategories: {
            'basic': {
                name: 'Basic',
                description: 'Basic accessible housing',
                budgetKey: null
            },
            'improved-liveability': {
                name: 'Improved Liveability',
                description: 'Housing with improved accessibility features',
                budgetKey: null
            },
            'fully-accessible': {
                name: 'Fully Accessible',
                description: 'Fully wheelchair accessible housing',
                budgetKey: null
            },
            'robust': {
                name: 'Robust',
                description: 'Housing with reinforced construction',
                budgetKey: null
            },
            'high-physical-support': {
                name: 'High Physical Support',
                description: 'Housing for high physical support needs',
                budgetKey: null
            }
        }
    }
};

// Support types
export const SUPPORT_TYPES = {
    'ongoing': {
        name: 'Ongoing',
        description: 'Regular, recurring support services',
        icon: 'repeat'
    },
    'episodic': {
        name: 'Episodic',
        description: 'Support provided as needed or during specific periods',
        icon: 'calendar'
    },
    'one-off': {
        name: 'One-off',
        description: 'Single purchase or service',
        icon: 'shopping-bag'
    }
};

// NDIS Support Items with pricing
export const SUPPORT_ITEMS = {
  "01_002_0107_1_1": {
    "itemNumber": "01_002_0107_1_1",
    "name": "Assistance With Self-Care Activities - Standard",
    "shortName": "Assistance With Self-Care Activities",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 78.81,
    "weekdayEveningRate": 78.81,
    "saturdayRate": 78.81,
    "sundayRate": 78.81,
    "publicHolidayRate": 78.81,
    "nightRate": 78.81,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assistance With Self-Care Activities - Standard - Weekday Night",
    "regionalPricing": {
      "weekday": {
        "national": 78.81,
        "remote": 110.33,
        "veryRemote": 118.22
      },
      "saturday": {
        "national": 78.81,
        "remote": 110.33,
        "veryRemote": 118.22
      },
      "sunday": {
        "national": 78.81,
        "remote": 110.33,
        "veryRemote": 118.22
      },
      "publicHoliday": {
        "national": 78.81,
        "remote": 110.33,
        "veryRemote": 118.22
      }
    }
  },
  "01_004_0107_1_1": {
    "itemNumber": "01_004_0107_1_1",
    "name": "Assistance with Personal Domestic Activities",
    "shortName": "Assistance with Personal Domestic Activities",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 59.06,
    "weekdayEveningRate": 59.06,
    "saturdayRate": 59.06,
    "sundayRate": 59.06,
    "publicHolidayRate": 59.06,
    "nightRate": 59.06,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assistance with Personal Domestic Activities",
    "regionalPricing": {
      "weekday": {
        "national": 59.06,
        "remote": 82.68,
        "veryRemote": 88.59
      },
      "saturday": {
        "national": 59.06,
        "remote": 82.68,
        "veryRemote": 88.59
      },
      "sunday": {
        "national": 59.06,
        "remote": 82.68,
        "veryRemote": 88.59
      },
      "publicHoliday": {
        "national": 59.06,
        "remote": 82.68,
        "veryRemote": 88.59
      }
    }
  },
  "01_010_0107_1_1": {
    "itemNumber": "01_010_0107_1_1",
    "name": "Assistance with Self-Care Activities",
    "shortName": "Assistance with Self-Care Activities",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 297.6,
    "weekdayEveningRate": 297.6,
    "saturdayRate": 297.6,
    "sundayRate": 297.6,
    "publicHolidayRate": 297.6,
    "nightRate": 297.6,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assistance with Self-Care Activities - Night-Time Sleepover",
    "regionalPricing": {
      "weekday": {
        "national": 297.6,
        "remote": 416.64,
        "veryRemote": 446.4
      },
      "saturday": {
        "national": 297.6,
        "remote": 416.64,
        "veryRemote": 446.4
      },
      "sunday": {
        "national": 297.6,
        "remote": 416.64,
        "veryRemote": 446.4
      },
      "publicHoliday": {
        "national": 297.6,
        "remote": 416.64,
        "veryRemote": 446.4
      }
    }
  },
  "01_011_0107_1_1": {
    "itemNumber": "01_011_0107_1_1",
    "name": "Assistance With Self-Care Activities - Standard",
    "shortName": "Assistance With Self-Care Activities",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 70.23,
    "weekdayEveningRate": 70.23,
    "saturdayRate": 70.23,
    "sundayRate": 70.23,
    "publicHolidayRate": 70.23,
    "nightRate": 70.23,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assistance With Self-Care Activities - Standard - Weekday Daytime",
    "regionalPricing": {
      "weekday": {
        "national": 70.23,
        "remote": 70.23,
        "veryRemote": 98.32
      },
      "saturday": {
        "national": 70.23,
        "remote": 70.23,
        "veryRemote": 98.32
      },
      "sunday": {
        "national": 70.23,
        "remote": 70.23,
        "veryRemote": 98.32
      },
      "publicHoliday": {
        "national": 70.23,
        "remote": 70.23,
        "veryRemote": 98.32
      }
    }
  },
  "01_012_0107_1_1": {
    "itemNumber": "01_012_0107_1_1",
    "name": "Assistance With Self-Care Activities - Standard",
    "shortName": "Assistance With Self-Care Activities",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 156.03,
    "weekdayEveningRate": 156.03,
    "saturdayRate": 156.03,
    "sundayRate": 156.03,
    "publicHolidayRate": 156.03,
    "nightRate": 156.03,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assistance With Self-Care Activities - Standard - Public Holiday",
    "regionalPricing": {
      "weekday": {
        "national": 156.03,
        "remote": 218.44,
        "veryRemote": 234.05
      },
      "saturday": {
        "national": 156.03,
        "remote": 218.44,
        "veryRemote": 234.05
      },
      "sunday": {
        "national": 156.03,
        "remote": 218.44,
        "veryRemote": 234.05
      },
      "publicHoliday": {
        "national": 156.03,
        "remote": 218.44,
        "veryRemote": 234.05
      }
    }
  },
  "01_013_0107_1_1": {
    "itemNumber": "01_013_0107_1_1",
    "name": "Assistance With Self-Care Activities - Standard",
    "shortName": "Assistance With Self-Care Activities",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 98.83,
    "weekdayEveningRate": 98.83,
    "saturdayRate": 98.83,
    "sundayRate": 98.83,
    "publicHolidayRate": 98.83,
    "nightRate": 98.83,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assistance With Self-Care Activities - Standard - Saturday",
    "regionalPricing": {
      "weekday": {
        "national": 98.83,
        "remote": 138.36,
        "veryRemote": 148.25
      },
      "saturday": {
        "national": 98.83,
        "remote": 138.36,
        "veryRemote": 148.25
      },
      "sunday": {
        "national": 98.83,
        "remote": 138.36,
        "veryRemote": 148.25
      },
      "publicHoliday": {
        "national": 98.83,
        "remote": 138.36,
        "veryRemote": 148.25
      }
    }
  },
  "01_014_0107_1_1": {
    "itemNumber": "01_014_0107_1_1",
    "name": "Assistance With Self-Care Activities - Standard",
    "shortName": "Assistance With Self-Care Activities",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 127.43,
    "weekdayEveningRate": 127.43,
    "saturdayRate": 127.43,
    "sundayRate": 127.43,
    "publicHolidayRate": 127.43,
    "nightRate": 127.43,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assistance With Self-Care Activities - Standard - Sunday",
    "regionalPricing": {
      "weekday": {
        "national": 127.43,
        "remote": 178.4,
        "veryRemote": 191.15
      },
      "saturday": {
        "national": 127.43,
        "remote": 178.4,
        "veryRemote": 191.15
      },
      "sunday": {
        "national": 127.43,
        "remote": 178.4,
        "veryRemote": 191.15
      },
      "publicHoliday": {
        "national": 127.43,
        "remote": 178.4,
        "veryRemote": 191.15
      }
    }
  },
  "01_015_0107_1_1": {
    "itemNumber": "01_015_0107_1_1",
    "name": "Assistance With Self-Care Activities - Standard",
    "shortName": "Assistance With Self-Care Activities",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 77.38,
    "weekdayEveningRate": 77.38,
    "saturdayRate": 77.38,
    "sundayRate": 77.38,
    "publicHolidayRate": 77.38,
    "nightRate": 77.38,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assistance With Self-Care Activities - Standard - Weekday Evening",
    "regionalPricing": {
      "weekday": {
        "national": 77.38,
        "remote": 108.33,
        "veryRemote": 116.07
      },
      "saturday": {
        "national": 77.38,
        "remote": 108.33,
        "veryRemote": 116.07
      },
      "sunday": {
        "national": 77.38,
        "remote": 108.33,
        "veryRemote": 116.07
      },
      "publicHoliday": {
        "national": 77.38,
        "remote": 108.33,
        "veryRemote": 116.07
      }
    }
  },
  "01_019_0120_1_1": {
    "itemNumber": "01_019_0120_1_1",
    "name": "House or Yard Maintenance",
    "shortName": "House or Yard Maintenance",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 56.98,
    "weekdayEveningRate": 56.98,
    "saturdayRate": 56.98,
    "sundayRate": 56.98,
    "publicHolidayRate": 56.98,
    "nightRate": 56.98,
    "ttp": false,
    "quoteRequired": false,
    "description": "House or Yard Maintenance",
    "regionalPricing": {
      "weekday": {
        "national": 56.98,
        "remote": 79.77,
        "veryRemote": 85.47
      },
      "saturday": {
        "national": 56.98,
        "remote": 79.77,
        "veryRemote": 85.47
      },
      "sunday": {
        "national": 56.98,
        "remote": 79.77,
        "veryRemote": 85.47
      },
      "publicHoliday": {
        "national": 56.98,
        "remote": 79.77,
        "veryRemote": 85.47
      }
    }
  },
  "01_023_0120_1_1": {
    "itemNumber": "01_023_0120_1_1",
    "name": "Assistance with the cost of the preparation and delivery of meals",
    "shortName": "Assistance with the cost of the preparation and delivery of meals",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assistance with the cost of the preparation and delivery of meals",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "01_045_0115_1_1": {
    "itemNumber": "01_045_0115_1_1",
    "name": "STA And Assistance (Inc. Respite) - 1:4",
    "shortName": "STA And Assistance (Inc. Respite)",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "day",
    "weekdayRate": 708.75,
    "weekdayEveningRate": 708.75,
    "saturdayRate": 708.75,
    "sundayRate": 708.75,
    "publicHolidayRate": 708.75,
    "nightRate": 708.75,
    "ttp": false,
    "quoteRequired": false,
    "description": "STA And Assistance (Inc. Respite) - 1:4 - Weekday",
    "regionalPricing": {
      "weekday": {
        "national": 708.75,
        "remote": 992.25,
        "veryRemote": 1063.13
      },
      "saturday": {
        "national": 708.75,
        "remote": 992.25,
        "veryRemote": 1063.13
      },
      "sunday": {
        "national": 708.75,
        "remote": 992.25,
        "veryRemote": 1063.13
      },
      "publicHoliday": {
        "national": 708.75,
        "remote": 992.25,
        "veryRemote": 1063.13
      }
    }
  },
  "01_049_0107_1_1": {
    "itemNumber": "01_049_0107_1_1",
    "name": "Establishment Fee for Personal Care/Participation",
    "shortName": "Establishment Fee for Personal Care/Participation",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 702.3,
    "weekdayEveningRate": 702.3,
    "saturdayRate": 702.3,
    "sundayRate": 702.3,
    "publicHolidayRate": 702.3,
    "nightRate": 702.3,
    "ttp": false,
    "quoteRequired": false,
    "description": "Establishment Fee for Personal Care/Participation",
    "regionalPricing": {
      "weekday": {
        "national": 702.3,
        "remote": 983.22,
        "veryRemote": 1053.45
      },
      "saturday": {
        "national": 702.3,
        "remote": 983.22,
        "veryRemote": 1053.45
      },
      "sunday": {
        "national": 702.3,
        "remote": 983.22,
        "veryRemote": 1053.45
      },
      "publicHoliday": {
        "national": 702.3,
        "remote": 983.22,
        "veryRemote": 1053.45
      }
    }
  },
  "01_051_0115_1_1": {
    "itemNumber": "01_051_0115_1_1",
    "name": "STA And Assistance (Inc. Respite) - 1:4",
    "shortName": "STA And Assistance (Inc. Respite)",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "day",
    "weekdayRate": 860.39,
    "weekdayEveningRate": 860.39,
    "saturdayRate": 860.39,
    "sundayRate": 860.39,
    "publicHolidayRate": 860.39,
    "nightRate": 860.39,
    "ttp": false,
    "quoteRequired": false,
    "description": "STA And Assistance (Inc. Respite) - 1:4 - Saturday",
    "regionalPricing": {
      "weekday": {
        "national": 860.39,
        "remote": 1204.55,
        "veryRemote": 1290.59
      },
      "saturday": {
        "national": 860.39,
        "remote": 1204.55,
        "veryRemote": 1290.59
      },
      "sunday": {
        "national": 860.39,
        "remote": 1204.55,
        "veryRemote": 1290.59
      },
      "publicHoliday": {
        "national": 860.39,
        "remote": 1204.55,
        "veryRemote": 1290.59
      }
    }
  },
  "01_052_0115_1_1": {
    "itemNumber": "01_052_0115_1_1",
    "name": "STA And Assistance (Inc. Respite) - 1:4",
    "shortName": "STA And Assistance (Inc. Respite)",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "day",
    "weekdayRate": 1046.03,
    "weekdayEveningRate": 1046.03,
    "saturdayRate": 1046.03,
    "sundayRate": 1046.03,
    "publicHolidayRate": 1046.03,
    "nightRate": 1046.03,
    "ttp": false,
    "quoteRequired": false,
    "description": "STA And Assistance (Inc. Respite) - 1:4 - Sunday",
    "regionalPricing": {
      "weekday": {
        "national": 1046.03,
        "remote": 1464.44,
        "veryRemote": 1569.05
      },
      "saturday": {
        "national": 1046.03,
        "remote": 1464.44,
        "veryRemote": 1569.05
      },
      "sunday": {
        "national": 1046.03,
        "remote": 1464.44,
        "veryRemote": 1569.05
      },
      "publicHoliday": {
        "national": 1046.03,
        "remote": 1464.44,
        "veryRemote": 1569.05
      }
    }
  },
  "01_053_0115_1_1": {
    "itemNumber": "01_053_0115_1_1",
    "name": "STA And Assistance (Inc. Respite) - 1:4",
    "shortName": "STA And Assistance (Inc. Respite)",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "day",
    "weekdayRate": 1231.67,
    "weekdayEveningRate": 1231.67,
    "saturdayRate": 1231.67,
    "sundayRate": 1231.67,
    "publicHolidayRate": 1231.67,
    "nightRate": 1231.67,
    "ttp": false,
    "quoteRequired": false,
    "description": "STA And Assistance (Inc. Respite) - 1:4 - Public Holiday",
    "regionalPricing": {
      "weekday": {
        "national": 1231.67,
        "remote": 1724.34,
        "veryRemote": 1847.51
      },
      "saturday": {
        "national": 1231.67,
        "remote": 1724.34,
        "veryRemote": 1847.51
      },
      "sunday": {
        "national": 1231.67,
        "remote": 1724.34,
        "veryRemote": 1847.51
      },
      "publicHoliday": {
        "national": 1231.67,
        "remote": 1724.34,
        "veryRemote": 1847.51
      }
    }
  },
  "01_054_0115_1_1": {
    "itemNumber": "01_054_0115_1_1",
    "name": "STA And Assistance (Inc. Respite) - 1:2",
    "shortName": "STA And Assistance (Inc. Respite)",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "day",
    "weekdayRate": 1198.69,
    "weekdayEveningRate": 1198.69,
    "saturdayRate": 1198.69,
    "sundayRate": 1198.69,
    "publicHolidayRate": 1198.69,
    "nightRate": 1198.69,
    "ttp": false,
    "quoteRequired": false,
    "description": "STA And Assistance (Inc. Respite) - 1:2 - Weekday",
    "regionalPricing": {
      "weekday": {
        "national": 1198.69,
        "remote": 1678.17,
        "veryRemote": 1798.04
      },
      "saturday": {
        "national": 1198.69,
        "remote": 1678.17,
        "veryRemote": 1798.04
      },
      "sunday": {
        "national": 1198.69,
        "remote": 1678.17,
        "veryRemote": 1798.04
      },
      "publicHoliday": {
        "national": 1198.69,
        "remote": 1678.17,
        "veryRemote": 1798.04
      }
    }
  },
  "01_055_0115_1_1": {
    "itemNumber": "01_055_0115_1_1",
    "name": "STA And Assistance (Inc. Respite) - 1:2",
    "shortName": "STA And Assistance (Inc. Respite)",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "day",
    "weekdayRate": 1501.97,
    "weekdayEveningRate": 1501.97,
    "saturdayRate": 1501.97,
    "sundayRate": 1501.97,
    "publicHolidayRate": 1501.97,
    "nightRate": 1501.97,
    "ttp": false,
    "quoteRequired": false,
    "description": "STA And Assistance (Inc. Respite) - 1:2 - Saturday",
    "regionalPricing": {
      "weekday": {
        "national": 1501.97,
        "remote": 2102.76,
        "veryRemote": 2252.96
      },
      "saturday": {
        "national": 1501.97,
        "remote": 2102.76,
        "veryRemote": 2252.96
      },
      "sunday": {
        "national": 1501.97,
        "remote": 2102.76,
        "veryRemote": 2252.96
      },
      "publicHoliday": {
        "national": 1501.97,
        "remote": 2102.76,
        "veryRemote": 2252.96
      }
    }
  },
  "01_056_0115_1_1": {
    "itemNumber": "01_056_0115_1_1",
    "name": "STA And Assistance (Inc. Respite) - 1:2",
    "shortName": "STA And Assistance (Inc. Respite)",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "day",
    "weekdayRate": 1873.25,
    "weekdayEveningRate": 1873.25,
    "saturdayRate": 1873.25,
    "sundayRate": 1873.25,
    "publicHolidayRate": 1873.25,
    "nightRate": 1873.25,
    "ttp": false,
    "quoteRequired": false,
    "description": "STA And Assistance (Inc. Respite) - 1:2 - Sunday",
    "regionalPricing": {
      "weekday": {
        "national": 1873.25,
        "remote": 2622.55,
        "veryRemote": 2809.88
      },
      "saturday": {
        "national": 1873.25,
        "remote": 2622.55,
        "veryRemote": 2809.88
      },
      "sunday": {
        "national": 1873.25,
        "remote": 2622.55,
        "veryRemote": 2809.88
      },
      "publicHoliday": {
        "national": 1873.25,
        "remote": 2622.55,
        "veryRemote": 2809.88
      }
    }
  },
  "01_057_0115_1_1": {
    "itemNumber": "01_057_0115_1_1",
    "name": "STA And Assistance (Inc. Respite) - 1:2",
    "shortName": "STA And Assistance (Inc. Respite)",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "day",
    "weekdayRate": 2244.53,
    "weekdayEveningRate": 2244.53,
    "saturdayRate": 2244.53,
    "sundayRate": 2244.53,
    "publicHolidayRate": 2244.53,
    "nightRate": 2244.53,
    "ttp": false,
    "quoteRequired": false,
    "description": "STA And Assistance (Inc. Respite) - 1:2 - Public Holiday",
    "regionalPricing": {
      "weekday": {
        "national": 2244.53,
        "remote": 3142.34,
        "veryRemote": 3366.8
      },
      "saturday": {
        "national": 2244.53,
        "remote": 3142.34,
        "veryRemote": 3366.8
      },
      "sunday": {
        "national": 2244.53,
        "remote": 3142.34,
        "veryRemote": 3366.8
      },
      "publicHoliday": {
        "national": 2244.53,
        "remote": 3142.34,
        "veryRemote": 3366.8
      }
    }
  },
  "01_058_0115_1_1": {
    "itemNumber": "01_058_0115_1_1",
    "name": "STA And Assistance (Inc. Respite) - 1:1",
    "shortName": "STA And Assistance (Inc. Respite)",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "day",
    "weekdayRate": 2178.57,
    "weekdayEveningRate": 2178.57,
    "saturdayRate": 2178.57,
    "sundayRate": 2178.57,
    "publicHolidayRate": 2178.57,
    "nightRate": 2178.57,
    "ttp": false,
    "quoteRequired": false,
    "description": "STA And Assistance (Inc. Respite) - 1:1 - Weekday",
    "regionalPricing": {
      "weekday": {
        "national": 2178.57,
        "remote": 3050,
        "veryRemote": 3267.86
      },
      "saturday": {
        "national": 2178.57,
        "remote": 3050,
        "veryRemote": 3267.86
      },
      "sunday": {
        "national": 2178.57,
        "remote": 3050,
        "veryRemote": 3267.86
      },
      "publicHoliday": {
        "national": 2178.57,
        "remote": 3050,
        "veryRemote": 3267.86
      }
    }
  },
  "01_059_0115_1_1": {
    "itemNumber": "01_059_0115_1_1",
    "name": "STA And Assistance (Inc. Respite) - 1:1",
    "shortName": "STA And Assistance (Inc. Respite)",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "day",
    "weekdayRate": 2785.13,
    "weekdayEveningRate": 2785.13,
    "saturdayRate": 2785.13,
    "sundayRate": 2785.13,
    "publicHolidayRate": 2785.13,
    "nightRate": 2785.13,
    "ttp": false,
    "quoteRequired": false,
    "description": "STA And Assistance (Inc. Respite) - 1:1 - Saturday",
    "regionalPricing": {
      "weekday": {
        "national": 2785.13,
        "remote": 3899.18,
        "veryRemote": 4177.7
      },
      "saturday": {
        "national": 2785.13,
        "remote": 3899.18,
        "veryRemote": 4177.7
      },
      "sunday": {
        "national": 2785.13,
        "remote": 3899.18,
        "veryRemote": 4177.7
      },
      "publicHoliday": {
        "national": 2785.13,
        "remote": 3899.18,
        "veryRemote": 4177.7
      }
    }
  },
  "01_060_0115_1_1": {
    "itemNumber": "01_060_0115_1_1",
    "name": "STA And Assistance (Inc. Respite) - 1:1",
    "shortName": "STA And Assistance (Inc. Respite)",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "day",
    "weekdayRate": 3527.69,
    "weekdayEveningRate": 3527.69,
    "saturdayRate": 3527.69,
    "sundayRate": 3527.69,
    "publicHolidayRate": 3527.69,
    "nightRate": 3527.69,
    "ttp": false,
    "quoteRequired": false,
    "description": "STA And Assistance (Inc. Respite) - 1:1 - Sunday",
    "regionalPricing": {
      "weekday": {
        "national": 3527.69,
        "remote": 4938.77,
        "veryRemote": 5291.54
      },
      "saturday": {
        "national": 3527.69,
        "remote": 4938.77,
        "veryRemote": 5291.54
      },
      "sunday": {
        "national": 3527.69,
        "remote": 4938.77,
        "veryRemote": 5291.54
      },
      "publicHoliday": {
        "national": 3527.69,
        "remote": 4938.77,
        "veryRemote": 5291.54
      }
    }
  },
  "01_061_0115_1_1": {
    "itemNumber": "01_061_0115_1_1",
    "name": "STA And Assistance (Inc. Respite) - 1:1",
    "shortName": "STA And Assistance (Inc. Respite)",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "day",
    "weekdayRate": 4270.25,
    "weekdayEveningRate": 4270.25,
    "saturdayRate": 4270.25,
    "sundayRate": 4270.25,
    "publicHolidayRate": 4270.25,
    "nightRate": 4270.25,
    "ttp": false,
    "quoteRequired": false,
    "description": "STA And Assistance (Inc. Respite) - 1:1 - Public Holiday",
    "regionalPricing": {
      "weekday": {
        "national": 4270.25,
        "remote": 5978.35,
        "veryRemote": 6405.38
      },
      "saturday": {
        "national": 4270.25,
        "remote": 5978.35,
        "veryRemote": 6405.38
      },
      "sunday": {
        "national": 4270.25,
        "remote": 5978.35,
        "veryRemote": 6405.38
      },
      "publicHoliday": {
        "national": 4270.25,
        "remote": 5978.35,
        "veryRemote": 6405.38
      }
    }
  },
  "01_062_0115_1_1": {
    "itemNumber": "01_062_0115_1_1",
    "name": "STA And Assistance (Inc. Respite) - 1:3",
    "shortName": "STA And Assistance (Inc. Respite)",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "day",
    "weekdayRate": 872.06,
    "weekdayEveningRate": 872.06,
    "saturdayRate": 872.06,
    "sundayRate": 872.06,
    "publicHolidayRate": 872.06,
    "nightRate": 872.06,
    "ttp": false,
    "quoteRequired": false,
    "description": "STA And Assistance (Inc. Respite) - 1:3 - Weekday",
    "regionalPricing": {
      "weekday": {
        "national": 872.06,
        "remote": 1220.88,
        "veryRemote": 1308.09
      },
      "saturday": {
        "national": 872.06,
        "remote": 1220.88,
        "veryRemote": 1308.09
      },
      "sunday": {
        "national": 872.06,
        "remote": 1220.88,
        "veryRemote": 1308.09
      },
      "publicHoliday": {
        "national": 872.06,
        "remote": 1220.88,
        "veryRemote": 1308.09
      }
    }
  },
  "01_063_0115_1_1": {
    "itemNumber": "01_063_0115_1_1",
    "name": "STA And Assistance (Inc. Respite) - 1:3",
    "shortName": "STA And Assistance (Inc. Respite)",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "day",
    "weekdayRate": 1074.25,
    "weekdayEveningRate": 1074.25,
    "saturdayRate": 1074.25,
    "sundayRate": 1074.25,
    "publicHolidayRate": 1074.25,
    "nightRate": 1074.25,
    "ttp": false,
    "quoteRequired": false,
    "description": "STA And Assistance (Inc. Respite) - 1:3 - Saturday",
    "regionalPricing": {
      "weekday": {
        "national": 1074.25,
        "remote": 1503.95,
        "veryRemote": 1611.38
      },
      "saturday": {
        "national": 1074.25,
        "remote": 1503.95,
        "veryRemote": 1611.38
      },
      "sunday": {
        "national": 1074.25,
        "remote": 1503.95,
        "veryRemote": 1611.38
      },
      "publicHoliday": {
        "national": 1074.25,
        "remote": 1503.95,
        "veryRemote": 1611.38
      }
    }
  },
  "01_064_0115_1_1": {
    "itemNumber": "01_064_0115_1_1",
    "name": "STA And Assistance (Inc. Respite) - 1:3",
    "shortName": "STA And Assistance (Inc. Respite)",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "day",
    "weekdayRate": 1321.77,
    "weekdayEveningRate": 1321.77,
    "saturdayRate": 1321.77,
    "sundayRate": 1321.77,
    "publicHolidayRate": 1321.77,
    "nightRate": 1321.77,
    "ttp": false,
    "quoteRequired": false,
    "description": "STA And Assistance (Inc. Respite) - 1:3 - Sunday",
    "regionalPricing": {
      "weekday": {
        "national": 1321.77,
        "remote": 1850.48,
        "veryRemote": 1982.66
      },
      "saturday": {
        "national": 1321.77,
        "remote": 1850.48,
        "veryRemote": 1982.66
      },
      "sunday": {
        "national": 1321.77,
        "remote": 1850.48,
        "veryRemote": 1982.66
      },
      "publicHoliday": {
        "national": 1321.77,
        "remote": 1850.48,
        "veryRemote": 1982.66
      }
    }
  },
  "01_065_0115_1_1": {
    "itemNumber": "01_065_0115_1_1",
    "name": "STA And Assistance (Inc. Respite) - 1:3",
    "shortName": "STA And Assistance (Inc. Respite)",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "day",
    "weekdayRate": 1569.29,
    "weekdayEveningRate": 1569.29,
    "saturdayRate": 1569.29,
    "sundayRate": 1569.29,
    "publicHolidayRate": 1569.29,
    "nightRate": 1569.29,
    "ttp": false,
    "quoteRequired": false,
    "description": "STA And Assistance (Inc. Respite) - 1:3 - Public Holiday",
    "regionalPricing": {
      "weekday": {
        "national": 1569.29,
        "remote": 2197.01,
        "veryRemote": 2353.94
      },
      "saturday": {
        "national": 1569.29,
        "remote": 2197.01,
        "veryRemote": 2353.94
      },
      "sunday": {
        "national": 1569.29,
        "remote": 2197.01,
        "veryRemote": 2353.94
      },
      "publicHoliday": {
        "national": 1569.29,
        "remote": 2197.01,
        "veryRemote": 2353.94
      }
    }
  },
  "01_066_0115_1_1": {
    "itemNumber": "01_066_0115_1_1",
    "name": "Unplanned onsite shared supports in Specialist Week Disability Accommodation",
    "shortName": "Unplanned onsite shared supports in Specialist Week Disability Accommodation",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "",
    "weekdayRate": 1542.71,
    "weekdayEveningRate": 1542.71,
    "saturdayRate": 1542.71,
    "sundayRate": 1542.71,
    "publicHolidayRate": 1542.71,
    "nightRate": 1542.71,
    "ttp": false,
    "quoteRequired": false,
    "description": "Unplanned onsite shared supports in Specialist Week Disability Accommodation",
    "regionalPricing": {
      "weekday": {
        "national": 1542.71,
        "remote": 2159.79,
        "veryRemote": 2314.07
      },
      "saturday": {
        "national": 1542.71,
        "remote": 2159.79,
        "veryRemote": 2314.07
      },
      "sunday": {
        "national": 1542.71,
        "remote": 2159.79,
        "veryRemote": 2314.07
      },
      "publicHoliday": {
        "national": 1542.71,
        "remote": 2159.79,
        "veryRemote": 2314.07
      }
    }
  },
  "01_082_0115_1_1": {
    "itemNumber": "01_082_0115_1_1",
    "name": "Medium Term Accommodation",
    "shortName": "Medium Term Accommodation",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "day",
    "weekdayRate": 155.68,
    "weekdayEveningRate": 155.68,
    "saturdayRate": 155.68,
    "sundayRate": 155.68,
    "publicHolidayRate": 155.68,
    "nightRate": 155.68,
    "ttp": false,
    "quoteRequired": false,
    "description": "Medium Term Accommodation",
    "regionalPricing": {
      "weekday": {
        "national": 155.68,
        "remote": 217.95,
        "veryRemote": 233.52
      },
      "saturday": {
        "national": 155.68,
        "remote": 217.95,
        "veryRemote": 233.52
      },
      "sunday": {
        "national": 155.68,
        "remote": 217.95,
        "veryRemote": 233.52
      },
      "publicHoliday": {
        "national": 155.68,
        "remote": 217.95,
        "veryRemote": 233.52
      }
    }
  },
  "01_134_0117_8_1": {
    "itemNumber": "01_134_0117_8_1",
    "name": "Capacity Building and Training in Self- Management and Plan Management",
    "shortName": "Capacity Building and Training in Self- Management and Plan Management",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 80.06,
    "weekdayEveningRate": 80.06,
    "saturdayRate": 80.06,
    "sundayRate": 80.06,
    "publicHolidayRate": 80.06,
    "nightRate": 80.06,
    "ttp": false,
    "quoteRequired": false,
    "description": "Capacity Building and Training in Self- Management and Plan Management",
    "regionalPricing": {
      "weekday": {
        "national": 80.06,
        "remote": 112.08,
        "veryRemote": 120.09
      },
      "saturday": {
        "national": 80.06,
        "remote": 112.08,
        "veryRemote": 120.09
      },
      "sunday": {
        "national": 80.06,
        "remote": 112.08,
        "veryRemote": 120.09
      },
      "publicHoliday": {
        "national": 80.06,
        "remote": 112.08,
        "veryRemote": 120.09
      }
    }
  },
  "01_200_0115_1_1": {
    "itemNumber": "01_200_0115_1_1",
    "name": "Assistance With Self-Care Activities in a STA",
    "shortName": "Assistance With Self-Care Activities in a STA",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 70.23,
    "weekdayEveningRate": 70.23,
    "saturdayRate": 70.23,
    "sundayRate": 70.23,
    "publicHolidayRate": 70.23,
    "nightRate": 70.23,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assistance With Self-Care Activities in a STA - Weekday Daytime",
    "regionalPricing": {
      "weekday": {
        "national": 70.23,
        "remote": 98.32,
        "veryRemote": 105.35
      },
      "saturday": {
        "national": 70.23,
        "remote": 98.32,
        "veryRemote": 105.35
      },
      "sunday": {
        "national": 70.23,
        "remote": 98.32,
        "veryRemote": 105.35
      },
      "publicHoliday": {
        "national": 70.23,
        "remote": 98.32,
        "veryRemote": 105.35
      }
    }
  },
  "01_201_0115_1_1": {
    "itemNumber": "01_201_0115_1_1",
    "name": "Assistance With Self-Care Activities in a STA",
    "shortName": "Assistance With Self-Care Activities in a STA",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 77.38,
    "weekdayEveningRate": 77.38,
    "saturdayRate": 77.38,
    "sundayRate": 77.38,
    "publicHolidayRate": 77.38,
    "nightRate": 77.38,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assistance With Self-Care Activities in a STA - Weekday Evening",
    "regionalPricing": {
      "weekday": {
        "national": 77.38,
        "remote": 108.33,
        "veryRemote": 116.07
      },
      "saturday": {
        "national": 77.38,
        "remote": 108.33,
        "veryRemote": 116.07
      },
      "sunday": {
        "national": 77.38,
        "remote": 108.33,
        "veryRemote": 116.07
      },
      "publicHoliday": {
        "national": 77.38,
        "remote": 108.33,
        "veryRemote": 116.07
      }
    }
  },
  "01_202_0115_1_1": {
    "itemNumber": "01_202_0115_1_1",
    "name": "Assistance With Self-Care Activities in a STA",
    "shortName": "Assistance With Self-Care Activities in a STA",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 98.83,
    "weekdayEveningRate": 98.83,
    "saturdayRate": 98.83,
    "sundayRate": 98.83,
    "publicHolidayRate": 98.83,
    "nightRate": 98.83,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assistance With Self-Care Activities in a STA - Saturday",
    "regionalPricing": {
      "weekday": {
        "national": 98.83,
        "remote": 138.36,
        "veryRemote": 148.25
      },
      "saturday": {
        "national": 98.83,
        "remote": 138.36,
        "veryRemote": 148.25
      },
      "sunday": {
        "national": 98.83,
        "remote": 138.36,
        "veryRemote": 148.25
      },
      "publicHoliday": {
        "national": 98.83,
        "remote": 138.36,
        "veryRemote": 148.25
      }
    }
  },
  "01_203_0115_1_1": {
    "itemNumber": "01_203_0115_1_1",
    "name": "Assistance With Self-Care Activities in a STA",
    "shortName": "Assistance With Self-Care Activities in a STA",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 127.43,
    "weekdayEveningRate": 127.43,
    "saturdayRate": 127.43,
    "sundayRate": 127.43,
    "publicHolidayRate": 127.43,
    "nightRate": 127.43,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assistance With Self-Care Activities in a STA - Sunday",
    "regionalPricing": {
      "weekday": {
        "national": 127.43,
        "remote": 178.4,
        "veryRemote": 191.15
      },
      "saturday": {
        "national": 127.43,
        "remote": 178.4,
        "veryRemote": 191.15
      },
      "sunday": {
        "national": 127.43,
        "remote": 178.4,
        "veryRemote": 191.15
      },
      "publicHoliday": {
        "national": 127.43,
        "remote": 178.4,
        "veryRemote": 191.15
      }
    }
  },
  "01_204_0115_1_1": {
    "itemNumber": "01_204_0115_1_1",
    "name": "Assistance With Self-Care Activities in a STA",
    "shortName": "Assistance With Self-Care Activities in a STA",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 156.03,
    "weekdayEveningRate": 156.03,
    "saturdayRate": 156.03,
    "sundayRate": 156.03,
    "publicHolidayRate": 156.03,
    "nightRate": 156.03,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assistance With Self-Care Activities in a STA - Public Holiday",
    "regionalPricing": {
      "weekday": {
        "national": 156.03,
        "remote": 218.44,
        "veryRemote": 234.05
      },
      "saturday": {
        "national": 156.03,
        "remote": 218.44,
        "veryRemote": 234.05
      },
      "sunday": {
        "national": 156.03,
        "remote": 218.44,
        "veryRemote": 234.05
      },
      "publicHoliday": {
        "national": 156.03,
        "remote": 218.44,
        "veryRemote": 234.05
      }
    }
  },
  "01_205_0115_1_1": {
    "itemNumber": "01_205_0115_1_1",
    "name": "Assistance With Self-Care Activities in a STA",
    "shortName": "Assistance With Self-Care Activities in a STA",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 78.81,
    "weekdayEveningRate": 78.81,
    "saturdayRate": 78.81,
    "sundayRate": 78.81,
    "publicHolidayRate": 78.81,
    "nightRate": 78.81,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assistance With Self-Care Activities in a STA - Weekday Night",
    "regionalPricing": {
      "weekday": {
        "national": 78.81,
        "remote": 110.33,
        "veryRemote": 118.22
      },
      "saturday": {
        "national": 78.81,
        "remote": 110.33,
        "veryRemote": 118.22
      },
      "sunday": {
        "national": 78.81,
        "remote": 110.33,
        "veryRemote": 118.22
      },
      "publicHoliday": {
        "national": 78.81,
        "remote": 110.33,
        "veryRemote": 118.22
      }
    }
  },
  "01_450_0107_1_1": {
    "itemNumber": "01_450_0107_1_1",
    "name": "Intensive and Complex Behaviour Supports",
    "shortName": "Intensive and Complex Behaviour Supports",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 75.98,
    "weekdayEveningRate": 75.98,
    "saturdayRate": 75.98,
    "sundayRate": 75.98,
    "publicHolidayRate": 75.98,
    "nightRate": 75.98,
    "ttp": false,
    "quoteRequired": false,
    "description": "Intensive and Complex Behaviour Supports - Weekday Daytime",
    "regionalPricing": {
      "weekday": {
        "national": 75.98,
        "remote": 106.37,
        "veryRemote": 113.97
      },
      "saturday": {
        "national": 75.98,
        "remote": 106.37,
        "veryRemote": 113.97
      },
      "sunday": {
        "national": 75.98,
        "remote": 106.37,
        "veryRemote": 113.97
      },
      "publicHoliday": {
        "national": 75.98,
        "remote": 106.37,
        "veryRemote": 113.97
      }
    }
  },
  "01_450_0115_1_1": {
    "itemNumber": "01_450_0115_1_1",
    "name": "Intensive and Complex Behaviour Supports",
    "shortName": "Intensive and Complex Behaviour Supports",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 75.98,
    "weekdayEveningRate": 75.98,
    "saturdayRate": 75.98,
    "sundayRate": 75.98,
    "publicHolidayRate": 75.98,
    "nightRate": 75.98,
    "ttp": false,
    "quoteRequired": false,
    "description": "Intensive and Complex Behaviour Supports - Weekday Daytime",
    "regionalPricing": {
      "weekday": {
        "national": 75.98,
        "remote": 106.37,
        "veryRemote": 113.97
      },
      "saturday": {
        "national": 75.98,
        "remote": 106.37,
        "veryRemote": 113.97
      },
      "sunday": {
        "national": 75.98,
        "remote": 106.37,
        "veryRemote": 113.97
      },
      "publicHoliday": {
        "national": 75.98,
        "remote": 106.37,
        "veryRemote": 113.97
      }
    }
  },
  "01_451_0107_1_1": {
    "itemNumber": "01_451_0107_1_1",
    "name": "Intensive and Complex Behaviour Supports",
    "shortName": "Intensive and Complex Behaviour Supports",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 83.72,
    "weekdayEveningRate": 83.72,
    "saturdayRate": 83.72,
    "sundayRate": 83.72,
    "publicHolidayRate": 83.72,
    "nightRate": 83.72,
    "ttp": false,
    "quoteRequired": false,
    "description": "Intensive and Complex Behaviour Supports - Weekday Evening",
    "regionalPricing": {
      "weekday": {
        "national": 83.72,
        "remote": 117.21,
        "veryRemote": 125.58
      },
      "saturday": {
        "national": 83.72,
        "remote": 117.21,
        "veryRemote": 125.58
      },
      "sunday": {
        "national": 83.72,
        "remote": 117.21,
        "veryRemote": 125.58
      },
      "publicHoliday": {
        "national": 83.72,
        "remote": 117.21,
        "veryRemote": 125.58
      }
    }
  },
  "01_451_0115_1_1": {
    "itemNumber": "01_451_0115_1_1",
    "name": "Intensive and Complex Behaviour Supports",
    "shortName": "Intensive and Complex Behaviour Supports",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 83.72,
    "weekdayEveningRate": 83.72,
    "saturdayRate": 83.72,
    "sundayRate": 83.72,
    "publicHolidayRate": 83.72,
    "nightRate": 83.72,
    "ttp": false,
    "quoteRequired": false,
    "description": "Intensive and Complex Behaviour Supports - Weekday Evening",
    "regionalPricing": {
      "weekday": {
        "national": 83.72,
        "remote": 117.21,
        "veryRemote": 125.58
      },
      "saturday": {
        "national": 83.72,
        "remote": 117.21,
        "veryRemote": 125.58
      },
      "sunday": {
        "national": 83.72,
        "remote": 117.21,
        "veryRemote": 125.58
      },
      "publicHoliday": {
        "national": 83.72,
        "remote": 117.21,
        "veryRemote": 125.58
      }
    }
  },
  "01_452_0107_1_1": {
    "itemNumber": "01_452_0107_1_1",
    "name": "Intensive and Complex Behaviour Supports",
    "shortName": "Intensive and Complex Behaviour Supports",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 106.93,
    "weekdayEveningRate": 106.93,
    "saturdayRate": 106.93,
    "sundayRate": 106.93,
    "publicHolidayRate": 106.93,
    "nightRate": 106.93,
    "ttp": false,
    "quoteRequired": false,
    "description": "Intensive and Complex Behaviour Supports - Saturday",
    "regionalPricing": {
      "weekday": {
        "national": 106.93,
        "remote": 149.7,
        "veryRemote": 160.4
      },
      "saturday": {
        "national": 106.93,
        "remote": 149.7,
        "veryRemote": 160.4
      },
      "sunday": {
        "national": 106.93,
        "remote": 149.7,
        "veryRemote": 160.4
      },
      "publicHoliday": {
        "national": 106.93,
        "remote": 149.7,
        "veryRemote": 160.4
      }
    }
  },
  "01_452_0115_1_1": {
    "itemNumber": "01_452_0115_1_1",
    "name": "Intensive and Complex Behaviour Supports – Saturday",
    "shortName": "Intensive and Complex Behaviour Supports – Saturday",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 106.93,
    "weekdayEveningRate": 106.93,
    "saturdayRate": 106.93,
    "sundayRate": 106.93,
    "publicHolidayRate": 106.93,
    "nightRate": 106.93,
    "ttp": false,
    "quoteRequired": false,
    "description": "Intensive and Complex Behaviour Supports – Saturday",
    "regionalPricing": {
      "weekday": {
        "national": 106.93,
        "remote": 149.7,
        "veryRemote": 160.4
      },
      "saturday": {
        "national": 106.93,
        "remote": 149.7,
        "veryRemote": 160.4
      },
      "sunday": {
        "national": 106.93,
        "remote": 149.7,
        "veryRemote": 160.4
      },
      "publicHoliday": {
        "national": 106.93,
        "remote": 149.7,
        "veryRemote": 160.4
      }
    }
  },
  "01_453_0107_1_1": {
    "itemNumber": "01_453_0107_1_1",
    "name": "Intensive and Complex Behaviour Supports",
    "shortName": "Intensive and Complex Behaviour Supports",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 137.87,
    "weekdayEveningRate": 137.87,
    "saturdayRate": 137.87,
    "sundayRate": 137.87,
    "publicHolidayRate": 137.87,
    "nightRate": 137.87,
    "ttp": false,
    "quoteRequired": false,
    "description": "Intensive and Complex Behaviour Supports - Sunday",
    "regionalPricing": {
      "weekday": {
        "national": 137.87,
        "remote": 193.02,
        "veryRemote": 206.81
      },
      "saturday": {
        "national": 137.87,
        "remote": 193.02,
        "veryRemote": 206.81
      },
      "sunday": {
        "national": 137.87,
        "remote": 193.02,
        "veryRemote": 206.81
      },
      "publicHoliday": {
        "national": 137.87,
        "remote": 193.02,
        "veryRemote": 206.81
      }
    }
  },
  "01_453_0115_1_1": {
    "itemNumber": "01_453_0115_1_1",
    "name": "Intensive and Complex Behaviour Supports – Sunday",
    "shortName": "Intensive and Complex Behaviour Supports – Sunday",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 137.87,
    "weekdayEveningRate": 137.87,
    "saturdayRate": 137.87,
    "sundayRate": 137.87,
    "publicHolidayRate": 137.87,
    "nightRate": 137.87,
    "ttp": false,
    "quoteRequired": false,
    "description": "Intensive and Complex Behaviour Supports – Sunday",
    "regionalPricing": {
      "weekday": {
        "national": 137.87,
        "remote": 193.02,
        "veryRemote": 206.81
      },
      "saturday": {
        "national": 137.87,
        "remote": 193.02,
        "veryRemote": 206.81
      },
      "sunday": {
        "national": 137.87,
        "remote": 193.02,
        "veryRemote": 206.81
      },
      "publicHoliday": {
        "national": 137.87,
        "remote": 193.02,
        "veryRemote": 206.81
      }
    }
  },
  "01_454_0107_1_1": {
    "itemNumber": "01_454_0107_1_1",
    "name": "Intensive and Complex Behaviour Supports",
    "shortName": "Intensive and Complex Behaviour Supports",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 168.81,
    "weekdayEveningRate": 168.81,
    "saturdayRate": 168.81,
    "sundayRate": 168.81,
    "publicHolidayRate": 168.81,
    "nightRate": 168.81,
    "ttp": false,
    "quoteRequired": false,
    "description": "Intensive and Complex Behaviour Supports - Public Holiday",
    "regionalPricing": {
      "weekday": {
        "national": 168.81,
        "remote": 236.33,
        "veryRemote": 253.22
      },
      "saturday": {
        "national": 168.81,
        "remote": 236.33,
        "veryRemote": 253.22
      },
      "sunday": {
        "national": 168.81,
        "remote": 236.33,
        "veryRemote": 253.22
      },
      "publicHoliday": {
        "national": 168.81,
        "remote": 236.33,
        "veryRemote": 253.22
      }
    }
  },
  "01_454_0115_1_1": {
    "itemNumber": "01_454_0115_1_1",
    "name": "Intensive and Complex Behaviour Supports",
    "shortName": "Intensive and Complex Behaviour Supports",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 168.81,
    "weekdayEveningRate": 168.81,
    "saturdayRate": 168.81,
    "sundayRate": 168.81,
    "publicHolidayRate": 168.81,
    "nightRate": 168.81,
    "ttp": false,
    "quoteRequired": false,
    "description": "Intensive and Complex Behaviour Supports - Public Holiday",
    "regionalPricing": {
      "weekday": {
        "national": 168.81,
        "remote": 236.33,
        "veryRemote": 253.22
      },
      "saturday": {
        "national": 168.81,
        "remote": 236.33,
        "veryRemote": 253.22
      },
      "sunday": {
        "national": 168.81,
        "remote": 236.33,
        "veryRemote": 253.22
      },
      "publicHoliday": {
        "national": 168.81,
        "remote": 236.33,
        "veryRemote": 253.22
      }
    }
  },
  "01_455_0107_1_1": {
    "itemNumber": "01_455_0107_1_1",
    "name": "Intensive and Complex Behaviour Supports",
    "shortName": "Intensive and Complex Behaviour Supports",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 85.27,
    "weekdayEveningRate": 85.27,
    "saturdayRate": 85.27,
    "sundayRate": 85.27,
    "publicHolidayRate": 85.27,
    "nightRate": 85.27,
    "ttp": false,
    "quoteRequired": false,
    "description": "Intensive and Complex Behaviour Supports - Weekday Night",
    "regionalPricing": {
      "weekday": {
        "national": 85.27,
        "remote": 119.38,
        "veryRemote": 127.91
      },
      "saturday": {
        "national": 85.27,
        "remote": 119.38,
        "veryRemote": 127.91
      },
      "sunday": {
        "national": 85.27,
        "remote": 119.38,
        "veryRemote": 127.91
      },
      "publicHoliday": {
        "national": 85.27,
        "remote": 119.38,
        "veryRemote": 127.91
      }
    }
  },
  "01_455_0115_1_1": {
    "itemNumber": "01_455_0115_1_1",
    "name": "Intensive and Complex Behaviour Supports",
    "shortName": "Intensive and Complex Behaviour Supports",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 85.27,
    "weekdayEveningRate": 85.27,
    "saturdayRate": 85.27,
    "sundayRate": 85.27,
    "publicHolidayRate": 85.27,
    "nightRate": 85.27,
    "ttp": false,
    "quoteRequired": false,
    "description": "Intensive and Complex Behaviour Supports - Weekday Night",
    "regionalPricing": {
      "weekday": {
        "national": 85.27,
        "remote": 119.38,
        "veryRemote": 127.91
      },
      "saturday": {
        "national": 85.27,
        "remote": 119.38,
        "veryRemote": 127.91
      },
      "sunday": {
        "national": 85.27,
        "remote": 119.38,
        "veryRemote": 127.91
      },
      "publicHoliday": {
        "national": 85.27,
        "remote": 119.38,
        "veryRemote": 127.91
      }
    }
  },
  "01_600_0114_1_1": {
    "itemNumber": "01_600_0114_1_1",
    "name": "Delivery of Health Supports by an Enrolled Nurse",
    "shortName": "Delivery of Health Supports by an Enrolled Nurse",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 99.88,
    "weekdayEveningRate": 99.88,
    "saturdayRate": 99.88,
    "sundayRate": 99.88,
    "publicHolidayRate": 99.88,
    "nightRate": 99.88,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by an Enrolled Nurse - Weekday Daytime",
    "regionalPricing": {
      "weekday": {
        "national": 99.88,
        "remote": 139.83,
        "veryRemote": 149.82
      },
      "saturday": {
        "national": 99.88,
        "remote": 139.83,
        "veryRemote": 149.82
      },
      "sunday": {
        "national": 99.88,
        "remote": 139.83,
        "veryRemote": 149.82
      },
      "publicHoliday": {
        "national": 99.88,
        "remote": 139.83,
        "veryRemote": 149.82
      }
    }
  },
  "01_601_0114_1_1": {
    "itemNumber": "01_601_0114_1_1",
    "name": "Delivery of Health Supports by an Enrolled Nurse",
    "shortName": "Delivery of Health Supports by an Enrolled Nurse",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 110.18,
    "weekdayEveningRate": 110.18,
    "saturdayRate": 110.18,
    "sundayRate": 110.18,
    "publicHolidayRate": 110.18,
    "nightRate": 110.18,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by an Enrolled Nurse - Weekday Evening",
    "regionalPricing": {
      "weekday": {
        "national": 110.18,
        "remote": 154.25,
        "veryRemote": 165.27
      },
      "saturday": {
        "national": 110.18,
        "remote": 154.25,
        "veryRemote": 165.27
      },
      "sunday": {
        "national": 110.18,
        "remote": 154.25,
        "veryRemote": 165.27
      },
      "publicHoliday": {
        "national": 110.18,
        "remote": 154.25,
        "veryRemote": 165.27
      }
    }
  },
  "01_602_0114_1_1": {
    "itemNumber": "01_602_0114_1_1",
    "name": "Delivery of Health Supports by an Enrolled Nurse",
    "shortName": "Delivery of Health Supports by an Enrolled Nurse",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 142.48,
    "weekdayEveningRate": 142.48,
    "saturdayRate": 142.48,
    "sundayRate": 142.48,
    "publicHolidayRate": 142.48,
    "nightRate": 142.48,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by an Enrolled Nurse - Saturday",
    "regionalPricing": {
      "weekday": {
        "national": 142.48,
        "remote": 199.47,
        "veryRemote": 213.72
      },
      "saturday": {
        "national": 142.48,
        "remote": 199.47,
        "veryRemote": 213.72
      },
      "sunday": {
        "national": 142.48,
        "remote": 199.47,
        "veryRemote": 213.72
      },
      "publicHoliday": {
        "national": 142.48,
        "remote": 199.47,
        "veryRemote": 213.72
      }
    }
  },
  "01_603_0114_1_1": {
    "itemNumber": "01_603_0114_1_1",
    "name": "Delivery of Health Supports by an Enrolled Nurse",
    "shortName": "Delivery of Health Supports by an Enrolled Nurse",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 163.79,
    "weekdayEveningRate": 163.79,
    "saturdayRate": 163.79,
    "sundayRate": 163.79,
    "publicHolidayRate": 163.79,
    "nightRate": 163.79,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by an Enrolled Nurse - Sunday",
    "regionalPricing": {
      "weekday": {
        "national": 163.79,
        "remote": 229.31,
        "veryRemote": 245.69
      },
      "saturday": {
        "national": 163.79,
        "remote": 229.31,
        "veryRemote": 245.69
      },
      "sunday": {
        "national": 163.79,
        "remote": 229.31,
        "veryRemote": 245.69
      },
      "publicHoliday": {
        "national": 163.79,
        "remote": 229.31,
        "veryRemote": 245.69
      }
    }
  },
  "01_604_0114_1_1": {
    "itemNumber": "01_604_0114_1_1",
    "name": "Delivery of Health Supports by an Enrolled Nurse",
    "shortName": "Delivery of Health Supports by an Enrolled Nurse",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 185.08,
    "weekdayEveningRate": 185.08,
    "saturdayRate": 185.08,
    "sundayRate": 185.08,
    "publicHolidayRate": 185.08,
    "nightRate": 185.08,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by an Enrolled Nurse - Public Holiday",
    "regionalPricing": {
      "weekday": {
        "national": 185.08,
        "remote": 259.11,
        "veryRemote": 277.62
      },
      "saturday": {
        "national": 185.08,
        "remote": 259.11,
        "veryRemote": 277.62
      },
      "sunday": {
        "national": 185.08,
        "remote": 259.11,
        "veryRemote": 277.62
      },
      "publicHoliday": {
        "national": 185.08,
        "remote": 259.11,
        "veryRemote": 277.62
      }
    }
  },
  "01_605_0114_1_1": {
    "itemNumber": "01_605_0114_1_1",
    "name": "Delivery of Health Supports by an Enrolled Nurse",
    "shortName": "Delivery of Health Supports by an Enrolled Nurse",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 112.22,
    "weekdayEveningRate": 112.22,
    "saturdayRate": 112.22,
    "sundayRate": 112.22,
    "publicHolidayRate": 112.22,
    "nightRate": 112.22,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by an Enrolled Nurse - Weekday Night",
    "regionalPricing": {
      "weekday": {
        "national": 112.22,
        "remote": 157.11,
        "veryRemote": 168.33
      },
      "saturday": {
        "national": 112.22,
        "remote": 157.11,
        "veryRemote": 168.33
      },
      "sunday": {
        "national": 112.22,
        "remote": 157.11,
        "veryRemote": 168.33
      },
      "publicHoliday": {
        "national": 112.22,
        "remote": 157.11,
        "veryRemote": 168.33
      }
    }
  },
  "01_606_0114_1_1": {
    "itemNumber": "01_606_0114_1_1",
    "name": "Delivery of Health Supports by a Registered Nurse",
    "shortName": "Delivery of Health Supports by a Registered Nurse",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 123.65,
    "weekdayEveningRate": 123.65,
    "saturdayRate": 123.65,
    "sundayRate": 123.65,
    "publicHolidayRate": 123.65,
    "nightRate": 123.65,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by a Registered Nurse - Weekday Daytime",
    "regionalPricing": {
      "weekday": {
        "national": 123.65,
        "remote": 173.11,
        "veryRemote": 185.48
      },
      "saturday": {
        "national": 123.65,
        "remote": 173.11,
        "veryRemote": 185.48
      },
      "sunday": {
        "national": 123.65,
        "remote": 173.11,
        "veryRemote": 185.48
      },
      "publicHoliday": {
        "national": 123.65,
        "remote": 173.11,
        "veryRemote": 185.48
      }
    }
  },
  "01_607_0114_1_1": {
    "itemNumber": "01_607_0114_1_1",
    "name": "Delivery of Health Supports by a Registered Nurse",
    "shortName": "Delivery of Health Supports by a Registered Nurse",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 136.41,
    "weekdayEveningRate": 136.41,
    "saturdayRate": 136.41,
    "sundayRate": 136.41,
    "publicHolidayRate": 136.41,
    "nightRate": 136.41,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by a Registered Nurse - Weekday Evening",
    "regionalPricing": {
      "weekday": {
        "national": 136.41,
        "remote": 190.97,
        "veryRemote": 204.62
      },
      "saturday": {
        "national": 136.41,
        "remote": 190.97,
        "veryRemote": 204.62
      },
      "sunday": {
        "national": 136.41,
        "remote": 190.97,
        "veryRemote": 204.62
      },
      "publicHoliday": {
        "national": 136.41,
        "remote": 190.97,
        "veryRemote": 204.62
      }
    }
  },
  "01_608_0114_1_1": {
    "itemNumber": "01_608_0114_1_1",
    "name": "Delivery of Health Supports by a Registered Nurse",
    "shortName": "Delivery of Health Supports by a Registered Nurse",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 176.47,
    "weekdayEveningRate": 176.47,
    "saturdayRate": 176.47,
    "sundayRate": 176.47,
    "publicHolidayRate": 176.47,
    "nightRate": 176.47,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by a Registered Nurse - Saturday",
    "regionalPricing": {
      "weekday": {
        "national": 176.47,
        "remote": 247.06,
        "veryRemote": 264.71
      },
      "saturday": {
        "national": 176.47,
        "remote": 247.06,
        "veryRemote": 264.71
      },
      "sunday": {
        "national": 176.47,
        "remote": 247.06,
        "veryRemote": 264.71
      },
      "publicHoliday": {
        "national": 176.47,
        "remote": 247.06,
        "veryRemote": 264.71
      }
    }
  },
  "01_609_0114_1_1": {
    "itemNumber": "01_609_0114_1_1",
    "name": "Delivery of Health Supports by a Registered Nurse",
    "shortName": "Delivery of Health Supports by a Registered Nurse",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 202.87,
    "weekdayEveningRate": 202.87,
    "saturdayRate": 202.87,
    "sundayRate": 202.87,
    "publicHolidayRate": 202.87,
    "nightRate": 202.87,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by a Registered Nurse - Sunday",
    "regionalPricing": {
      "weekday": {
        "national": 202.87,
        "remote": 284.02,
        "veryRemote": 304.31
      },
      "saturday": {
        "national": 202.87,
        "remote": 284.02,
        "veryRemote": 304.31
      },
      "sunday": {
        "national": 202.87,
        "remote": 284.02,
        "veryRemote": 304.31
      },
      "publicHoliday": {
        "national": 202.87,
        "remote": 284.02,
        "veryRemote": 304.31
      }
    }
  },
  "01_610_0114_1_1": {
    "itemNumber": "01_610_0114_1_1",
    "name": "Delivery of Health Supports by a Registered Nurse",
    "shortName": "Delivery of Health Supports by a Registered Nurse",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 229.27,
    "weekdayEveningRate": 229.27,
    "saturdayRate": 229.27,
    "sundayRate": 229.27,
    "publicHolidayRate": 229.27,
    "nightRate": 229.27,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by a Registered Nurse - Public Holiday",
    "regionalPricing": {
      "weekday": {
        "national": 229.27,
        "remote": 320.98,
        "veryRemote": 343.91
      },
      "saturday": {
        "national": 229.27,
        "remote": 320.98,
        "veryRemote": 343.91
      },
      "sunday": {
        "national": 229.27,
        "remote": 320.98,
        "veryRemote": 343.91
      },
      "publicHoliday": {
        "national": 229.27,
        "remote": 320.98,
        "veryRemote": 343.91
      }
    }
  },
  "01_611_0114_1_1": {
    "itemNumber": "01_611_0114_1_1",
    "name": "Delivery of Health Supports by a Registered Nurse",
    "shortName": "Delivery of Health Supports by a Registered Nurse",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 138.95,
    "weekdayEveningRate": 138.95,
    "saturdayRate": 138.95,
    "sundayRate": 138.95,
    "publicHolidayRate": 138.95,
    "nightRate": 138.95,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by a Registered Nurse - Weekday Night",
    "regionalPricing": {
      "weekday": {
        "national": 138.95,
        "remote": 194.53,
        "veryRemote": 208.43
      },
      "saturday": {
        "national": 138.95,
        "remote": 194.53,
        "veryRemote": 208.43
      },
      "sunday": {
        "national": 138.95,
        "remote": 194.53,
        "veryRemote": 208.43
      },
      "publicHoliday": {
        "national": 138.95,
        "remote": 194.53,
        "veryRemote": 208.43
      }
    }
  },
  "01_612_0114_1_1": {
    "itemNumber": "01_612_0114_1_1",
    "name": "Delivery of Health Supports by a Clinical Nurse",
    "shortName": "Delivery of Health Supports by a Clinical Nurse",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 143.04,
    "weekdayEveningRate": 143.04,
    "saturdayRate": 143.04,
    "sundayRate": 143.04,
    "publicHolidayRate": 143.04,
    "nightRate": 143.04,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by a Clinical Nurse - Weekday Daytime",
    "regionalPricing": {
      "weekday": {
        "national": 143.04,
        "remote": 200.26,
        "veryRemote": 214.56
      },
      "saturday": {
        "national": 143.04,
        "remote": 200.26,
        "veryRemote": 214.56
      },
      "sunday": {
        "national": 143.04,
        "remote": 200.26,
        "veryRemote": 214.56
      },
      "publicHoliday": {
        "national": 143.04,
        "remote": 200.26,
        "veryRemote": 214.56
      }
    }
  },
  "01_613_0114_1_1": {
    "itemNumber": "01_613_0114_1_1",
    "name": "Delivery of Health Supports by a Clinical Nurse",
    "shortName": "Delivery of Health Supports by a Clinical Nurse",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 157.77,
    "weekdayEveningRate": 157.77,
    "saturdayRate": 157.77,
    "sundayRate": 157.77,
    "publicHolidayRate": 157.77,
    "nightRate": 157.77,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by a Clinical Nurse - Weekday Evening",
    "regionalPricing": {
      "weekday": {
        "national": 157.77,
        "remote": 220.88,
        "veryRemote": 236.66
      },
      "saturday": {
        "national": 157.77,
        "remote": 220.88,
        "veryRemote": 236.66
      },
      "sunday": {
        "national": 157.77,
        "remote": 220.88,
        "veryRemote": 236.66
      },
      "publicHoliday": {
        "national": 157.77,
        "remote": 220.88,
        "veryRemote": 236.66
      }
    }
  },
  "01_614_0114_1_1": {
    "itemNumber": "01_614_0114_1_1",
    "name": "Delivery of Health Supports by a Clinical Nurse – Saturday",
    "shortName": "Delivery of Health Supports by a Clinical Nurse – Saturday",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 204.12,
    "weekdayEveningRate": 204.12,
    "saturdayRate": 204.12,
    "sundayRate": 204.12,
    "publicHolidayRate": 204.12,
    "nightRate": 204.12,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by a Clinical Nurse – Saturday",
    "regionalPricing": {
      "weekday": {
        "national": 204.12,
        "remote": 285.77,
        "veryRemote": 306.18
      },
      "saturday": {
        "national": 204.12,
        "remote": 285.77,
        "veryRemote": 306.18
      },
      "sunday": {
        "national": 204.12,
        "remote": 285.77,
        "veryRemote": 306.18
      },
      "publicHoliday": {
        "national": 204.12,
        "remote": 285.77,
        "veryRemote": 306.18
      }
    }
  },
  "01_615_0114_1_1": {
    "itemNumber": "01_615_0114_1_1",
    "name": "Delivery of Health Supports by a Clinical Nurse – Sunday",
    "shortName": "Delivery of Health Supports by a Clinical Nurse – Sunday",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 234.67,
    "weekdayEveningRate": 234.67,
    "saturdayRate": 234.67,
    "sundayRate": 234.67,
    "publicHolidayRate": 234.67,
    "nightRate": 234.67,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by a Clinical Nurse – Sunday",
    "regionalPricing": {
      "weekday": {
        "national": 234.67,
        "remote": 328.54,
        "veryRemote": 352.01
      },
      "saturday": {
        "national": 234.67,
        "remote": 328.54,
        "veryRemote": 352.01
      },
      "sunday": {
        "national": 234.67,
        "remote": 328.54,
        "veryRemote": 352.01
      },
      "publicHoliday": {
        "national": 234.67,
        "remote": 328.54,
        "veryRemote": 352.01
      }
    }
  },
  "01_616_0114_1_1": {
    "itemNumber": "01_616_0114_1_1",
    "name": "Delivery of Health Supports by a Clinical Nurse",
    "shortName": "Delivery of Health Supports by a Clinical Nurse",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 265.2,
    "weekdayEveningRate": 265.2,
    "saturdayRate": 265.2,
    "sundayRate": 265.2,
    "publicHolidayRate": 265.2,
    "nightRate": 265.2,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by a Clinical Nurse - Public Holiday",
    "regionalPricing": {
      "weekday": {
        "national": 265.2,
        "remote": 371.28,
        "veryRemote": 397.8
      },
      "saturday": {
        "national": 265.2,
        "remote": 371.28,
        "veryRemote": 397.8
      },
      "sunday": {
        "national": 265.2,
        "remote": 371.28,
        "veryRemote": 397.8
      },
      "publicHoliday": {
        "national": 265.2,
        "remote": 371.28,
        "veryRemote": 397.8
      }
    }
  },
  "01_617_0114_1_1": {
    "itemNumber": "01_617_0114_1_1",
    "name": "Delivery of Health Supports by a Clinical Nurse",
    "shortName": "Delivery of Health Supports by a Clinical Nurse",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 160.73,
    "weekdayEveningRate": 160.73,
    "saturdayRate": 160.73,
    "sundayRate": 160.73,
    "publicHolidayRate": 160.73,
    "nightRate": 160.73,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by a Clinical Nurse - Weekday Night",
    "regionalPricing": {
      "weekday": {
        "national": 160.73,
        "remote": 225.02,
        "veryRemote": 241.1
      },
      "saturday": {
        "national": 160.73,
        "remote": 225.02,
        "veryRemote": 241.1
      },
      "sunday": {
        "national": 160.73,
        "remote": 225.02,
        "veryRemote": 241.1
      },
      "publicHoliday": {
        "national": 160.73,
        "remote": 225.02,
        "veryRemote": 241.1
      }
    }
  },
  "01_618_0114_1_1": {
    "itemNumber": "01_618_0114_1_1",
    "name": "Delivery of Health Supports by a Clinical Nurse Consultant",
    "shortName": "Delivery of Health Supports by a Clinical Nurse Consultant",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 169.16,
    "weekdayEveningRate": 169.16,
    "saturdayRate": 169.16,
    "sundayRate": 169.16,
    "publicHolidayRate": 169.16,
    "nightRate": 169.16,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by a Clinical Nurse Consultant - Weekday Daytime",
    "regionalPricing": {
      "weekday": {
        "national": 169.16,
        "remote": 236.82,
        "veryRemote": 253.74
      },
      "saturday": {
        "national": 169.16,
        "remote": 236.82,
        "veryRemote": 253.74
      },
      "sunday": {
        "national": 169.16,
        "remote": 236.82,
        "veryRemote": 253.74
      },
      "publicHoliday": {
        "national": 169.16,
        "remote": 236.82,
        "veryRemote": 253.74
      }
    }
  },
  "01_619_0114_1_1": {
    "itemNumber": "01_619_0114_1_1",
    "name": "Delivery of Health Supports by a Clinical Nurse Consultant",
    "shortName": "Delivery of Health Supports by a Clinical Nurse Consultant",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 186.63,
    "weekdayEveningRate": 186.63,
    "saturdayRate": 186.63,
    "sundayRate": 186.63,
    "publicHolidayRate": 186.63,
    "nightRate": 186.63,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by a Clinical Nurse Consultant - Weekday Evening",
    "regionalPricing": {
      "weekday": {
        "national": 186.63,
        "remote": 261.28,
        "veryRemote": 279.95
      },
      "saturday": {
        "national": 186.63,
        "remote": 261.28,
        "veryRemote": 279.95
      },
      "sunday": {
        "national": 186.63,
        "remote": 261.28,
        "veryRemote": 279.95
      },
      "publicHoliday": {
        "national": 186.63,
        "remote": 261.28,
        "veryRemote": 279.95
      }
    }
  },
  "01_620_0114_1_1": {
    "itemNumber": "01_620_0114_1_1",
    "name": "Delivery of Health Supports by a Clinical Nurse Consultant",
    "shortName": "Delivery of Health Supports by a Clinical Nurse Consultant",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 241.52,
    "weekdayEveningRate": 241.52,
    "saturdayRate": 241.52,
    "sundayRate": 241.52,
    "publicHolidayRate": 241.52,
    "nightRate": 241.52,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by a Clinical Nurse Consultant - Saturday",
    "regionalPricing": {
      "weekday": {
        "national": 241.52,
        "remote": 338.13,
        "veryRemote": 362.28
      },
      "saturday": {
        "national": 241.52,
        "remote": 338.13,
        "veryRemote": 362.28
      },
      "sunday": {
        "national": 241.52,
        "remote": 338.13,
        "veryRemote": 362.28
      },
      "publicHoliday": {
        "national": 241.52,
        "remote": 338.13,
        "veryRemote": 362.28
      }
    }
  },
  "01_621_0114_1_1": {
    "itemNumber": "01_621_0114_1_1",
    "name": "Delivery of Health Supports by a Clinical Nurse Consultant",
    "shortName": "Delivery of Health Supports by a Clinical Nurse Consultant",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 277.69,
    "weekdayEveningRate": 277.69,
    "saturdayRate": 277.69,
    "sundayRate": 277.69,
    "publicHolidayRate": 277.69,
    "nightRate": 277.69,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by a Clinical Nurse Consultant - Sunday",
    "regionalPricing": {
      "weekday": {
        "national": 277.69,
        "remote": 388.77,
        "veryRemote": 416.54
      },
      "saturday": {
        "national": 277.69,
        "remote": 388.77,
        "veryRemote": 416.54
      },
      "sunday": {
        "national": 277.69,
        "remote": 388.77,
        "veryRemote": 416.54
      },
      "publicHoliday": {
        "national": 277.69,
        "remote": 388.77,
        "veryRemote": 416.54
      }
    }
  },
  "01_622_0114_1_1": {
    "itemNumber": "01_622_0114_1_1",
    "name": "Delivery of Health Supports by a Clinical Nurse Consultant",
    "shortName": "Delivery of Health Supports by a Clinical Nurse Consultant",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 313.86,
    "weekdayEveningRate": 313.86,
    "saturdayRate": 313.86,
    "sundayRate": 313.86,
    "publicHolidayRate": 313.86,
    "nightRate": 313.86,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by a Clinical Nurse Consultant - Public Holiday",
    "regionalPricing": {
      "weekday": {
        "national": 313.86,
        "remote": 439.4,
        "veryRemote": 470.79
      },
      "saturday": {
        "national": 313.86,
        "remote": 439.4,
        "veryRemote": 470.79
      },
      "sunday": {
        "national": 313.86,
        "remote": 439.4,
        "veryRemote": 470.79
      },
      "publicHoliday": {
        "national": 313.86,
        "remote": 439.4,
        "veryRemote": 470.79
      }
    }
  },
  "01_623_0114_1_1": {
    "itemNumber": "01_623_0114_1_1",
    "name": "Delivery of Health Supports by a Clinical Nurse Consultant",
    "shortName": "Delivery of Health Supports by a Clinical Nurse Consultant",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 190.12,
    "weekdayEveningRate": 190.12,
    "saturdayRate": 190.12,
    "sundayRate": 190.12,
    "publicHolidayRate": 190.12,
    "nightRate": 190.12,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by a Clinical Nurse Consultant - Weekday Night",
    "regionalPricing": {
      "weekday": {
        "national": 190.12,
        "remote": 266.17,
        "veryRemote": 285.18
      },
      "saturday": {
        "national": 190.12,
        "remote": 266.17,
        "veryRemote": 285.18
      },
      "sunday": {
        "national": 190.12,
        "remote": 266.17,
        "veryRemote": 285.18
      },
      "publicHoliday": {
        "national": 190.12,
        "remote": 266.17,
        "veryRemote": 285.18
      }
    }
  },
  "01_624_0114_1_1": {
    "itemNumber": "01_624_0114_1_1",
    "name": "Delivery of Health Supports by a Nurse Practitioner",
    "shortName": "Delivery of Health Supports by a Nurse Practitioner",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 176.85,
    "weekdayEveningRate": 176.85,
    "saturdayRate": 176.85,
    "sundayRate": 176.85,
    "publicHolidayRate": 176.85,
    "nightRate": 176.85,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by a Nurse Practitioner - Weekday Daytime",
    "regionalPricing": {
      "weekday": {
        "national": 176.85,
        "remote": 247.59,
        "veryRemote": 265.28
      },
      "saturday": {
        "national": 176.85,
        "remote": 247.59,
        "veryRemote": 265.28
      },
      "sunday": {
        "national": 176.85,
        "remote": 247.59,
        "veryRemote": 265.28
      },
      "publicHoliday": {
        "national": 176.85,
        "remote": 247.59,
        "veryRemote": 265.28
      }
    }
  },
  "01_625_0114_1_1": {
    "itemNumber": "01_625_0114_1_1",
    "name": "Delivery of Health Supports by a Nurse Practitioner",
    "shortName": "Delivery of Health Supports by a Nurse Practitioner",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 195.09,
    "weekdayEveningRate": 195.09,
    "saturdayRate": 195.09,
    "sundayRate": 195.09,
    "publicHolidayRate": 195.09,
    "nightRate": 195.09,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by a Nurse Practitioner - Weekday Evening",
    "regionalPricing": {
      "weekday": {
        "national": 195.09,
        "remote": 273.13,
        "veryRemote": 292.64
      },
      "saturday": {
        "national": 195.09,
        "remote": 273.13,
        "veryRemote": 292.64
      },
      "sunday": {
        "national": 195.09,
        "remote": 273.13,
        "veryRemote": 292.64
      },
      "publicHoliday": {
        "national": 195.09,
        "remote": 273.13,
        "veryRemote": 292.64
      }
    }
  },
  "01_626_0114_1_1": {
    "itemNumber": "01_626_0114_1_1",
    "name": "Delivery of Health Supports by a Nurse Practitioner – Saturday",
    "shortName": "Delivery of Health Supports by a Nurse Practitioner – Saturday",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 252.51,
    "weekdayEveningRate": 252.51,
    "saturdayRate": 252.51,
    "sundayRate": 252.51,
    "publicHolidayRate": 252.51,
    "nightRate": 252.51,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by a Nurse Practitioner – Saturday",
    "regionalPricing": {
      "weekday": {
        "national": 252.51,
        "remote": 353.51,
        "veryRemote": 378.77
      },
      "saturday": {
        "national": 252.51,
        "remote": 353.51,
        "veryRemote": 378.77
      },
      "sunday": {
        "national": 252.51,
        "remote": 353.51,
        "veryRemote": 378.77
      },
      "publicHoliday": {
        "national": 252.51,
        "remote": 353.51,
        "veryRemote": 378.77
      }
    }
  },
  "01_627_0114_1_1": {
    "itemNumber": "01_627_0114_1_1",
    "name": "Delivery of Health Supports by a Nurse Practitioner – Sunday",
    "shortName": "Delivery of Health Supports by a Nurse Practitioner – Sunday",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 290.33,
    "weekdayEveningRate": 290.33,
    "saturdayRate": 290.33,
    "sundayRate": 290.33,
    "publicHolidayRate": 290.33,
    "nightRate": 290.33,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by a Nurse Practitioner – Sunday",
    "regionalPricing": {
      "weekday": {
        "national": 290.33,
        "remote": 406.46,
        "veryRemote": 435.5
      },
      "saturday": {
        "national": 290.33,
        "remote": 406.46,
        "veryRemote": 435.5
      },
      "sunday": {
        "national": 290.33,
        "remote": 406.46,
        "veryRemote": 435.5
      },
      "publicHoliday": {
        "national": 290.33,
        "remote": 406.46,
        "veryRemote": 435.5
      }
    }
  },
  "01_628_0114_1_1": {
    "itemNumber": "01_628_0114_1_1",
    "name": "Delivery of Health Supports by a Nurse Practitioner",
    "shortName": "Delivery of Health Supports by a Nurse Practitioner",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 328.16,
    "weekdayEveningRate": 328.16,
    "saturdayRate": 328.16,
    "sundayRate": 328.16,
    "publicHolidayRate": 328.16,
    "nightRate": 328.16,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by a Nurse Practitioner - Public Holiday",
    "regionalPricing": {
      "weekday": {
        "national": 328.16,
        "remote": 459.42,
        "veryRemote": 492.24
      },
      "saturday": {
        "national": 328.16,
        "remote": 459.42,
        "veryRemote": 492.24
      },
      "sunday": {
        "national": 328.16,
        "remote": 459.42,
        "veryRemote": 492.24
      },
      "publicHoliday": {
        "national": 328.16,
        "remote": 459.42,
        "veryRemote": 492.24
      }
    }
  },
  "01_629_0114_1_1": {
    "itemNumber": "01_629_0114_1_1",
    "name": "Delivery of Health Supports by a Nurse Practitioner",
    "shortName": "Delivery of Health Supports by a Nurse Practitioner",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 198.75,
    "weekdayEveningRate": 198.75,
    "saturdayRate": 198.75,
    "sundayRate": 198.75,
    "publicHolidayRate": 198.75,
    "nightRate": 198.75,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by a Nurse Practitioner - Weekday Night",
    "regionalPricing": {
      "weekday": {
        "national": 198.75,
        "remote": 278.25,
        "veryRemote": 298.13
      },
      "saturday": {
        "national": 198.75,
        "remote": 278.25,
        "veryRemote": 298.13
      },
      "sunday": {
        "national": 198.75,
        "remote": 278.25,
        "veryRemote": 298.13
      },
      "publicHoliday": {
        "national": 198.75,
        "remote": 278.25,
        "veryRemote": 298.13
      }
    }
  },
  "01_650_0118_1_3": {
    "itemNumber": "01_650_0118_1_3",
    "name": "Assessment Recommendation Therapy or Training - EC - Occupational Therapist",
    "shortName": "Assessment Recommendation Therapy or Training",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 193.99,
    "weekdayEveningRate": 193.99,
    "saturdayRate": 193.99,
    "sundayRate": 193.99,
    "publicHolidayRate": 193.99,
    "nightRate": 193.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assessment Recommendation Therapy or Training - EC - Occupational Therapist",
    "regionalPricing": {
      "weekday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "saturday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "sunday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "publicHoliday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      }
    }
  },
  "01_653_0118_1_3": {
    "itemNumber": "01_653_0118_1_3",
    "name": "Assessment Recommendation Therapy or Training - EC - Speech Pathologist",
    "shortName": "Assessment Recommendation Therapy or Training",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 193.99,
    "weekdayEveningRate": 193.99,
    "saturdayRate": 193.99,
    "sundayRate": 193.99,
    "publicHolidayRate": 193.99,
    "nightRate": 193.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assessment Recommendation Therapy or Training - EC - Speech Pathologist",
    "regionalPricing": {
      "weekday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "saturday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "sunday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "publicHoliday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      }
    }
  },
  "01_661_0128_1_3": {
    "itemNumber": "01_661_0128_1_3",
    "name": "Assessment Recommendation Therapy or Training Supports - Occupational Therapist",
    "shortName": "Assessment Recommendation Therapy or Training Supports",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 193.99,
    "weekdayEveningRate": 193.99,
    "saturdayRate": 193.99,
    "sundayRate": 193.99,
    "publicHolidayRate": 193.99,
    "nightRate": 193.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assessment Recommendation Therapy or Training Supports - Occupational Therapist",
    "regionalPricing": {
      "weekday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "saturday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "sunday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "publicHoliday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      }
    }
  },
  "01_663_0118_1_3": {
    "itemNumber": "01_663_0118_1_3",
    "name": "Assessment Recommendation Therapy or Training - EC - Podiatrist",
    "shortName": "Assessment Recommendation Therapy or Training",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 188.99,
    "weekdayEveningRate": 188.99,
    "saturdayRate": 188.99,
    "sundayRate": 188.99,
    "publicHolidayRate": 188.99,
    "nightRate": 188.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assessment Recommendation Therapy or Training - EC - Podiatrist",
    "regionalPricing": {
      "weekday": {
        "national": 188.99,
        "remote": 264.59,
        "veryRemote": 283.49
      },
      "saturday": {
        "national": 188.99,
        "remote": 264.59,
        "veryRemote": 283.49
      },
      "sunday": {
        "national": 188.99,
        "remote": 264.59,
        "veryRemote": 283.49
      },
      "publicHoliday": {
        "national": 188.99,
        "remote": 264.59,
        "veryRemote": 283.49
      }
    }
  },
  "01_663_0128_1_3": {
    "itemNumber": "01_663_0128_1_3",
    "name": "Assessment Recommendation Therapy or Training Supports - Podiatrist",
    "shortName": "Assessment Recommendation Therapy or Training Supports",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 188.99,
    "weekdayEveningRate": 188.99,
    "saturdayRate": 188.99,
    "sundayRate": 188.99,
    "publicHolidayRate": 188.99,
    "nightRate": 188.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assessment Recommendation Therapy or Training Supports - Podiatrist",
    "regionalPricing": {
      "weekday": {
        "national": 188.99,
        "remote": 264.59,
        "veryRemote": 283.49
      },
      "saturday": {
        "national": 188.99,
        "remote": 264.59,
        "veryRemote": 283.49
      },
      "sunday": {
        "national": 188.99,
        "remote": 264.59,
        "veryRemote": 283.49
      },
      "publicHoliday": {
        "national": 188.99,
        "remote": 264.59,
        "veryRemote": 283.49
      }
    }
  },
  "01_665_0128_1_3": {
    "itemNumber": "01_665_0128_1_3",
    "name": "Assessment Recommendation Therapy or Training Supports - Speech Pathologist",
    "shortName": "Assessment Recommendation Therapy or Training Supports",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 193.99,
    "weekdayEveningRate": 193.99,
    "saturdayRate": 193.99,
    "sundayRate": 193.99,
    "publicHolidayRate": 193.99,
    "nightRate": 193.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assessment Recommendation Therapy or Training Supports - Speech Pathologist",
    "regionalPricing": {
      "weekday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "saturday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "sunday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "publicHoliday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      }
    }
  },
  "01_700_0118_1_3": {
    "itemNumber": "01_700_0118_1_3",
    "name": "Assessment Recommendation Therapy or Training - EC - Psychologist",
    "shortName": "Assessment Recommendation Therapy or Training",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 232.99,
    "weekdayEveningRate": 232.99,
    "saturdayRate": 232.99,
    "sundayRate": 232.99,
    "publicHolidayRate": 232.99,
    "nightRate": 232.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assessment Recommendation Therapy or Training - EC - Psychologist",
    "regionalPricing": {
      "weekday": {
        "national": 232.99,
        "remote": 326.19,
        "veryRemote": 349.49
      },
      "saturday": {
        "national": 232.99,
        "remote": 326.19,
        "veryRemote": 349.49
      },
      "sunday": {
        "national": 232.99,
        "remote": 326.19,
        "veryRemote": 349.49
      },
      "publicHoliday": {
        "national": 232.99,
        "remote": 326.19,
        "veryRemote": 349.49
      }
    }
  },
  "01_701_0128_1_3": {
    "itemNumber": "01_701_0128_1_3",
    "name": "Assessment Recommendation Therapy or s Training Supports - Psychologist",
    "shortName": "Assessment Recommendation Therapy or s Training Supports",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 232.99,
    "weekdayEveningRate": 232.99,
    "saturdayRate": 232.99,
    "sundayRate": 232.99,
    "publicHolidayRate": 232.99,
    "nightRate": 232.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assessment Recommendation Therapy or s Training Supports - Psychologist",
    "regionalPricing": {
      "weekday": {
        "national": 232.99,
        "remote": 326.19,
        "veryRemote": 349.49
      },
      "saturday": {
        "national": 232.99,
        "remote": 326.19,
        "veryRemote": 349.49
      },
      "sunday": {
        "national": 232.99,
        "remote": 326.19,
        "veryRemote": 349.49
      },
      "publicHoliday": {
        "national": 232.99,
        "remote": 326.19,
        "veryRemote": 349.49
      }
    }
  },
  "01_720_0118_1_3": {
    "itemNumber": "01_720_0118_1_3",
    "name": "Assessment Recommendation Therapy or Training - EC - Physiotherapist",
    "shortName": "Assessment Recommendation Therapy or Training",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 183.99,
    "weekdayEveningRate": 183.99,
    "saturdayRate": 183.99,
    "sundayRate": 183.99,
    "publicHolidayRate": 183.99,
    "nightRate": 183.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assessment Recommendation Therapy or Training - EC - Physiotherapist",
    "regionalPricing": {
      "weekday": {
        "national": 183.99,
        "remote": 257.59,
        "veryRemote": 275.99
      },
      "saturday": {
        "national": 183.99,
        "remote": 257.59,
        "veryRemote": 275.99
      },
      "sunday": {
        "national": 183.99,
        "remote": 257.59,
        "veryRemote": 275.99
      },
      "publicHoliday": {
        "national": 183.99,
        "remote": 257.59,
        "veryRemote": 275.99
      }
    }
  },
  "01_721_0128_1_3": {
    "itemNumber": "01_721_0128_1_3",
    "name": "Assessment Recommendation Therapy or Training Supports - Physiotherapist",
    "shortName": "Assessment Recommendation Therapy or Training Supports",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 183.99,
    "weekdayEveningRate": 183.99,
    "saturdayRate": 183.99,
    "sundayRate": 183.99,
    "publicHolidayRate": 183.99,
    "nightRate": 183.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assessment Recommendation Therapy or Training Supports - Physiotherapist",
    "regionalPricing": {
      "weekday": {
        "national": 183.99,
        "remote": 257.59,
        "veryRemote": 275.99
      },
      "saturday": {
        "national": 183.99,
        "remote": 257.59,
        "veryRemote": 275.99
      },
      "sunday": {
        "national": 183.99,
        "remote": 257.59,
        "veryRemote": 275.99
      },
      "publicHoliday": {
        "national": 183.99,
        "remote": 257.59,
        "veryRemote": 275.99
      }
    }
  },
  "01_740_0118_1_3": {
    "itemNumber": "01_740_0118_1_3",
    "name": "Assessment Recommendation Therapy or Training - EC - Other Professional",
    "shortName": "Assessment Recommendation Therapy or Training",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 193.99,
    "weekdayEveningRate": 193.99,
    "saturdayRate": 193.99,
    "sundayRate": 193.99,
    "publicHolidayRate": 193.99,
    "nightRate": 193.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assessment Recommendation Therapy or Training - EC - Other Professional",
    "regionalPricing": {
      "weekday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "saturday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "sunday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "publicHoliday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      }
    }
  },
  "01_741_0128_1_3": {
    "itemNumber": "01_741_0128_1_3",
    "name": "Assessment Recommendation Therapy or Training Supports - Other Professional",
    "shortName": "Assessment Recommendation Therapy or Training Supports",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 193.99,
    "weekdayEveningRate": 193.99,
    "saturdayRate": 193.99,
    "sundayRate": 193.99,
    "publicHolidayRate": 193.99,
    "nightRate": 193.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assessment Recommendation Therapy or Training Supports - Other Professional",
    "regionalPricing": {
      "weekday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "saturday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "sunday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "publicHoliday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      }
    }
  },
  "01_760_0118_1_3": {
    "itemNumber": "01_760_0118_1_3",
    "name": "Assessment Recommendation Therapy or Training - EC - Dietitian",
    "shortName": "Assessment Recommendation Therapy or Training",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 188.99,
    "weekdayEveningRate": 188.99,
    "saturdayRate": 188.99,
    "sundayRate": 188.99,
    "publicHolidayRate": 188.99,
    "nightRate": 188.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assessment Recommendation Therapy or Training - EC - Dietitian",
    "regionalPricing": {
      "weekday": {
        "national": 188.99,
        "remote": 264.59,
        "veryRemote": 283.49
      },
      "saturday": {
        "national": 188.99,
        "remote": 264.59,
        "veryRemote": 283.49
      },
      "sunday": {
        "national": 188.99,
        "remote": 264.59,
        "veryRemote": 283.49
      },
      "publicHoliday": {
        "national": 188.99,
        "remote": 264.59,
        "veryRemote": 283.49
      }
    }
  },
  "01_760_0128_3_3": {
    "itemNumber": "01_760_0128_3_3",
    "name": "Assessment Recommendation Therapy or Training Supports - Dietitian",
    "shortName": "Assessment Recommendation Therapy or Training Supports",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 188.99,
    "weekdayEveningRate": 188.99,
    "saturdayRate": 188.99,
    "sundayRate": 188.99,
    "publicHolidayRate": 188.99,
    "nightRate": 188.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assessment Recommendation Therapy or Training Supports - Dietitian",
    "regionalPricing": {
      "weekday": {
        "national": 188.99,
        "remote": 188.99,
        "veryRemote": 264.59
      },
      "saturday": {
        "national": 188.99,
        "remote": 188.99,
        "veryRemote": 264.59
      },
      "sunday": {
        "national": 188.99,
        "remote": 188.99,
        "veryRemote": 264.59
      },
      "publicHoliday": {
        "national": 188.99,
        "remote": 188.99,
        "veryRemote": 264.59
      }
    }
  },
  "01_799_0102_1_1": {
    "itemNumber": "01_799_0102_1_1",
    "name": "Provider travel - non-labour costs",
    "shortName": "Provider travel",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provider travel - non-labour costs",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "01_799_0106_1_1": {
    "itemNumber": "01_799_0106_1_1",
    "name": "Provider travel - non-labour costs",
    "shortName": "Provider travel",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provider travel - non-labour costs",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "01_799_0107_1_1": {
    "itemNumber": "01_799_0107_1_1",
    "name": "Provider travel - non-labour costs",
    "shortName": "Provider travel",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provider travel - non-labour costs",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "01_799_0110_1_1": {
    "itemNumber": "01_799_0110_1_1",
    "name": "Provider travel - non-labour costs",
    "shortName": "Provider travel",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provider travel - non-labour costs",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "01_799_0114_1_1": {
    "itemNumber": "01_799_0114_1_1",
    "name": "Provider travel - non-labour costs",
    "shortName": "Provider travel",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provider travel - non-labour costs",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "01_799_0115_1_1": {
    "itemNumber": "01_799_0115_1_1",
    "name": "Provider travel - non-labour costs",
    "shortName": "Provider travel",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provider travel - non-labour costs",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "01_799_0117_8_1": {
    "itemNumber": "01_799_0117_8_1",
    "name": "Provider travel - non-labour costs",
    "shortName": "Provider travel",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provider travel - non-labour costs",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "01_799_0118_1_1": {
    "itemNumber": "01_799_0118_1_1",
    "name": "Provider travel - non-labour costs",
    "shortName": "Provider travel",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provider travel - non-labour costs",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "01_799_0119_1_1": {
    "itemNumber": "01_799_0119_1_1",
    "name": "Provider travel - non-labour costs",
    "shortName": "Provider travel",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provider travel - non-labour costs",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "01_799_0120_1_1": {
    "itemNumber": "01_799_0120_1_1",
    "name": "Provider travel - non-labour costs",
    "shortName": "Provider travel",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provider travel - non-labour costs",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "01_799_0126_1_1": {
    "itemNumber": "01_799_0126_1_1",
    "name": "Provider travel - non-labour costs",
    "shortName": "Provider travel",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provider travel - non-labour costs",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "01_799_0128_1_1": {
    "itemNumber": "01_799_0128_1_1",
    "name": "Provider travel - non-labour costs",
    "shortName": "Provider travel",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provider travel - non-labour costs",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "01_799_0129_1_1": {
    "itemNumber": "01_799_0129_1_1",
    "name": "Provider travel - non-labour costs",
    "shortName": "Provider travel",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provider travel - non-labour costs",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "01_799_0132_1_1": {
    "itemNumber": "01_799_0132_1_1",
    "name": "Provider travel - non-labour costs",
    "shortName": "Provider travel",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provider travel - non-labour costs",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "01_799_0134_1_1": {
    "itemNumber": "01_799_0134_1_1",
    "name": "Provider travel - non-labour costs",
    "shortName": "Provider travel",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provider travel - non-labour costs",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "01_799_0135_1_1": {
    "itemNumber": "01_799_0135_1_1",
    "name": "Provider travel - non-labour costs",
    "shortName": "Provider travel",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provider travel - non-labour costs",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "01_801_0115_1_1": {
    "itemNumber": "01_801_0115_1_1",
    "name": "Assistance in Supported Independent Living - Standard",
    "shortName": "Assistance in Supported Independent Living",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 70.23,
    "weekdayEveningRate": 70.23,
    "saturdayRate": 70.23,
    "sundayRate": 70.23,
    "publicHolidayRate": 70.23,
    "nightRate": 70.23,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assistance in Supported Independent Living - Standard - Weekday Daytime",
    "regionalPricing": {
      "weekday": {
        "national": 70.23,
        "remote": 70.23,
        "veryRemote": 98.32
      },
      "saturday": {
        "national": 70.23,
        "remote": 70.23,
        "veryRemote": 98.32
      },
      "sunday": {
        "national": 70.23,
        "remote": 70.23,
        "veryRemote": 98.32
      },
      "publicHoliday": {
        "national": 70.23,
        "remote": 70.23,
        "veryRemote": 98.32
      }
    }
  },
  "01_802_0115_1_1": {
    "itemNumber": "01_802_0115_1_1",
    "name": "Assistance in Supported Independent Living - Standard",
    "shortName": "Assistance in Supported Independent Living",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 77.38,
    "weekdayEveningRate": 77.38,
    "saturdayRate": 77.38,
    "sundayRate": 77.38,
    "publicHolidayRate": 77.38,
    "nightRate": 77.38,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assistance in Supported Independent Living - Standard - Weekday Evening",
    "regionalPricing": {
      "weekday": {
        "national": 77.38,
        "remote": 108.33,
        "veryRemote": 116.07
      },
      "saturday": {
        "national": 77.38,
        "remote": 108.33,
        "veryRemote": 116.07
      },
      "sunday": {
        "national": 77.38,
        "remote": 108.33,
        "veryRemote": 116.07
      },
      "publicHoliday": {
        "national": 77.38,
        "remote": 108.33,
        "veryRemote": 116.07
      }
    }
  },
  "01_803_0115_1_1": {
    "itemNumber": "01_803_0115_1_1",
    "name": "Assistance in Supported Independent Living - Standard",
    "shortName": "Assistance in Supported Independent Living",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 78.81,
    "weekdayEveningRate": 78.81,
    "saturdayRate": 78.81,
    "sundayRate": 78.81,
    "publicHolidayRate": 78.81,
    "nightRate": 78.81,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assistance in Supported Independent Living - Standard - Weekday Night",
    "regionalPricing": {
      "weekday": {
        "national": 78.81,
        "remote": 110.33,
        "veryRemote": 118.22
      },
      "saturday": {
        "national": 78.81,
        "remote": 110.33,
        "veryRemote": 118.22
      },
      "sunday": {
        "national": 78.81,
        "remote": 110.33,
        "veryRemote": 118.22
      },
      "publicHoliday": {
        "national": 78.81,
        "remote": 110.33,
        "veryRemote": 118.22
      }
    }
  },
  "01_804_0115_1_1": {
    "itemNumber": "01_804_0115_1_1",
    "name": "Assistance in Supported Independent Living - Standard",
    "shortName": "Assistance in Supported Independent Living",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 98.83,
    "weekdayEveningRate": 98.83,
    "saturdayRate": 98.83,
    "sundayRate": 98.83,
    "publicHolidayRate": 98.83,
    "nightRate": 98.83,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assistance in Supported Independent Living - Standard - Saturday",
    "regionalPricing": {
      "weekday": {
        "national": 98.83,
        "remote": 138.36,
        "veryRemote": 148.25
      },
      "saturday": {
        "national": 98.83,
        "remote": 138.36,
        "veryRemote": 148.25
      },
      "sunday": {
        "national": 98.83,
        "remote": 138.36,
        "veryRemote": 148.25
      },
      "publicHoliday": {
        "national": 98.83,
        "remote": 138.36,
        "veryRemote": 148.25
      }
    }
  },
  "01_805_0115_1_1": {
    "itemNumber": "01_805_0115_1_1",
    "name": "Assistance in Supported Independent Living - Standard",
    "shortName": "Assistance in Supported Independent Living",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 127.43,
    "weekdayEveningRate": 127.43,
    "saturdayRate": 127.43,
    "sundayRate": 127.43,
    "publicHolidayRate": 127.43,
    "nightRate": 127.43,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assistance in Supported Independent Living - Standard - Sunday",
    "regionalPricing": {
      "weekday": {
        "national": 127.43,
        "remote": 178.4,
        "veryRemote": 191.15
      },
      "saturday": {
        "national": 127.43,
        "remote": 178.4,
        "veryRemote": 191.15
      },
      "sunday": {
        "national": 127.43,
        "remote": 178.4,
        "veryRemote": 191.15
      },
      "publicHoliday": {
        "national": 127.43,
        "remote": 178.4,
        "veryRemote": 191.15
      }
    }
  },
  "01_806_0115_1_1": {
    "itemNumber": "01_806_0115_1_1",
    "name": "Assistance in Supported Independent Living - Standard",
    "shortName": "Assistance in Supported Independent Living",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 156.03,
    "weekdayEveningRate": 156.03,
    "saturdayRate": 156.03,
    "sundayRate": 156.03,
    "publicHolidayRate": 156.03,
    "nightRate": 156.03,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assistance in Supported Independent Living - Standard - Public Holiday",
    "regionalPricing": {
      "weekday": {
        "national": 156.03,
        "remote": 218.44,
        "veryRemote": 234.05
      },
      "saturday": {
        "national": 156.03,
        "remote": 218.44,
        "veryRemote": 234.05
      },
      "sunday": {
        "national": 156.03,
        "remote": 218.44,
        "veryRemote": 234.05
      },
      "publicHoliday": {
        "national": 156.03,
        "remote": 218.44,
        "veryRemote": 234.05
      }
    }
  },
  "01_811_0115_1_1": {
    "itemNumber": "01_811_0115_1_1",
    "name": "Assistance in Supported Independent Living - High Intensity",
    "shortName": "Assistance in Supported Independent Living",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 75.98,
    "weekdayEveningRate": 75.98,
    "saturdayRate": 75.98,
    "sundayRate": 75.98,
    "publicHolidayRate": 75.98,
    "nightRate": 75.98,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assistance in Supported Independent Living - High Intensity - Weekday Daytime",
    "regionalPricing": {
      "weekday": {
        "national": 75.98,
        "remote": 106.37,
        "veryRemote": 113.97
      },
      "saturday": {
        "national": 75.98,
        "remote": 106.37,
        "veryRemote": 113.97
      },
      "sunday": {
        "national": 75.98,
        "remote": 106.37,
        "veryRemote": 113.97
      },
      "publicHoliday": {
        "national": 75.98,
        "remote": 106.37,
        "veryRemote": 113.97
      }
    }
  },
  "01_812_0115_1_1": {
    "itemNumber": "01_812_0115_1_1",
    "name": "Assistance in Supported Independent Living - High Intensity",
    "shortName": "Assistance in Supported Independent Living",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 83.72,
    "weekdayEveningRate": 83.72,
    "saturdayRate": 83.72,
    "sundayRate": 83.72,
    "publicHolidayRate": 83.72,
    "nightRate": 83.72,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assistance in Supported Independent Living - High Intensity - Weekday Evening",
    "regionalPricing": {
      "weekday": {
        "national": 83.72,
        "remote": 117.21,
        "veryRemote": 125.58
      },
      "saturday": {
        "national": 83.72,
        "remote": 117.21,
        "veryRemote": 125.58
      },
      "sunday": {
        "national": 83.72,
        "remote": 117.21,
        "veryRemote": 125.58
      },
      "publicHoliday": {
        "national": 83.72,
        "remote": 117.21,
        "veryRemote": 125.58
      }
    }
  },
  "01_813_0115_1_1": {
    "itemNumber": "01_813_0115_1_1",
    "name": "Assistance in Supported Independent Living - High Intensity",
    "shortName": "Assistance in Supported Independent Living",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 85.27,
    "weekdayEveningRate": 85.27,
    "saturdayRate": 85.27,
    "sundayRate": 85.27,
    "publicHolidayRate": 85.27,
    "nightRate": 85.27,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assistance in Supported Independent Living - High Intensity - Weekday Night",
    "regionalPricing": {
      "weekday": {
        "national": 85.27,
        "remote": 119.38,
        "veryRemote": 127.91
      },
      "saturday": {
        "national": 85.27,
        "remote": 119.38,
        "veryRemote": 127.91
      },
      "sunday": {
        "national": 85.27,
        "remote": 119.38,
        "veryRemote": 127.91
      },
      "publicHoliday": {
        "national": 85.27,
        "remote": 119.38,
        "veryRemote": 127.91
      }
    }
  },
  "01_814_0115_1_1": {
    "itemNumber": "01_814_0115_1_1",
    "name": "Assistance in Supported Independent Living - High Intensity",
    "shortName": "Assistance in Supported Independent Living",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 106.93,
    "weekdayEveningRate": 106.93,
    "saturdayRate": 106.93,
    "sundayRate": 106.93,
    "publicHolidayRate": 106.93,
    "nightRate": 106.93,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assistance in Supported Independent Living - High Intensity - Saturday",
    "regionalPricing": {
      "weekday": {
        "national": 106.93,
        "remote": 149.7,
        "veryRemote": 160.4
      },
      "saturday": {
        "national": 106.93,
        "remote": 149.7,
        "veryRemote": 160.4
      },
      "sunday": {
        "national": 106.93,
        "remote": 149.7,
        "veryRemote": 160.4
      },
      "publicHoliday": {
        "national": 106.93,
        "remote": 149.7,
        "veryRemote": 160.4
      }
    }
  },
  "01_815_0115_1_1": {
    "itemNumber": "01_815_0115_1_1",
    "name": "Assistance in Supported Independent Living - High Intensity",
    "shortName": "Assistance in Supported Independent Living",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 137.87,
    "weekdayEveningRate": 137.87,
    "saturdayRate": 137.87,
    "sundayRate": 137.87,
    "publicHolidayRate": 137.87,
    "nightRate": 137.87,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assistance in Supported Independent Living - High Intensity - Sunday",
    "regionalPricing": {
      "weekday": {
        "national": 137.87,
        "remote": 193.02,
        "veryRemote": 206.81
      },
      "saturday": {
        "national": 137.87,
        "remote": 193.02,
        "veryRemote": 206.81
      },
      "sunday": {
        "national": 137.87,
        "remote": 193.02,
        "veryRemote": 206.81
      },
      "publicHoliday": {
        "national": 137.87,
        "remote": 193.02,
        "veryRemote": 206.81
      }
    }
  },
  "01_816_0115_1_1": {
    "itemNumber": "01_816_0115_1_1",
    "name": "Assistance in Supported Independent Living - High Intensity",
    "shortName": "Assistance in Supported Independent Living",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 168.81,
    "weekdayEveningRate": 168.81,
    "saturdayRate": 168.81,
    "sundayRate": 168.81,
    "publicHolidayRate": 168.81,
    "nightRate": 168.81,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assistance in Supported Independent Living - High Intensity - Public Holiday",
    "regionalPricing": {
      "weekday": {
        "national": 168.81,
        "remote": 236.33,
        "veryRemote": 253.22
      },
      "saturday": {
        "national": 168.81,
        "remote": 236.33,
        "veryRemote": 253.22
      },
      "sunday": {
        "national": 168.81,
        "remote": 236.33,
        "veryRemote": 253.22
      },
      "publicHoliday": {
        "national": 168.81,
        "remote": 236.33,
        "veryRemote": 253.22
      }
    }
  },
  "01_832_0115_1_1": {
    "itemNumber": "01_832_0115_1_1",
    "name": "Assistance in Supported Independent Living",
    "shortName": "Assistance in Supported Independent Living",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 297.6,
    "weekdayEveningRate": 297.6,
    "saturdayRate": 297.6,
    "sundayRate": 297.6,
    "publicHolidayRate": 297.6,
    "nightRate": 297.6,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assistance in Supported Independent Living - Night-Time Sleepover",
    "regionalPricing": {
      "weekday": {
        "national": 297.6,
        "remote": 416.64,
        "veryRemote": 446.4
      },
      "saturday": {
        "national": 297.6,
        "remote": 416.64,
        "veryRemote": 446.4
      },
      "sunday": {
        "national": 297.6,
        "remote": 416.64,
        "veryRemote": 446.4
      },
      "publicHoliday": {
        "national": 297.6,
        "remote": 416.64,
        "veryRemote": 446.4
      }
    }
  },
  "01_850_0106_1_1": {
    "itemNumber": "01_850_0106_1_1",
    "name": "Individualised Living Options - Exploration and Design",
    "shortName": "Individualised Living Options",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 100.14,
    "weekdayEveningRate": 100.14,
    "saturdayRate": 100.14,
    "sundayRate": 100.14,
    "publicHolidayRate": 100.14,
    "nightRate": 100.14,
    "ttp": false,
    "quoteRequired": false,
    "description": "Individualised Living Options - Exploration and Design",
    "regionalPricing": {
      "weekday": {
        "national": 100.14,
        "remote": 140.19,
        "veryRemote": 150.21
      },
      "saturday": {
        "national": 100.14,
        "remote": 140.19,
        "veryRemote": 150.21
      },
      "sunday": {
        "national": 100.14,
        "remote": 140.19,
        "veryRemote": 150.21
      },
      "publicHoliday": {
        "national": 100.14,
        "remote": 140.19,
        "veryRemote": 150.21
      }
    }
  },
  "02_051_0108_1_1": {
    "itemNumber": "02_051_0108_1_1",
    "name": "Transport Year",
    "shortName": "Transport Year",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Transport Year",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "07_001_0106_8_3": {
    "itemNumber": "07_001_0106_8_3",
    "name": "Support Coordination Level 1: Support Connection",
    "shortName": "Support Coordination Level 1: Support Connection",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 80.06,
    "weekdayEveningRate": 80.06,
    "saturdayRate": 80.06,
    "sundayRate": 80.06,
    "publicHolidayRate": 80.06,
    "nightRate": 80.06,
    "ttp": false,
    "quoteRequired": false,
    "description": "Support Coordination Level 1: Support Connection",
    "regionalPricing": {
      "weekday": {
        "national": 80.06,
        "remote": 112.08,
        "veryRemote": 120.09
      },
      "saturday": {
        "national": 80.06,
        "remote": 112.08,
        "veryRemote": 120.09
      },
      "sunday": {
        "national": 80.06,
        "remote": 112.08,
        "veryRemote": 120.09
      },
      "publicHoliday": {
        "national": 80.06,
        "remote": 112.08,
        "veryRemote": 120.09
      }
    }
  },
  "07_002_0106_8_3": {
    "itemNumber": "07_002_0106_8_3",
    "name": "Support Coordination Level 2: Coordination of Supports",
    "shortName": "Support Coordination Level 2: Coordination of Supports",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 100.14,
    "weekdayEveningRate": 100.14,
    "saturdayRate": 100.14,
    "sundayRate": 100.14,
    "publicHolidayRate": 100.14,
    "nightRate": 100.14,
    "ttp": false,
    "quoteRequired": false,
    "description": "Support Coordination Level 2: Coordination of Supports",
    "regionalPricing": {
      "weekday": {
        "national": 100.14,
        "remote": 140.19,
        "veryRemote": 150.21
      },
      "saturday": {
        "national": 100.14,
        "remote": 140.19,
        "veryRemote": 150.21
      },
      "sunday": {
        "national": 100.14,
        "remote": 140.19,
        "veryRemote": 150.21
      },
      "publicHoliday": {
        "national": 100.14,
        "remote": 140.19,
        "veryRemote": 150.21
      }
    }
  },
  "07_004_0132_8_3": {
    "itemNumber": "07_004_0132_8_3",
    "name": "Support Coordination Level 3: Specialist Support Coordination",
    "shortName": "Support Coordination Level 3: Specialist Support Coordination",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 190.54,
    "weekdayEveningRate": 190.54,
    "saturdayRate": 190.54,
    "sundayRate": 190.54,
    "publicHolidayRate": 190.54,
    "nightRate": 190.54,
    "ttp": false,
    "quoteRequired": false,
    "description": "Support Coordination Level 3: Specialist Support Coordination",
    "regionalPricing": {
      "weekday": {
        "national": 190.54,
        "remote": 266.75,
        "veryRemote": 285.8
      },
      "saturday": {
        "national": 190.54,
        "remote": 266.75,
        "veryRemote": 285.8
      },
      "sunday": {
        "national": 190.54,
        "remote": 266.75,
        "veryRemote": 285.8
      },
      "publicHoliday": {
        "national": 190.54,
        "remote": 266.75,
        "veryRemote": 285.8
      }
    }
  },
  "07_101_0106_6_3": {
    "itemNumber": "07_101_0106_6_3",
    "name": "Psychosocial Recovery Coaching",
    "shortName": "Psychosocial Recovery Coaching",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 105.43,
    "weekdayEveningRate": 105.43,
    "saturdayRate": 105.43,
    "sundayRate": 105.43,
    "publicHolidayRate": 105.43,
    "nightRate": 105.43,
    "ttp": false,
    "quoteRequired": false,
    "description": "Psychosocial Recovery Coaching - Weekday Daytime",
    "regionalPricing": {
      "weekday": {
        "national": 105.43,
        "remote": 147.6,
        "veryRemote": 158.15
      },
      "saturday": {
        "national": 105.43,
        "remote": 147.6,
        "veryRemote": 158.15
      },
      "sunday": {
        "national": 105.43,
        "remote": 147.6,
        "veryRemote": 158.15
      },
      "publicHoliday": {
        "national": 105.43,
        "remote": 147.6,
        "veryRemote": 158.15
      }
    }
  },
  "07_102_0106_6_3": {
    "itemNumber": "07_102_0106_6_3",
    "name": "Psychosocial Recovery Coaching",
    "shortName": "Psychosocial Recovery Coaching",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 116.16,
    "weekdayEveningRate": 116.16,
    "saturdayRate": 116.16,
    "sundayRate": 116.16,
    "publicHolidayRate": 116.16,
    "nightRate": 116.16,
    "ttp": false,
    "quoteRequired": false,
    "description": "Psychosocial Recovery Coaching - Weekday Evening",
    "regionalPricing": {
      "weekday": {
        "national": 116.16,
        "remote": 162.62,
        "veryRemote": 174.24
      },
      "saturday": {
        "national": 116.16,
        "remote": 162.62,
        "veryRemote": 174.24
      },
      "sunday": {
        "national": 116.16,
        "remote": 162.62,
        "veryRemote": 174.24
      },
      "publicHoliday": {
        "national": 116.16,
        "remote": 162.62,
        "veryRemote": 174.24
      }
    }
  },
  "07_103_0106_6_3": {
    "itemNumber": "07_103_0106_6_3",
    "name": "Psychosocial Recovery Coaching",
    "shortName": "Psychosocial Recovery Coaching",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 118.31,
    "weekdayEveningRate": 118.31,
    "saturdayRate": 118.31,
    "sundayRate": 118.31,
    "publicHolidayRate": 118.31,
    "nightRate": 118.31,
    "ttp": false,
    "quoteRequired": false,
    "description": "Psychosocial Recovery Coaching - Weekday Night",
    "regionalPricing": {
      "weekday": {
        "national": 118.31,
        "remote": 165.63,
        "veryRemote": 177.47
      },
      "saturday": {
        "national": 118.31,
        "remote": 165.63,
        "veryRemote": 177.47
      },
      "sunday": {
        "national": 118.31,
        "remote": 165.63,
        "veryRemote": 177.47
      },
      "publicHoliday": {
        "national": 118.31,
        "remote": 165.63,
        "veryRemote": 177.47
      }
    }
  },
  "07_104_0106_6_3": {
    "itemNumber": "07_104_0106_6_3",
    "name": "Psychosocial Recovery Coaching",
    "shortName": "Psychosocial Recovery Coaching",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 148.36,
    "weekdayEveningRate": 148.36,
    "saturdayRate": 148.36,
    "sundayRate": 148.36,
    "publicHolidayRate": 148.36,
    "nightRate": 148.36,
    "ttp": false,
    "quoteRequired": false,
    "description": "Psychosocial Recovery Coaching - Saturday",
    "regionalPricing": {
      "weekday": {
        "national": 148.36,
        "remote": 207.7,
        "veryRemote": 222.54
      },
      "saturday": {
        "national": 148.36,
        "remote": 207.7,
        "veryRemote": 222.54
      },
      "sunday": {
        "national": 148.36,
        "remote": 207.7,
        "veryRemote": 222.54
      },
      "publicHoliday": {
        "national": 148.36,
        "remote": 207.7,
        "veryRemote": 222.54
      }
    }
  },
  "07_105_0106_6_3": {
    "itemNumber": "07_105_0106_6_3",
    "name": "Psychosocial Recovery Coaching",
    "shortName": "Psychosocial Recovery Coaching",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 191.29,
    "weekdayEveningRate": 191.29,
    "saturdayRate": 191.29,
    "sundayRate": 191.29,
    "publicHolidayRate": 191.29,
    "nightRate": 191.29,
    "ttp": false,
    "quoteRequired": false,
    "description": "Psychosocial Recovery Coaching - Sunday",
    "regionalPricing": {
      "weekday": {
        "national": 191.29,
        "remote": 267.81,
        "veryRemote": 286.94
      },
      "saturday": {
        "national": 191.29,
        "remote": 267.81,
        "veryRemote": 286.94
      },
      "sunday": {
        "national": 191.29,
        "remote": 267.81,
        "veryRemote": 286.94
      },
      "publicHoliday": {
        "national": 191.29,
        "remote": 267.81,
        "veryRemote": 286.94
      }
    }
  },
  "07_106_0106_6_3": {
    "itemNumber": "07_106_0106_6_3",
    "name": "Psychosocial Recovery Coaching",
    "shortName": "Psychosocial Recovery Coaching",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 234.23,
    "weekdayEveningRate": 234.23,
    "saturdayRate": 234.23,
    "sundayRate": 234.23,
    "publicHolidayRate": 234.23,
    "nightRate": 234.23,
    "ttp": false,
    "quoteRequired": false,
    "description": "Psychosocial Recovery Coaching - Public Holiday",
    "regionalPricing": {
      "weekday": {
        "national": 234.23,
        "remote": 327.92,
        "veryRemote": 351.35
      },
      "saturday": {
        "national": 234.23,
        "remote": 327.92,
        "veryRemote": 351.35
      },
      "sunday": {
        "national": 234.23,
        "remote": 327.92,
        "veryRemote": 351.35
      },
      "publicHoliday": {
        "national": 234.23,
        "remote": 327.92,
        "veryRemote": 351.35
      }
    }
  },
  "07_501_0106_6_3": {
    "itemNumber": "07_501_0106_6_3",
    "name": "Activity Based Transport",
    "shortName": "Activity Based Transport",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Activity Based Transport",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "07_799_0106_6_3": {
    "itemNumber": "07_799_0106_6_3",
    "name": "Provider travel - non-labour costs",
    "shortName": "Provider travel",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provider travel - non-labour costs",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "07_799_0117_8_3": {
    "itemNumber": "07_799_0117_8_3",
    "name": "Provider travel - non-labour costs",
    "shortName": "Provider travel",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provider travel - non-labour costs",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "07_799_0132_8_3": {
    "itemNumber": "07_799_0132_8_3",
    "name": "Provider travel - non-labour costs",
    "shortName": "Provider travel",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provider travel - non-labour costs",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "08_005_0106_2_3": {
    "itemNumber": "08_005_0106_2_3",
    "name": "Assistance With Accommodation And Tenancy Obligations",
    "shortName": "Assistance With Accommodation And Tenancy Obligations",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 80.06,
    "weekdayEveningRate": 80.06,
    "saturdayRate": 80.06,
    "sundayRate": 80.06,
    "publicHolidayRate": 80.06,
    "nightRate": 80.06,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assistance With Accommodation And Tenancy Obligations",
    "regionalPricing": {
      "weekday": {
        "national": 80.06,
        "remote": 80.06,
        "veryRemote": 112.08
      },
      "saturday": {
        "national": 80.06,
        "remote": 80.06,
        "veryRemote": 112.08
      },
      "sunday": {
        "national": 80.06,
        "remote": 80.06,
        "veryRemote": 112.08
      },
      "publicHoliday": {
        "national": 80.06,
        "remote": 80.06,
        "veryRemote": 112.08
      }
    }
  },
  "08_590_0106_2_3": {
    "itemNumber": "08_590_0106_2_3",
    "name": "Activity Based Transport",
    "shortName": "Activity Based Transport",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Activity Based Transport",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "08_799_0106_2_3": {
    "itemNumber": "08_799_0106_2_3",
    "name": "Provider travel - non-labour costs",
    "shortName": "Provider travel",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provider travel - non-labour costs",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "09_006_0106_6_3": {
    "itemNumber": "09_006_0106_6_3",
    "name": "Life Transition Planning Incl. Mentoring Peer- Support And Indiv Skill Develop",
    "shortName": "Life Transition Planning Incl. Mentoring Peer- Support And Indiv Skill Develop",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 80.06,
    "weekdayEveningRate": 80.06,
    "saturdayRate": 80.06,
    "sundayRate": 80.06,
    "publicHolidayRate": 80.06,
    "nightRate": 80.06,
    "ttp": false,
    "quoteRequired": false,
    "description": "Life Transition Planning Incl. Mentoring Peer- Support And Indiv Skill Develop",
    "regionalPricing": {
      "weekday": {
        "national": 80.06,
        "remote": 112.08,
        "veryRemote": 120.09
      },
      "saturday": {
        "national": 80.06,
        "remote": 112.08,
        "veryRemote": 120.09
      },
      "sunday": {
        "national": 80.06,
        "remote": 112.08,
        "veryRemote": 120.09
      },
      "publicHoliday": {
        "national": 80.06,
        "remote": 112.08,
        "veryRemote": 120.09
      }
    }
  },
  "09_009_0117_6_3": {
    "itemNumber": "09_009_0117_6_3",
    "name": "Skills Development and Training",
    "shortName": "Skills Development and Training",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 80.06,
    "weekdayEveningRate": 80.06,
    "saturdayRate": 80.06,
    "sundayRate": 80.06,
    "publicHolidayRate": 80.06,
    "nightRate": 80.06,
    "ttp": false,
    "quoteRequired": false,
    "description": "Skills Development and Training",
    "regionalPricing": {
      "weekday": {
        "national": 80.06,
        "remote": 112.08,
        "veryRemote": 120.09
      },
      "saturday": {
        "national": 80.06,
        "remote": 112.08,
        "veryRemote": 120.09
      },
      "sunday": {
        "national": 80.06,
        "remote": 112.08,
        "veryRemote": 120.09
      },
      "publicHoliday": {
        "national": 80.06,
        "remote": 112.08,
        "veryRemote": 120.09
      }
    }
  },
  "09_590_0106_6_3": {
    "itemNumber": "09_590_0106_6_3",
    "name": "Activity Based Transport",
    "shortName": "Activity Based Transport",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Activity Based Transport",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "09_591_0117_6_3": {
    "itemNumber": "09_591_0117_6_3",
    "name": "Activity Based Transport",
    "shortName": "Activity Based Transport",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Activity Based Transport",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "09_799_0106_6_3": {
    "itemNumber": "09_799_0106_6_3",
    "name": "Provider travel - non-labour costs",
    "shortName": "Provider travel",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provider travel - non-labour costs",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "09_799_0117_6_3": {
    "itemNumber": "09_799_0117_6_3",
    "name": "Provider travel - non-labour costs",
    "shortName": "Provider travel",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provider travel - non-labour costs",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "10_002_0106_8_3": {
    "itemNumber": "10_002_0106_8_3",
    "name": "Support Coordination Level 2: Coordination of Supports",
    "shortName": "Support Coordination Level 2: Coordination of Supports",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 100.14,
    "weekdayEveningRate": 100.14,
    "saturdayRate": 100.14,
    "sundayRate": 100.14,
    "publicHolidayRate": 100.14,
    "nightRate": 100.14,
    "ttp": false,
    "quoteRequired": false,
    "description": "Support Coordination Level 2: Coordination of Supports",
    "regionalPricing": {
      "weekday": {
        "national": 100.14,
        "remote": 140.19,
        "veryRemote": 150.21
      },
      "saturday": {
        "national": 100.14,
        "remote": 140.19,
        "veryRemote": 150.21
      },
      "sunday": {
        "national": 100.14,
        "remote": 140.19,
        "veryRemote": 150.21
      },
      "publicHoliday": {
        "national": 100.14,
        "remote": 140.19,
        "veryRemote": 150.21
      }
    }
  },
  "10_011_0128_5_3": {
    "itemNumber": "10_011_0128_5_3",
    "name": "Employment Related Assessment, Counselling and Advice – Other Professional",
    "shortName": "Employment Related Assessment, Counselling and Advice – Other Professional",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 193.99,
    "weekdayEveningRate": 193.99,
    "saturdayRate": 193.99,
    "sundayRate": 193.99,
    "publicHolidayRate": 193.99,
    "nightRate": 193.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Employment Related Assessment, Counselling and Advice – Other Professional",
    "regionalPricing": {
      "weekday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "saturday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "sunday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "publicHoliday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      }
    }
  },
  "10_016_0102_5_3": {
    "itemNumber": "10_016_0102_5_3",
    "name": "Employment Assistance",
    "shortName": "Employment Assistance",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 80.06,
    "weekdayEveningRate": 80.06,
    "saturdayRate": 80.06,
    "sundayRate": 80.06,
    "publicHolidayRate": 80.06,
    "nightRate": 80.06,
    "ttp": false,
    "quoteRequired": false,
    "description": "Employment Assistance",
    "regionalPricing": {
      "weekday": {
        "national": 80.06,
        "remote": 112.08,
        "veryRemote": 120.09
      },
      "saturday": {
        "national": 80.06,
        "remote": 112.08,
        "veryRemote": 120.09
      },
      "sunday": {
        "national": 80.06,
        "remote": 112.08,
        "veryRemote": 120.09
      },
      "publicHoliday": {
        "national": 80.06,
        "remote": 112.08,
        "veryRemote": 120.09
      }
    }
  },
  "10_054_0128_5_3": {
    "itemNumber": "10_054_0128_5_3",
    "name": "Employment Related Assessment, Counselling and Advice -Psychologist",
    "shortName": "Employment Related Assessment, Counselling and Advice -Psychologist",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 232.99,
    "weekdayEveningRate": 232.99,
    "saturdayRate": 232.99,
    "sundayRate": 232.99,
    "publicHolidayRate": 232.99,
    "nightRate": 232.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Employment Related Assessment, Counselling and Advice -Psychologist",
    "regionalPricing": {
      "weekday": {
        "national": 232.99,
        "remote": 326.19,
        "veryRemote": 349.49
      },
      "saturday": {
        "national": 232.99,
        "remote": 326.19,
        "veryRemote": 349.49
      },
      "sunday": {
        "national": 232.99,
        "remote": 326.19,
        "veryRemote": 349.49
      },
      "publicHoliday": {
        "national": 232.99,
        "remote": 326.19,
        "veryRemote": 349.49
      }
    }
  },
  "10_055_0128_5_3": {
    "itemNumber": "10_055_0128_5_3",
    "name": "Employment Related Assessment, Counselling and Advice -Physiotherapist",
    "shortName": "Employment Related Assessment, Counselling and Advice -Physiotherapist",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 183.99,
    "weekdayEveningRate": 183.99,
    "saturdayRate": 183.99,
    "sundayRate": 183.99,
    "publicHolidayRate": 183.99,
    "nightRate": 183.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Employment Related Assessment, Counselling and Advice -Physiotherapist",
    "regionalPricing": {
      "weekday": {
        "national": 183.99,
        "remote": 257.59,
        "veryRemote": 275.99
      },
      "saturday": {
        "national": 183.99,
        "remote": 257.59,
        "veryRemote": 275.99
      },
      "sunday": {
        "national": 183.99,
        "remote": 257.59,
        "veryRemote": 275.99
      },
      "publicHoliday": {
        "national": 183.99,
        "remote": 257.59,
        "veryRemote": 275.99
      }
    }
  },
  "10_101_0106_6_3": {
    "itemNumber": "10_101_0106_6_3",
    "name": "Psychosocial Recovery Coaching – Weekday Daytime",
    "shortName": "Psychosocial Recovery Coaching – Weekday Daytime",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 105.43,
    "weekdayEveningRate": 105.43,
    "saturdayRate": 105.43,
    "sundayRate": 105.43,
    "publicHolidayRate": 105.43,
    "nightRate": 105.43,
    "ttp": false,
    "quoteRequired": false,
    "description": "Psychosocial Recovery Coaching – Weekday Daytime",
    "regionalPricing": {
      "weekday": {
        "national": 105.43,
        "remote": 147.6,
        "veryRemote": 158.15
      },
      "saturday": {
        "national": 105.43,
        "remote": 147.6,
        "veryRemote": 158.15
      },
      "sunday": {
        "national": 105.43,
        "remote": 147.6,
        "veryRemote": 158.15
      },
      "publicHoliday": {
        "national": 105.43,
        "remote": 147.6,
        "veryRemote": 158.15
      }
    }
  },
  "10_590_0102_5_3": {
    "itemNumber": "10_590_0102_5_3",
    "name": "Activity Based Transport",
    "shortName": "Activity Based Transport",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Activity Based Transport",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "10_613_0128_5_3": {
    "itemNumber": "10_613_0128_5_3",
    "name": "Employment Related Assessment, Counselling and Advice - Developmental Educator",
    "shortName": "Employment Related Assessment, Counselling and Advice",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 193.99,
    "weekdayEveningRate": 193.99,
    "saturdayRate": 193.99,
    "sundayRate": 193.99,
    "publicHolidayRate": 193.99,
    "nightRate": 193.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Employment Related Assessment, Counselling and Advice - Developmental Educator",
    "regionalPricing": {
      "weekday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "saturday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "sunday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "publicHoliday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      }
    }
  },
  "10_617_0128_5_3": {
    "itemNumber": "10_617_0128_5_3",
    "name": "Employment Related Assessment, Counselling and Advice - Occupational Therapist",
    "shortName": "Employment Related Assessment, Counselling and Advice",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 193.99,
    "weekdayEveningRate": 193.99,
    "saturdayRate": 193.99,
    "sundayRate": 193.99,
    "publicHolidayRate": 193.99,
    "nightRate": 193.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Employment Related Assessment, Counselling and Advice - Occupational Therapist",
    "regionalPricing": {
      "weekday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "saturday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "sunday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "publicHoliday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      }
    }
  },
  "10_620_0128_5_3": {
    "itemNumber": "10_620_0128_5_3",
    "name": "Employment Related Assessment, Counselling and Advice - Rehabilitation Counsellor",
    "shortName": "Employment Related Assessment, Counselling and Advice",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 193.99,
    "weekdayEveningRate": 193.99,
    "saturdayRate": 193.99,
    "sundayRate": 193.99,
    "publicHolidayRate": 193.99,
    "nightRate": 193.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Employment Related Assessment, Counselling and Advice - Rehabilitation Counsellor",
    "regionalPricing": {
      "weekday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "saturday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "sunday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "publicHoliday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      }
    }
  },
  "10_622_0128_5_3": {
    "itemNumber": "10_622_0128_5_3",
    "name": "Employment Related Assessment, Counselling and Advice – Speech Pathologist",
    "shortName": "Employment Related Assessment, Counselling and Advice – Speech Pathologist",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 193.99,
    "weekdayEveningRate": 193.99,
    "saturdayRate": 193.99,
    "sundayRate": 193.99,
    "publicHolidayRate": 193.99,
    "nightRate": 193.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Employment Related Assessment, Counselling and Advice – Speech Pathologist",
    "regionalPricing": {
      "weekday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "saturday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "sunday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "publicHoliday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      }
    }
  },
  "10_799_0102_5_3": {
    "itemNumber": "10_799_0102_5_3",
    "name": "Provider travel - non-labour costs",
    "shortName": "Provider travel",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provider travel - non-labour costs",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "10_799_0128_5_3": {
    "itemNumber": "10_799_0128_5_3",
    "name": "Provider travel - non-labour costs",
    "shortName": "Provider travel",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provider travel - non-labour costs",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "11_022_0110_7_3": {
    "itemNumber": "11_022_0110_7_3",
    "name": "Specialist Behavioural Intervention Support • Highly specialised intensive support interventions to",
    "shortName": "Specialist Behavioural Intervention Support • Highly specialised intensive support interventions to",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 232.99,
    "weekdayEveningRate": 232.99,
    "saturdayRate": 232.99,
    "sundayRate": 232.99,
    "publicHolidayRate": 232.99,
    "nightRate": 232.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Specialist Behavioural Intervention Support • Highly specialised intensive support interventions to",
    "regionalPricing": {
      "weekday": {
        "national": 232.99,
        "remote": 326.19,
        "veryRemote": 349.49
      },
      "saturday": {
        "national": 232.99,
        "remote": 326.19,
        "veryRemote": 349.49
      },
      "sunday": {
        "national": 232.99,
        "remote": 326.19,
        "veryRemote": 349.49
      },
      "publicHoliday": {
        "national": 232.99,
        "remote": 326.19,
        "veryRemote": 349.49
      }
    }
  },
  "11_023_0110_7_3": {
    "itemNumber": "11_023_0110_7_3",
    "name": "Behaviour Management Plan Including Training in Behaviour Management Strategies • Training for carers and others in behaviour management strategies required due the participant’s disability.",
    "shortName": "Behaviour Management Plan Including Training in Behaviour Management Strategies • Training for carers and others in behaviour management strategies required due the participant’s disability.",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 232.99,
    "weekdayEveningRate": 232.99,
    "saturdayRate": 232.99,
    "sundayRate": 232.99,
    "publicHolidayRate": 232.99,
    "nightRate": 232.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Behaviour Management Plan Including Training in Behaviour Management Strategies • Training for carers and others in behaviour management strategies required due the participant’s disability.",
    "regionalPricing": {
      "weekday": {
        "national": 232.99,
        "remote": 326.19,
        "veryRemote": 349.49
      },
      "saturday": {
        "national": 232.99,
        "remote": 326.19,
        "veryRemote": 349.49
      },
      "sunday": {
        "national": 232.99,
        "remote": 326.19,
        "veryRemote": 349.49
      },
      "publicHoliday": {
        "national": 232.99,
        "remote": 326.19,
        "veryRemote": 349.49
      }
    }
  },
  "11_024_0117_7_3": {
    "itemNumber": "11_024_0117_7_3",
    "name": "Individual Social Skills Development",
    "shortName": "Individual Social Skills Development",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 80.06,
    "weekdayEveningRate": 80.06,
    "saturdayRate": 80.06,
    "sundayRate": 80.06,
    "publicHolidayRate": 80.06,
    "nightRate": 80.06,
    "ttp": false,
    "quoteRequired": false,
    "description": "Individual Social Skills Development",
    "regionalPricing": {
      "weekday": {
        "national": 80.06,
        "remote": 112.08,
        "veryRemote": 120.09
      },
      "saturday": {
        "national": 80.06,
        "remote": 112.08,
        "veryRemote": 120.09
      },
      "sunday": {
        "national": 80.06,
        "remote": 112.08,
        "veryRemote": 120.09
      },
      "publicHoliday": {
        "national": 80.06,
        "remote": 112.08,
        "veryRemote": 120.09
      }
    }
  },
  "11_590_0117_7_3": {
    "itemNumber": "11_590_0117_7_3",
    "name": "Activity Based Transport",
    "shortName": "Activity Based Transport",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Activity Based Transport",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "11_799_0110_7_3": {
    "itemNumber": "11_799_0110_7_3",
    "name": "Provider travel - non-labour costs",
    "shortName": "Provider travel",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provider travel - non-labour costs",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "11_799_0117_7_3": {
    "itemNumber": "11_799_0117_7_3",
    "name": "Provider travel - non-labour costs",
    "shortName": "Provider travel",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provider travel - non-labour costs",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "12_025_0128_3_3": {
    "itemNumber": "12_025_0128_3_3",
    "name": "Advice provided by a Dietitian on managing diet for health and well-being",
    "shortName": "Advice provided by a Dietitian on managing diet for health and well-being",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 188.99,
    "weekdayEveningRate": 188.99,
    "saturdayRate": 188.99,
    "sundayRate": 188.99,
    "publicHolidayRate": 188.99,
    "nightRate": 188.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Advice provided by a Dietitian on managing diet for health and well-being",
    "regionalPricing": {
      "weekday": {
        "national": 188.99,
        "remote": 264.59,
        "veryRemote": 283.49
      },
      "saturday": {
        "national": 188.99,
        "remote": 264.59,
        "veryRemote": 283.49
      },
      "sunday": {
        "national": 188.99,
        "remote": 264.59,
        "veryRemote": 283.49
      },
      "publicHoliday": {
        "national": 188.99,
        "remote": 264.59,
        "veryRemote": 283.49
      }
    }
  },
  "12_027_0126_3_3": {
    "itemNumber": "12_027_0126_3_3",
    "name": "Advice provided by an Exercise Physiologist regarding exercise required",
    "shortName": "Advice provided by an Exercise Physiologist regarding exercise required",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 166.99,
    "weekdayEveningRate": 166.99,
    "saturdayRate": 166.99,
    "sundayRate": 166.99,
    "publicHolidayRate": 166.99,
    "nightRate": 166.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Advice provided by an Exercise Physiologist regarding exercise required",
    "regionalPricing": {
      "weekday": {
        "national": 166.99,
        "remote": 233.79,
        "veryRemote": 250.49
      },
      "saturday": {
        "national": 166.99,
        "remote": 233.79,
        "veryRemote": 250.49
      },
      "sunday": {
        "national": 166.99,
        "remote": 233.79,
        "veryRemote": 250.49
      },
      "publicHoliday": {
        "national": 166.99,
        "remote": 233.79,
        "veryRemote": 250.49
      }
    }
  },
  "12_027_0128_3_3": {
    "itemNumber": "12_027_0128_3_3",
    "name": "Advice provided by an Exercise Physiologist regarding exercise required",
    "shortName": "Advice provided by an Exercise Physiologist regarding exercise required",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 166.99,
    "weekdayEveningRate": 166.99,
    "saturdayRate": 166.99,
    "sundayRate": 166.99,
    "publicHolidayRate": 166.99,
    "nightRate": 166.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Advice provided by an Exercise Physiologist regarding exercise required",
    "regionalPricing": {
      "weekday": {
        "national": 166.99,
        "remote": 233.79,
        "veryRemote": 250.49
      },
      "saturday": {
        "national": 166.99,
        "remote": 233.79,
        "veryRemote": 250.49
      },
      "sunday": {
        "national": 166.99,
        "remote": 233.79,
        "veryRemote": 250.49
      },
      "publicHoliday": {
        "national": 166.99,
        "remote": 233.79,
        "veryRemote": 250.49
      }
    }
  },
  "12_029_0126_3_3": {
    "itemNumber": "12_029_0126_3_3",
    "name": "Personal training provided by a Personal Trainer to a participant",
    "shortName": "Personal training provided by a Personal Trainer to a participant",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 67,
    "weekdayEveningRate": 67,
    "saturdayRate": 67,
    "sundayRate": 67,
    "publicHolidayRate": 67,
    "nightRate": 67,
    "ttp": false,
    "quoteRequired": false,
    "description": "Personal training provided by a Personal Trainer to a participant",
    "regionalPricing": {
      "weekday": {
        "national": 67,
        "remote": 93.8,
        "veryRemote": 100.5
      },
      "saturday": {
        "national": 67,
        "remote": 93.8,
        "veryRemote": 100.5
      },
      "sunday": {
        "national": 67,
        "remote": 93.8,
        "veryRemote": 100.5
      },
      "publicHoliday": {
        "national": 67,
        "remote": 93.8,
        "veryRemote": 100.5
      }
    }
  },
  "12_799_0126_3_3": {
    "itemNumber": "12_799_0126_3_3",
    "name": "Provider travel - non-labour costs",
    "shortName": "Provider travel",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provider travel - non-labour costs",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "12_799_0128_3_3": {
    "itemNumber": "12_799_0128_3_3",
    "name": "Provider travel - non-labour costs",
    "shortName": "Provider travel",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provider travel - non-labour costs",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "13_030_0102_4_3": {
    "itemNumber": "13_030_0102_4_3",
    "name": "Transition through School and to Further Education",
    "shortName": "Transition through School and to Further Education",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 80.06,
    "weekdayEveningRate": 80.06,
    "saturdayRate": 80.06,
    "sundayRate": 80.06,
    "publicHolidayRate": 80.06,
    "nightRate": 80.06,
    "ttp": false,
    "quoteRequired": false,
    "description": "Transition through School and to Further Education",
    "regionalPricing": {
      "weekday": {
        "national": 80.06,
        "remote": 112.08,
        "veryRemote": 120.09
      },
      "saturday": {
        "national": 80.06,
        "remote": 112.08,
        "veryRemote": 120.09
      },
      "sunday": {
        "national": 80.06,
        "remote": 112.08,
        "veryRemote": 120.09
      },
      "publicHoliday": {
        "national": 80.06,
        "remote": 112.08,
        "veryRemote": 120.09
      }
    }
  },
  "13_590_0102_4_3": {
    "itemNumber": "13_590_0102_4_3",
    "name": "Activity Based Transport",
    "shortName": "Activity Based Transport",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Activity Based Transport",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "13_799_0102_4_3": {
    "itemNumber": "13_799_0102_4_3",
    "name": "Provider travel - non-labour costs",
    "shortName": "Provider travel",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provider travel - non-labour costs",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "14_034_0127_8_3": {
    "itemNumber": "14_034_0127_8_3",
    "name": "Plan Management - Monthly Fee Month Month • A monthly fee for the ongoing maintenance of",
    "shortName": "Plan Management",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "",
    "weekdayRate": 104.45,
    "weekdayEveningRate": 104.45,
    "saturdayRate": 104.45,
    "sundayRate": 104.45,
    "publicHolidayRate": 104.45,
    "nightRate": 104.45,
    "ttp": false,
    "quoteRequired": false,
    "description": "Plan Management - Monthly Fee Month Month • A monthly fee for the ongoing maintenance of",
    "regionalPricing": {
      "weekday": {
        "national": 104.45,
        "remote": 104.45,
        "veryRemote": null
      },
      "saturday": {
        "national": 104.45,
        "remote": 104.45,
        "veryRemote": null
      },
      "sunday": {
        "national": 104.45,
        "remote": 104.45,
        "veryRemote": null
      },
      "publicHoliday": {
        "national": 104.45,
        "remote": 104.45,
        "veryRemote": null
      }
    }
  },
  "14_799_0127_8_3": {
    "itemNumber": "14_799_0127_8_3",
    "name": "Provider travel - non-labour costs",
    "shortName": "Provider travel",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provider travel - non-labour costs",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "15_005_0118_1_3": {
    "itemNumber": "15_005_0118_1_3",
    "name": "Early Childhood Intervention Professional - Other Early Childhood Professional",
    "shortName": "Early Childhood Intervention Professional",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 193.99,
    "weekdayEveningRate": 193.99,
    "saturdayRate": 193.99,
    "sundayRate": 193.99,
    "publicHolidayRate": 193.99,
    "nightRate": 193.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Early Childhood Intervention Professional - Other Early Childhood Professional",
    "regionalPricing": {
      "weekday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "saturday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "sunday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "publicHoliday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      }
    }
  },
  "15_035_0106_1_3": {
    "itemNumber": "15_035_0106_1_3",
    "name": "Assistance With Decision Making Daily Planning and Budgeting • Provision of time limited support to assist a person to develop and maintain daily budget, including assisting in planning purchases.",
    "shortName": "Assistance With Decision Making Daily Planning and Budgeting • Provision of time limited support to assist a person to develop and maintain daily budget, including assisting in planning purchases.",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 70.23,
    "weekdayEveningRate": 70.23,
    "saturdayRate": 70.23,
    "sundayRate": 70.23,
    "publicHolidayRate": 70.23,
    "nightRate": 70.23,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assistance With Decision Making Daily Planning and Budgeting • Provision of time limited support to assist a person to develop and maintain daily budget, including assisting in planning purchases.",
    "regionalPricing": {
      "weekday": {
        "national": 70.23,
        "remote": 70.23,
        "veryRemote": 98.32
      },
      "saturday": {
        "national": 70.23,
        "remote": 70.23,
        "veryRemote": 98.32
      },
      "sunday": {
        "national": 70.23,
        "remote": 70.23,
        "veryRemote": 98.32
      },
      "publicHoliday": {
        "national": 70.23,
        "remote": 70.23,
        "veryRemote": 98.32
      }
    }
  },
  "15_037_0117_1_3": {
    "itemNumber": "15_037_0117_1_3",
    "name": "Skill Development And Training including Public Transport Training • Individual training provided in the home or in the community for general life skills to increase independence.",
    "shortName": "Skill Development And Training including Public Transport Training • Individual training provided in the home or in the community for general life skills to increase independence.",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 70.23,
    "weekdayEveningRate": 70.23,
    "saturdayRate": 70.23,
    "sundayRate": 70.23,
    "publicHolidayRate": 70.23,
    "nightRate": 70.23,
    "ttp": false,
    "quoteRequired": false,
    "description": "Skill Development And Training including Public Transport Training • Individual training provided in the home or in the community for general life skills to increase independence.",
    "regionalPricing": {
      "weekday": {
        "national": 70.23,
        "remote": 98.32,
        "veryRemote": 105.35
      },
      "saturday": {
        "national": 70.23,
        "remote": 98.32,
        "veryRemote": 105.35
      },
      "sunday": {
        "national": 70.23,
        "remote": 98.32,
        "veryRemote": 105.35
      },
      "publicHoliday": {
        "national": 70.23,
        "remote": 98.32,
        "veryRemote": 105.35
      }
    }
  },
  "15_038_0117_1_3": {
    "itemNumber": "15_038_0117_1_3",
    "name": "Training For Carers/Parents • Training for carers in matters related to caring for a person with disability.",
    "shortName": "Training For Carers/Parents • Training for carers in matters related to caring for a person with disability.",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 80.06,
    "weekdayEveningRate": 80.06,
    "saturdayRate": 80.06,
    "sundayRate": 80.06,
    "publicHolidayRate": 80.06,
    "nightRate": 80.06,
    "ttp": false,
    "quoteRequired": false,
    "description": "Training For Carers/Parents • Training for carers in matters related to caring for a person with disability.",
    "regionalPricing": {
      "weekday": {
        "national": 80.06,
        "remote": 112.08,
        "veryRemote": 120.09
      },
      "saturday": {
        "national": 80.06,
        "remote": 112.08,
        "veryRemote": 120.09
      },
      "sunday": {
        "national": 80.06,
        "remote": 112.08,
        "veryRemote": 120.09
      },
      "publicHoliday": {
        "national": 80.06,
        "remote": 112.08,
        "veryRemote": 120.09
      }
    }
  },
  "15_043_0128_1_3": {
    "itemNumber": "15_043_0128_1_3",
    "name": "Assessment Recommendation Therapy or Training - Counsellor",
    "shortName": "Assessment Recommendation Therapy or Training",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 156.16,
    "weekdayEveningRate": 156.16,
    "saturdayRate": 156.16,
    "sundayRate": 156.16,
    "publicHolidayRate": 156.16,
    "nightRate": 156.16,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assessment Recommendation Therapy or Training - Counsellor",
    "regionalPricing": {
      "weekday": {
        "national": 156.16,
        "remote": 218.62,
        "veryRemote": 234.24
      },
      "saturday": {
        "national": 156.16,
        "remote": 218.62,
        "veryRemote": 234.24
      },
      "sunday": {
        "national": 156.16,
        "remote": 218.62,
        "veryRemote": 234.24
      },
      "publicHoliday": {
        "national": 156.16,
        "remote": 218.62,
        "veryRemote": 234.24
      }
    }
  },
  "15_045_0128_1_3": {
    "itemNumber": "15_045_0128_1_3",
    "name": "Community Engagement Assistance",
    "shortName": "Community Engagement Assistance",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 51.2,
    "weekdayEveningRate": 51.2,
    "saturdayRate": 51.2,
    "sundayRate": 51.2,
    "publicHolidayRate": 51.2,
    "nightRate": 51.2,
    "ttp": false,
    "quoteRequired": false,
    "description": "Community Engagement Assistance",
    "regionalPricing": {
      "weekday": {
        "national": 51.2,
        "remote": 71.68,
        "veryRemote": 76.8
      },
      "saturday": {
        "national": 51.2,
        "remote": 71.68,
        "veryRemote": 76.8
      },
      "sunday": {
        "national": 51.2,
        "remote": 71.68,
        "veryRemote": 76.8
      },
      "publicHoliday": {
        "national": 51.2,
        "remote": 71.68,
        "veryRemote": 76.8
      }
    }
  },
  "15_047_0135_1_3": {
    "itemNumber": "15_047_0135_1_3",
    "name": "Selection and/or Manufacture of Customised or Wearable Technology",
    "shortName": "Selection and/or Manufacture of Customised or Wearable Technology",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 193.99,
    "weekdayEveningRate": 193.99,
    "saturdayRate": 193.99,
    "sundayRate": 193.99,
    "publicHolidayRate": 193.99,
    "nightRate": 193.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Selection and/or Manufacture of Customised or Wearable Technology",
    "regionalPricing": {
      "weekday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "saturday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "sunday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "publicHoliday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      }
    }
  },
  "15_052_0128_1_3": {
    "itemNumber": "15_052_0128_1_3",
    "name": "Therapy Assistant - Level 1 • Support must be delivered by a therapy assistant working under the delegation and direct supervision at all times of a therapist.",
    "shortName": "Therapy Assistant",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 56.16,
    "weekdayEveningRate": 56.16,
    "saturdayRate": 56.16,
    "sundayRate": 56.16,
    "publicHolidayRate": 56.16,
    "nightRate": 56.16,
    "ttp": false,
    "quoteRequired": false,
    "description": "Therapy Assistant - Level 1 • Support must be delivered by a therapy assistant working under the delegation and direct supervision at all times of a therapist.",
    "regionalPricing": {
      "weekday": {
        "national": 56.16,
        "remote": 78.62,
        "veryRemote": 84.24
      },
      "saturday": {
        "national": 56.16,
        "remote": 78.62,
        "veryRemote": 84.24
      },
      "sunday": {
        "national": 56.16,
        "remote": 78.62,
        "veryRemote": 84.24
      },
      "publicHoliday": {
        "national": 56.16,
        "remote": 78.62,
        "veryRemote": 84.24
      }
    }
  },
  "15_053_0128_1_3": {
    "itemNumber": "15_053_0128_1_3",
    "name": "Therapy Assistant - Level 2 • Support must be delivered by a therapy",
    "shortName": "Therapy Assistant",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 86.79,
    "weekdayEveningRate": 86.79,
    "saturdayRate": 86.79,
    "sundayRate": 86.79,
    "publicHolidayRate": 86.79,
    "nightRate": 86.79,
    "ttp": false,
    "quoteRequired": false,
    "description": "Therapy Assistant - Level 2 • Support must be delivered by a therapy",
    "regionalPricing": {
      "weekday": {
        "national": 86.79,
        "remote": 121.51,
        "veryRemote": 130.19
      },
      "saturday": {
        "national": 86.79,
        "remote": 121.51,
        "veryRemote": 130.19
      },
      "sunday": {
        "national": 86.79,
        "remote": 121.51,
        "veryRemote": 130.19
      },
      "publicHoliday": {
        "national": 86.79,
        "remote": 121.51,
        "veryRemote": 130.19
      }
    }
  },
  "15_054_0128_1_3": {
    "itemNumber": "15_054_0128_1_3",
    "name": "Assessment Recommendation Therapy or Training - Psychologist",
    "shortName": "Assessment Recommendation Therapy or Training",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 232.99,
    "weekdayEveningRate": 232.99,
    "saturdayRate": 232.99,
    "sundayRate": 232.99,
    "publicHolidayRate": 232.99,
    "nightRate": 232.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assessment Recommendation Therapy or Training - Psychologist",
    "regionalPricing": {
      "weekday": {
        "national": 232.99,
        "remote": 326.19,
        "veryRemote": 349.49
      },
      "saturday": {
        "national": 232.99,
        "remote": 326.19,
        "veryRemote": 349.49
      },
      "sunday": {
        "national": 232.99,
        "remote": 326.19,
        "veryRemote": 349.49
      },
      "publicHoliday": {
        "national": 232.99,
        "remote": 326.19,
        "veryRemote": 349.49
      }
    }
  },
  "15_055_0128_1_3": {
    "itemNumber": "15_055_0128_1_3",
    "name": "Assessment Recommendation Therapy or Training - Physiotherapist",
    "shortName": "Assessment Recommendation Therapy or Training",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 183.99,
    "weekdayEveningRate": 183.99,
    "saturdayRate": 183.99,
    "sundayRate": 183.99,
    "publicHolidayRate": 183.99,
    "nightRate": 183.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assessment Recommendation Therapy or Training - Physiotherapist",
    "regionalPricing": {
      "weekday": {
        "national": 183.99,
        "remote": 257.59,
        "veryRemote": 275.99
      },
      "saturday": {
        "national": 183.99,
        "remote": 257.59,
        "veryRemote": 275.99
      },
      "sunday": {
        "national": 183.99,
        "remote": 257.59,
        "veryRemote": 275.99
      },
      "publicHoliday": {
        "national": 183.99,
        "remote": 257.59,
        "veryRemote": 275.99
      }
    }
  },
  "15_056_0128_1_3": {
    "itemNumber": "15_056_0128_1_3",
    "name": "Assessment Recommendation Therapy or Training - Other Professional",
    "shortName": "Assessment Recommendation Therapy or Training",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 193.99,
    "weekdayEveningRate": 193.99,
    "saturdayRate": 193.99,
    "sundayRate": 193.99,
    "publicHolidayRate": 193.99,
    "nightRate": 193.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assessment Recommendation Therapy or Training - Other Professional",
    "regionalPricing": {
      "weekday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "saturday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "sunday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "publicHoliday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      }
    }
  },
  "15_062_0128_3_3": {
    "itemNumber": "15_062_0128_3_3",
    "name": "Assessment Recommendation Therapy or Training - Dietitian",
    "shortName": "Assessment Recommendation Therapy or Training",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 188.99,
    "weekdayEveningRate": 188.99,
    "saturdayRate": 188.99,
    "sundayRate": 188.99,
    "publicHolidayRate": 188.99,
    "nightRate": 188.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assessment Recommendation Therapy or Training - Dietitian",
    "regionalPricing": {
      "weekday": {
        "national": 188.99,
        "remote": 264.59,
        "veryRemote": 283.49
      },
      "saturday": {
        "national": 188.99,
        "remote": 264.59,
        "veryRemote": 283.49
      },
      "sunday": {
        "national": 188.99,
        "remote": 264.59,
        "veryRemote": 283.49
      },
      "publicHoliday": {
        "national": 188.99,
        "remote": 264.59,
        "veryRemote": 283.49
      }
    }
  },
  "15_200_0126_1_3": {
    "itemNumber": "15_200_0126_1_3",
    "name": "Assessment Recommendation Therapy or Training - Exercise Physiologist",
    "shortName": "Assessment Recommendation Therapy or Training",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 166.99,
    "weekdayEveningRate": 166.99,
    "saturdayRate": 166.99,
    "sundayRate": 166.99,
    "publicHolidayRate": 166.99,
    "nightRate": 166.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assessment Recommendation Therapy or Training - Exercise Physiologist",
    "regionalPricing": {
      "weekday": {
        "national": 166.99,
        "remote": 233.79,
        "veryRemote": 250.49
      },
      "saturday": {
        "national": 166.99,
        "remote": 233.79,
        "veryRemote": 250.49
      },
      "sunday": {
        "national": 166.99,
        "remote": 233.79,
        "veryRemote": 250.49
      },
      "publicHoliday": {
        "national": 166.99,
        "remote": 233.79,
        "veryRemote": 250.49
      }
    }
  },
  "15_200_0128_1_3": {
    "itemNumber": "15_200_0128_1_3",
    "name": "Assessment Recommendation Therapy or Training - Exercise Physiologist",
    "shortName": "Assessment Recommendation Therapy or Training",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 166.99,
    "weekdayEveningRate": 166.99,
    "saturdayRate": 166.99,
    "sundayRate": 166.99,
    "publicHolidayRate": 166.99,
    "nightRate": 166.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assessment Recommendation Therapy or Training - Exercise Physiologist",
    "regionalPricing": {
      "weekday": {
        "national": 166.99,
        "remote": 233.79,
        "veryRemote": 250.49
      },
      "saturday": {
        "national": 166.99,
        "remote": 233.79,
        "veryRemote": 250.49
      },
      "sunday": {
        "national": 166.99,
        "remote": 233.79,
        "veryRemote": 250.49
      },
      "publicHoliday": {
        "national": 166.99,
        "remote": 233.79,
        "veryRemote": 250.49
      }
    }
  },
  "15_300_0103_1_3": {
    "itemNumber": "15_300_0103_1_3",
    "name": "Assistive Technology Mentoring",
    "shortName": "Assistive Technology Mentoring",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 105.43,
    "weekdayEveningRate": 105.43,
    "saturdayRate": 105.43,
    "sundayRate": 105.43,
    "publicHolidayRate": 105.43,
    "nightRate": 105.43,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assistive Technology Mentoring",
    "regionalPricing": {
      "weekday": {
        "national": 105.43,
        "remote": 147.6,
        "veryRemote": 158.15
      },
      "saturday": {
        "national": 105.43,
        "remote": 147.6,
        "veryRemote": 158.15
      },
      "sunday": {
        "national": 105.43,
        "remote": 147.6,
        "veryRemote": 158.15
      },
      "publicHoliday": {
        "national": 105.43,
        "remote": 147.6,
        "veryRemote": 158.15
      }
    }
  },
  "15_400_0114_1_3": {
    "itemNumber": "15_400_0114_1_3",
    "name": "Delivery of Health Supports by an Enrolled Nurse",
    "shortName": "Delivery of Health Supports by an Enrolled Nurse",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 99.88,
    "weekdayEveningRate": 99.88,
    "saturdayRate": 99.88,
    "sundayRate": 99.88,
    "publicHolidayRate": 99.88,
    "nightRate": 99.88,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by an Enrolled Nurse - Weekday Daytime",
    "regionalPricing": {
      "weekday": {
        "national": 99.88,
        "remote": 139.83,
        "veryRemote": 149.82
      },
      "saturday": {
        "national": 99.88,
        "remote": 139.83,
        "veryRemote": 149.82
      },
      "sunday": {
        "national": 99.88,
        "remote": 139.83,
        "veryRemote": 149.82
      },
      "publicHoliday": {
        "national": 99.88,
        "remote": 139.83,
        "veryRemote": 149.82
      }
    }
  },
  "15_401_0114_1_3": {
    "itemNumber": "15_401_0114_1_3",
    "name": "Delivery of Health Supports by an Enrolled Nurse",
    "shortName": "Delivery of Health Supports by an Enrolled Nurse",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 110.18,
    "weekdayEveningRate": 110.18,
    "saturdayRate": 110.18,
    "sundayRate": 110.18,
    "publicHolidayRate": 110.18,
    "nightRate": 110.18,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by an Enrolled Nurse - Weekday Evening",
    "regionalPricing": {
      "weekday": {
        "national": 110.18,
        "remote": 154.25,
        "veryRemote": 165.27
      },
      "saturday": {
        "national": 110.18,
        "remote": 154.25,
        "veryRemote": 165.27
      },
      "sunday": {
        "national": 110.18,
        "remote": 154.25,
        "veryRemote": 165.27
      },
      "publicHoliday": {
        "national": 110.18,
        "remote": 154.25,
        "veryRemote": 165.27
      }
    }
  },
  "15_402_0114_1_3": {
    "itemNumber": "15_402_0114_1_3",
    "name": "Delivery of Health Supports by an Enrolled Nurse",
    "shortName": "Delivery of Health Supports by an Enrolled Nurse",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 142.48,
    "weekdayEveningRate": 142.48,
    "saturdayRate": 142.48,
    "sundayRate": 142.48,
    "publicHolidayRate": 142.48,
    "nightRate": 142.48,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by an Enrolled Nurse - Saturday",
    "regionalPricing": {
      "weekday": {
        "national": 142.48,
        "remote": 199.47,
        "veryRemote": 213.72
      },
      "saturday": {
        "national": 142.48,
        "remote": 199.47,
        "veryRemote": 213.72
      },
      "sunday": {
        "national": 142.48,
        "remote": 199.47,
        "veryRemote": 213.72
      },
      "publicHoliday": {
        "national": 142.48,
        "remote": 199.47,
        "veryRemote": 213.72
      }
    }
  },
  "15_403_0114_1_3": {
    "itemNumber": "15_403_0114_1_3",
    "name": "Delivery of Health Supports by an Enrolled Nurse",
    "shortName": "Delivery of Health Supports by an Enrolled Nurse",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 163.79,
    "weekdayEveningRate": 163.79,
    "saturdayRate": 163.79,
    "sundayRate": 163.79,
    "publicHolidayRate": 163.79,
    "nightRate": 163.79,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by an Enrolled Nurse - Sunday",
    "regionalPricing": {
      "weekday": {
        "national": 163.79,
        "remote": 229.31,
        "veryRemote": 245.69
      },
      "saturday": {
        "national": 163.79,
        "remote": 229.31,
        "veryRemote": 245.69
      },
      "sunday": {
        "national": 163.79,
        "remote": 229.31,
        "veryRemote": 245.69
      },
      "publicHoliday": {
        "national": 163.79,
        "remote": 229.31,
        "veryRemote": 245.69
      }
    }
  },
  "15_404_0114_1_3": {
    "itemNumber": "15_404_0114_1_3",
    "name": "Delivery of Health Supports by an Enrolled Nurse",
    "shortName": "Delivery of Health Supports by an Enrolled Nurse",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 185.08,
    "weekdayEveningRate": 185.08,
    "saturdayRate": 185.08,
    "sundayRate": 185.08,
    "publicHolidayRate": 185.08,
    "nightRate": 185.08,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by an Enrolled Nurse - Public Holiday",
    "regionalPricing": {
      "weekday": {
        "national": 185.08,
        "remote": 259.11,
        "veryRemote": 277.62
      },
      "saturday": {
        "national": 185.08,
        "remote": 259.11,
        "veryRemote": 277.62
      },
      "sunday": {
        "national": 185.08,
        "remote": 259.11,
        "veryRemote": 277.62
      },
      "publicHoliday": {
        "national": 185.08,
        "remote": 259.11,
        "veryRemote": 277.62
      }
    }
  },
  "15_405_0114_1_3": {
    "itemNumber": "15_405_0114_1_3",
    "name": "Delivery of Health Supports by an Enrolled Nurse",
    "shortName": "Delivery of Health Supports by an Enrolled Nurse",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 112.22,
    "weekdayEveningRate": 112.22,
    "saturdayRate": 112.22,
    "sundayRate": 112.22,
    "publicHolidayRate": 112.22,
    "nightRate": 112.22,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by an Enrolled Nurse - Weekday Night",
    "regionalPricing": {
      "weekday": {
        "national": 112.22,
        "remote": 157.11,
        "veryRemote": 168.33
      },
      "saturday": {
        "national": 112.22,
        "remote": 157.11,
        "veryRemote": 168.33
      },
      "sunday": {
        "national": 112.22,
        "remote": 157.11,
        "veryRemote": 168.33
      },
      "publicHoliday": {
        "national": 112.22,
        "remote": 157.11,
        "veryRemote": 168.33
      }
    }
  },
  "15_406_0114_1_3": {
    "itemNumber": "15_406_0114_1_3",
    "name": "Delivery of Health Supports by a Registered Nurse",
    "shortName": "Delivery of Health Supports by a Registered Nurse",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 123.65,
    "weekdayEveningRate": 123.65,
    "saturdayRate": 123.65,
    "sundayRate": 123.65,
    "publicHolidayRate": 123.65,
    "nightRate": 123.65,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by a Registered Nurse - Weekday Daytime",
    "regionalPricing": {
      "weekday": {
        "national": 123.65,
        "remote": 173.11,
        "veryRemote": 185.48
      },
      "saturday": {
        "national": 123.65,
        "remote": 173.11,
        "veryRemote": 185.48
      },
      "sunday": {
        "national": 123.65,
        "remote": 173.11,
        "veryRemote": 185.48
      },
      "publicHoliday": {
        "national": 123.65,
        "remote": 173.11,
        "veryRemote": 185.48
      }
    }
  },
  "15_407_0114_1_3": {
    "itemNumber": "15_407_0114_1_3",
    "name": "Delivery of Health Supports by a Registered Nurse",
    "shortName": "Delivery of Health Supports by a Registered Nurse",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 136.41,
    "weekdayEveningRate": 136.41,
    "saturdayRate": 136.41,
    "sundayRate": 136.41,
    "publicHolidayRate": 136.41,
    "nightRate": 136.41,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by a Registered Nurse - Weekday Evening",
    "regionalPricing": {
      "weekday": {
        "national": 136.41,
        "remote": 190.97,
        "veryRemote": 204.62
      },
      "saturday": {
        "national": 136.41,
        "remote": 190.97,
        "veryRemote": 204.62
      },
      "sunday": {
        "national": 136.41,
        "remote": 190.97,
        "veryRemote": 204.62
      },
      "publicHoliday": {
        "national": 136.41,
        "remote": 190.97,
        "veryRemote": 204.62
      }
    }
  },
  "15_408_0114_1_3": {
    "itemNumber": "15_408_0114_1_3",
    "name": "Delivery of Health Supports by a Registered Nurse",
    "shortName": "Delivery of Health Supports by a Registered Nurse",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 176.47,
    "weekdayEveningRate": 176.47,
    "saturdayRate": 176.47,
    "sundayRate": 176.47,
    "publicHolidayRate": 176.47,
    "nightRate": 176.47,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by a Registered Nurse - Saturday",
    "regionalPricing": {
      "weekday": {
        "national": 176.47,
        "remote": 247.06,
        "veryRemote": 264.71
      },
      "saturday": {
        "national": 176.47,
        "remote": 247.06,
        "veryRemote": 264.71
      },
      "sunday": {
        "national": 176.47,
        "remote": 247.06,
        "veryRemote": 264.71
      },
      "publicHoliday": {
        "national": 176.47,
        "remote": 247.06,
        "veryRemote": 264.71
      }
    }
  },
  "15_409_0114_1_3": {
    "itemNumber": "15_409_0114_1_3",
    "name": "Delivery of Health Supports by a Registered Nurse",
    "shortName": "Delivery of Health Supports by a Registered Nurse",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 202.87,
    "weekdayEveningRate": 202.87,
    "saturdayRate": 202.87,
    "sundayRate": 202.87,
    "publicHolidayRate": 202.87,
    "nightRate": 202.87,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by a Registered Nurse - Sunday",
    "regionalPricing": {
      "weekday": {
        "national": 202.87,
        "remote": 284.02,
        "veryRemote": 304.31
      },
      "saturday": {
        "national": 202.87,
        "remote": 284.02,
        "veryRemote": 304.31
      },
      "sunday": {
        "national": 202.87,
        "remote": 284.02,
        "veryRemote": 304.31
      },
      "publicHoliday": {
        "national": 202.87,
        "remote": 284.02,
        "veryRemote": 304.31
      }
    }
  },
  "15_410_0114_1_3": {
    "itemNumber": "15_410_0114_1_3",
    "name": "Delivery of Health Supports by a Registered Nurse",
    "shortName": "Delivery of Health Supports by a Registered Nurse",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 229.27,
    "weekdayEveningRate": 229.27,
    "saturdayRate": 229.27,
    "sundayRate": 229.27,
    "publicHolidayRate": 229.27,
    "nightRate": 229.27,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by a Registered Nurse - Public Holiday",
    "regionalPricing": {
      "weekday": {
        "national": 229.27,
        "remote": 320.98,
        "veryRemote": 343.91
      },
      "saturday": {
        "national": 229.27,
        "remote": 320.98,
        "veryRemote": 343.91
      },
      "sunday": {
        "national": 229.27,
        "remote": 320.98,
        "veryRemote": 343.91
      },
      "publicHoliday": {
        "national": 229.27,
        "remote": 320.98,
        "veryRemote": 343.91
      }
    }
  },
  "15_411_0114_1_3": {
    "itemNumber": "15_411_0114_1_3",
    "name": "Delivery of Health Supports by a Registered Nurse",
    "shortName": "Delivery of Health Supports by a Registered Nurse",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 138.95,
    "weekdayEveningRate": 138.95,
    "saturdayRate": 138.95,
    "sundayRate": 138.95,
    "publicHolidayRate": 138.95,
    "nightRate": 138.95,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by a Registered Nurse - Weekday Night",
    "regionalPricing": {
      "weekday": {
        "national": 138.95,
        "remote": 194.53,
        "veryRemote": 208.43
      },
      "saturday": {
        "national": 138.95,
        "remote": 194.53,
        "veryRemote": 208.43
      },
      "sunday": {
        "national": 138.95,
        "remote": 194.53,
        "veryRemote": 208.43
      },
      "publicHoliday": {
        "national": 138.95,
        "remote": 194.53,
        "veryRemote": 208.43
      }
    }
  },
  "15_412_0114_1_3": {
    "itemNumber": "15_412_0114_1_3",
    "name": "Delivery of Health Supports by a Clinical Nurse",
    "shortName": "Delivery of Health Supports by a Clinical Nurse",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 143.04,
    "weekdayEveningRate": 143.04,
    "saturdayRate": 143.04,
    "sundayRate": 143.04,
    "publicHolidayRate": 143.04,
    "nightRate": 143.04,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by a Clinical Nurse - Weekday Daytime",
    "regionalPricing": {
      "weekday": {
        "national": 143.04,
        "remote": 200.26,
        "veryRemote": 214.56
      },
      "saturday": {
        "national": 143.04,
        "remote": 200.26,
        "veryRemote": 214.56
      },
      "sunday": {
        "national": 143.04,
        "remote": 200.26,
        "veryRemote": 214.56
      },
      "publicHoliday": {
        "national": 143.04,
        "remote": 200.26,
        "veryRemote": 214.56
      }
    }
  },
  "15_413_0114_1_3": {
    "itemNumber": "15_413_0114_1_3",
    "name": "Delivery of Health Supports by a Clinical Nurse",
    "shortName": "Delivery of Health Supports by a Clinical Nurse",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 157.77,
    "weekdayEveningRate": 157.77,
    "saturdayRate": 157.77,
    "sundayRate": 157.77,
    "publicHolidayRate": 157.77,
    "nightRate": 157.77,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by a Clinical Nurse - Weekday Evening",
    "regionalPricing": {
      "weekday": {
        "national": 157.77,
        "remote": 220.88,
        "veryRemote": 236.66
      },
      "saturday": {
        "national": 157.77,
        "remote": 220.88,
        "veryRemote": 236.66
      },
      "sunday": {
        "national": 157.77,
        "remote": 220.88,
        "veryRemote": 236.66
      },
      "publicHoliday": {
        "national": 157.77,
        "remote": 220.88,
        "veryRemote": 236.66
      }
    }
  },
  "15_414_0114_1_3": {
    "itemNumber": "15_414_0114_1_3",
    "name": "Delivery of Health Supports by a Clinical Nurse",
    "shortName": "Delivery of Health Supports by a Clinical Nurse",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 204.12,
    "weekdayEveningRate": 204.12,
    "saturdayRate": 204.12,
    "sundayRate": 204.12,
    "publicHolidayRate": 204.12,
    "nightRate": 204.12,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by a Clinical Nurse - Saturday",
    "regionalPricing": {
      "weekday": {
        "national": 204.12,
        "remote": 285.77,
        "veryRemote": 306.18
      },
      "saturday": {
        "national": 204.12,
        "remote": 285.77,
        "veryRemote": 306.18
      },
      "sunday": {
        "national": 204.12,
        "remote": 285.77,
        "veryRemote": 306.18
      },
      "publicHoliday": {
        "national": 204.12,
        "remote": 285.77,
        "veryRemote": 306.18
      }
    }
  },
  "15_415_0114_1_3": {
    "itemNumber": "15_415_0114_1_3",
    "name": "Delivery of Health Supports by a Clinical Nurse",
    "shortName": "Delivery of Health Supports by a Clinical Nurse",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 234.67,
    "weekdayEveningRate": 234.67,
    "saturdayRate": 234.67,
    "sundayRate": 234.67,
    "publicHolidayRate": 234.67,
    "nightRate": 234.67,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by a Clinical Nurse - Sunday",
    "regionalPricing": {
      "weekday": {
        "national": 234.67,
        "remote": 328.54,
        "veryRemote": 352.01
      },
      "saturday": {
        "national": 234.67,
        "remote": 328.54,
        "veryRemote": 352.01
      },
      "sunday": {
        "national": 234.67,
        "remote": 328.54,
        "veryRemote": 352.01
      },
      "publicHoliday": {
        "national": 234.67,
        "remote": 328.54,
        "veryRemote": 352.01
      }
    }
  },
  "15_416_0114_1_3": {
    "itemNumber": "15_416_0114_1_3",
    "name": "Delivery of Health Supports by a Clinical Nurse",
    "shortName": "Delivery of Health Supports by a Clinical Nurse",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 265.2,
    "weekdayEveningRate": 265.2,
    "saturdayRate": 265.2,
    "sundayRate": 265.2,
    "publicHolidayRate": 265.2,
    "nightRate": 265.2,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by a Clinical Nurse - Public Holiday",
    "regionalPricing": {
      "weekday": {
        "national": 265.2,
        "remote": 371.28,
        "veryRemote": 397.8
      },
      "saturday": {
        "national": 265.2,
        "remote": 371.28,
        "veryRemote": 397.8
      },
      "sunday": {
        "national": 265.2,
        "remote": 371.28,
        "veryRemote": 397.8
      },
      "publicHoliday": {
        "national": 265.2,
        "remote": 371.28,
        "veryRemote": 397.8
      }
    }
  },
  "15_417_0114_1_3": {
    "itemNumber": "15_417_0114_1_3",
    "name": "Delivery of Health Supports by a Clinical Nurse",
    "shortName": "Delivery of Health Supports by a Clinical Nurse",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 160.73,
    "weekdayEveningRate": 160.73,
    "saturdayRate": 160.73,
    "sundayRate": 160.73,
    "publicHolidayRate": 160.73,
    "nightRate": 160.73,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by a Clinical Nurse - Weekday Night",
    "regionalPricing": {
      "weekday": {
        "national": 160.73,
        "remote": 225.02,
        "veryRemote": 241.1
      },
      "saturday": {
        "national": 160.73,
        "remote": 225.02,
        "veryRemote": 241.1
      },
      "sunday": {
        "national": 160.73,
        "remote": 225.02,
        "veryRemote": 241.1
      },
      "publicHoliday": {
        "national": 160.73,
        "remote": 225.02,
        "veryRemote": 241.1
      }
    }
  },
  "15_418_0114_1_3": {
    "itemNumber": "15_418_0114_1_3",
    "name": "Delivery of Health Supports by a Clinical Nurse Consultant",
    "shortName": "Delivery of Health Supports by a Clinical Nurse Consultant",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 169.16,
    "weekdayEveningRate": 169.16,
    "saturdayRate": 169.16,
    "sundayRate": 169.16,
    "publicHolidayRate": 169.16,
    "nightRate": 169.16,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by a Clinical Nurse Consultant - Weekday Daytime",
    "regionalPricing": {
      "weekday": {
        "national": 169.16,
        "remote": 236.82,
        "veryRemote": 253.74
      },
      "saturday": {
        "national": 169.16,
        "remote": 236.82,
        "veryRemote": 253.74
      },
      "sunday": {
        "national": 169.16,
        "remote": 236.82,
        "veryRemote": 253.74
      },
      "publicHoliday": {
        "national": 169.16,
        "remote": 236.82,
        "veryRemote": 253.74
      }
    }
  },
  "15_419_0114_1_3": {
    "itemNumber": "15_419_0114_1_3",
    "name": "Delivery of Health Supports by a Clinical Nurse Consultant",
    "shortName": "Delivery of Health Supports by a Clinical Nurse Consultant",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 186.63,
    "weekdayEveningRate": 186.63,
    "saturdayRate": 186.63,
    "sundayRate": 186.63,
    "publicHolidayRate": 186.63,
    "nightRate": 186.63,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by a Clinical Nurse Consultant - Weekday Evening",
    "regionalPricing": {
      "weekday": {
        "national": 186.63,
        "remote": 261.28,
        "veryRemote": 279.95
      },
      "saturday": {
        "national": 186.63,
        "remote": 261.28,
        "veryRemote": 279.95
      },
      "sunday": {
        "national": 186.63,
        "remote": 261.28,
        "veryRemote": 279.95
      },
      "publicHoliday": {
        "national": 186.63,
        "remote": 261.28,
        "veryRemote": 279.95
      }
    }
  },
  "15_420_0114_1_3": {
    "itemNumber": "15_420_0114_1_3",
    "name": "Delivery of Health Supports by a Clinical Nurse Consultant",
    "shortName": "Delivery of Health Supports by a Clinical Nurse Consultant",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 241.52,
    "weekdayEveningRate": 241.52,
    "saturdayRate": 241.52,
    "sundayRate": 241.52,
    "publicHolidayRate": 241.52,
    "nightRate": 241.52,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by a Clinical Nurse Consultant - Saturday",
    "regionalPricing": {
      "weekday": {
        "national": 241.52,
        "remote": 338.13,
        "veryRemote": 362.28
      },
      "saturday": {
        "national": 241.52,
        "remote": 338.13,
        "veryRemote": 362.28
      },
      "sunday": {
        "national": 241.52,
        "remote": 338.13,
        "veryRemote": 362.28
      },
      "publicHoliday": {
        "national": 241.52,
        "remote": 338.13,
        "veryRemote": 362.28
      }
    }
  },
  "15_421_0114_1_3": {
    "itemNumber": "15_421_0114_1_3",
    "name": "Delivery of Health Supports by a Clinical Nurse Consultant",
    "shortName": "Delivery of Health Supports by a Clinical Nurse Consultant",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 277.69,
    "weekdayEveningRate": 277.69,
    "saturdayRate": 277.69,
    "sundayRate": 277.69,
    "publicHolidayRate": 277.69,
    "nightRate": 277.69,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by a Clinical Nurse Consultant - Sunday",
    "regionalPricing": {
      "weekday": {
        "national": 277.69,
        "remote": 388.77,
        "veryRemote": 416.54
      },
      "saturday": {
        "national": 277.69,
        "remote": 388.77,
        "veryRemote": 416.54
      },
      "sunday": {
        "national": 277.69,
        "remote": 388.77,
        "veryRemote": 416.54
      },
      "publicHoliday": {
        "national": 277.69,
        "remote": 388.77,
        "veryRemote": 416.54
      }
    }
  },
  "15_422_0114_1_3": {
    "itemNumber": "15_422_0114_1_3",
    "name": "Delivery of Health Supports by a Clinical Nurse Consultant",
    "shortName": "Delivery of Health Supports by a Clinical Nurse Consultant",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 313.86,
    "weekdayEveningRate": 313.86,
    "saturdayRate": 313.86,
    "sundayRate": 313.86,
    "publicHolidayRate": 313.86,
    "nightRate": 313.86,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by a Clinical Nurse Consultant - Public Holiday",
    "regionalPricing": {
      "weekday": {
        "national": 313.86,
        "remote": 439.4,
        "veryRemote": 470.79
      },
      "saturday": {
        "national": 313.86,
        "remote": 439.4,
        "veryRemote": 470.79
      },
      "sunday": {
        "national": 313.86,
        "remote": 439.4,
        "veryRemote": 470.79
      },
      "publicHoliday": {
        "national": 313.86,
        "remote": 439.4,
        "veryRemote": 470.79
      }
    }
  },
  "15_423_0114_1_3": {
    "itemNumber": "15_423_0114_1_3",
    "name": "Delivery of Health Supports by a Clinical Nurse Consultant",
    "shortName": "Delivery of Health Supports by a Clinical Nurse Consultant",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 190.12,
    "weekdayEveningRate": 190.12,
    "saturdayRate": 190.12,
    "sundayRate": 190.12,
    "publicHolidayRate": 190.12,
    "nightRate": 190.12,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by a Clinical Nurse Consultant - Weekday Night",
    "regionalPricing": {
      "weekday": {
        "national": 190.12,
        "remote": 266.17,
        "veryRemote": 285.18
      },
      "saturday": {
        "national": 190.12,
        "remote": 266.17,
        "veryRemote": 285.18
      },
      "sunday": {
        "national": 190.12,
        "remote": 266.17,
        "veryRemote": 285.18
      },
      "publicHoliday": {
        "national": 190.12,
        "remote": 266.17,
        "veryRemote": 285.18
      }
    }
  },
  "15_424_0114_1_3": {
    "itemNumber": "15_424_0114_1_3",
    "name": "Delivery of Health Supports by a Nurse Practitioner",
    "shortName": "Delivery of Health Supports by a Nurse Practitioner",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 176.85,
    "weekdayEveningRate": 176.85,
    "saturdayRate": 176.85,
    "sundayRate": 176.85,
    "publicHolidayRate": 176.85,
    "nightRate": 176.85,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by a Nurse Practitioner - Weekday Daytime",
    "regionalPricing": {
      "weekday": {
        "national": 176.85,
        "remote": 247.59,
        "veryRemote": 265.28
      },
      "saturday": {
        "national": 176.85,
        "remote": 247.59,
        "veryRemote": 265.28
      },
      "sunday": {
        "national": 176.85,
        "remote": 247.59,
        "veryRemote": 265.28
      },
      "publicHoliday": {
        "national": 176.85,
        "remote": 247.59,
        "veryRemote": 265.28
      }
    }
  },
  "15_425_0114_1_3": {
    "itemNumber": "15_425_0114_1_3",
    "name": "Delivery of Health Supports by a Nurse Practitioner",
    "shortName": "Delivery of Health Supports by a Nurse Practitioner",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 195.09,
    "weekdayEveningRate": 195.09,
    "saturdayRate": 195.09,
    "sundayRate": 195.09,
    "publicHolidayRate": 195.09,
    "nightRate": 195.09,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by a Nurse Practitioner - Weekday Evening",
    "regionalPricing": {
      "weekday": {
        "national": 195.09,
        "remote": 273.13,
        "veryRemote": 292.64
      },
      "saturday": {
        "national": 195.09,
        "remote": 273.13,
        "veryRemote": 292.64
      },
      "sunday": {
        "national": 195.09,
        "remote": 273.13,
        "veryRemote": 292.64
      },
      "publicHoliday": {
        "national": 195.09,
        "remote": 273.13,
        "veryRemote": 292.64
      }
    }
  },
  "15_426_0114_1_3": {
    "itemNumber": "15_426_0114_1_3",
    "name": "Delivery of Health Supports by a Nurse Practitioner",
    "shortName": "Delivery of Health Supports by a Nurse Practitioner",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 252.51,
    "weekdayEveningRate": 252.51,
    "saturdayRate": 252.51,
    "sundayRate": 252.51,
    "publicHolidayRate": 252.51,
    "nightRate": 252.51,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by a Nurse Practitioner - Saturday",
    "regionalPricing": {
      "weekday": {
        "national": 252.51,
        "remote": 353.51,
        "veryRemote": 378.77
      },
      "saturday": {
        "national": 252.51,
        "remote": 353.51,
        "veryRemote": 378.77
      },
      "sunday": {
        "national": 252.51,
        "remote": 353.51,
        "veryRemote": 378.77
      },
      "publicHoliday": {
        "national": 252.51,
        "remote": 353.51,
        "veryRemote": 378.77
      }
    }
  },
  "15_427_0114_1_3": {
    "itemNumber": "15_427_0114_1_3",
    "name": "Delivery of Health Supports by a Nurse Practitioner",
    "shortName": "Delivery of Health Supports by a Nurse Practitioner",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 290.33,
    "weekdayEveningRate": 290.33,
    "saturdayRate": 290.33,
    "sundayRate": 290.33,
    "publicHolidayRate": 290.33,
    "nightRate": 290.33,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by a Nurse Practitioner - Sunday",
    "regionalPricing": {
      "weekday": {
        "national": 290.33,
        "remote": 406.46,
        "veryRemote": 435.5
      },
      "saturday": {
        "national": 290.33,
        "remote": 406.46,
        "veryRemote": 435.5
      },
      "sunday": {
        "national": 290.33,
        "remote": 406.46,
        "veryRemote": 435.5
      },
      "publicHoliday": {
        "national": 290.33,
        "remote": 406.46,
        "veryRemote": 435.5
      }
    }
  },
  "15_428_0114_1_3": {
    "itemNumber": "15_428_0114_1_3",
    "name": "Delivery of Health Supports by a Nurse Practitioner",
    "shortName": "Delivery of Health Supports by a Nurse Practitioner",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 328.16,
    "weekdayEveningRate": 328.16,
    "saturdayRate": 328.16,
    "sundayRate": 328.16,
    "publicHolidayRate": 328.16,
    "nightRate": 328.16,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by a Nurse Practitioner - Public Holiday",
    "regionalPricing": {
      "weekday": {
        "national": 328.16,
        "remote": 459.42,
        "veryRemote": 492.24
      },
      "saturday": {
        "national": 328.16,
        "remote": 459.42,
        "veryRemote": 492.24
      },
      "sunday": {
        "national": 328.16,
        "remote": 459.42,
        "veryRemote": 492.24
      },
      "publicHoliday": {
        "national": 328.16,
        "remote": 459.42,
        "veryRemote": 492.24
      }
    }
  },
  "15_429_0114_1_3": {
    "itemNumber": "15_429_0114_1_3",
    "name": "Delivery of Health Supports by a Nurse Practitioner",
    "shortName": "Delivery of Health Supports by a Nurse Practitioner",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 198.75,
    "weekdayEveningRate": 198.75,
    "saturdayRate": 198.75,
    "sundayRate": 198.75,
    "publicHolidayRate": 198.75,
    "nightRate": 198.75,
    "ttp": false,
    "quoteRequired": false,
    "description": "Delivery of Health Supports by a Nurse Practitioner - Weekday Night",
    "regionalPricing": {
      "weekday": {
        "national": 198.75,
        "remote": 278.25,
        "veryRemote": 298.13
      },
      "saturday": {
        "national": 198.75,
        "remote": 278.25,
        "veryRemote": 298.13
      },
      "sunday": {
        "national": 198.75,
        "remote": 278.25,
        "veryRemote": 298.13
      },
      "publicHoliday": {
        "national": 198.75,
        "remote": 278.25,
        "veryRemote": 298.13
      }
    }
  },
  "15_501_0119_1_3": {
    "itemNumber": "15_501_0119_1_3",
    "name": "Provision of Hearing Services by an Audiologist",
    "shortName": "Provision of Hearing Services by an Audiologist",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 193.99,
    "weekdayEveningRate": 193.99,
    "saturdayRate": 193.99,
    "sundayRate": 193.99,
    "publicHolidayRate": 193.99,
    "nightRate": 193.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provision of Hearing Services by an Audiologist",
    "regionalPricing": {
      "weekday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "saturday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "sunday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "publicHoliday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      }
    }
  },
  "15_502_0134_1_3": {
    "itemNumber": "15_502_0134_1_3",
    "name": "Provision of Hearing Services by an Audiologist",
    "shortName": "Provision of Hearing Services by an Audiologist",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 193.99,
    "weekdayEveningRate": 193.99,
    "saturdayRate": 193.99,
    "sundayRate": 193.99,
    "publicHolidayRate": 193.99,
    "nightRate": 193.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provision of Hearing Services by an Audiologist",
    "regionalPricing": {
      "weekday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "saturday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "sunday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "publicHoliday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      }
    }
  },
  "15_503_0134_1_3": {
    "itemNumber": "15_503_0134_1_3",
    "name": "Provision of Hearing Services by an Audiometrist",
    "shortName": "Provision of Hearing Services by an Audiometrist",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 166.83,
    "weekdayEveningRate": 166.83,
    "saturdayRate": 166.83,
    "sundayRate": 166.83,
    "publicHolidayRate": 166.83,
    "nightRate": 166.83,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provision of Hearing Services by an Audiometrist",
    "regionalPricing": {
      "weekday": {
        "national": 166.83,
        "remote": 233.56,
        "veryRemote": 250.25
      },
      "saturday": {
        "national": 166.83,
        "remote": 233.56,
        "veryRemote": 250.25
      },
      "sunday": {
        "national": 166.83,
        "remote": 233.56,
        "veryRemote": 250.25
      },
      "publicHoliday": {
        "national": 166.83,
        "remote": 233.56,
        "veryRemote": 250.25
      }
    }
  },
  "15_610_0128_1_3": {
    "itemNumber": "15_610_0128_1_3",
    "name": "Assessment Recommendation Therapy or Training - Art Therapist",
    "shortName": "Assessment Recommendation Therapy or Training",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 193.99,
    "weekdayEveningRate": 193.99,
    "saturdayRate": 193.99,
    "sundayRate": 193.99,
    "publicHolidayRate": 193.99,
    "nightRate": 193.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assessment Recommendation Therapy or Training - Art Therapist",
    "regionalPricing": {
      "weekday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "saturday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "sunday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "publicHoliday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      }
    }
  },
  "15_611_0128_1_3": {
    "itemNumber": "15_611_0128_1_3",
    "name": "Assessment Recommendation Therapy or Training - Audiologist",
    "shortName": "Assessment Recommendation Therapy or Training",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 193.99,
    "weekdayEveningRate": 193.99,
    "saturdayRate": 193.99,
    "sundayRate": 193.99,
    "publicHolidayRate": 193.99,
    "nightRate": 193.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assessment Recommendation Therapy or Training - Audiologist",
    "regionalPricing": {
      "weekday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "saturday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "sunday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "publicHoliday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      }
    }
  },
  "15_613_0118_1_3": {
    "itemNumber": "15_613_0118_1_3",
    "name": "Early Childhood Intervention Professional – Developmental educator",
    "shortName": "Early Childhood Intervention Professional – Developmental educator",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 193.99,
    "weekdayEveningRate": 193.99,
    "saturdayRate": 193.99,
    "sundayRate": 193.99,
    "publicHolidayRate": 193.99,
    "nightRate": 193.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Early Childhood Intervention Professional – Developmental educator",
    "regionalPricing": {
      "weekday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "saturday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "sunday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "publicHoliday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      }
    }
  },
  "15_613_0128_1_3": {
    "itemNumber": "15_613_0128_1_3",
    "name": "Assessment Recommendation Therapy or Training - Developmental Educator",
    "shortName": "Assessment Recommendation Therapy or Training",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 193.99,
    "weekdayEveningRate": 193.99,
    "saturdayRate": 193.99,
    "sundayRate": 193.99,
    "publicHolidayRate": 193.99,
    "nightRate": 193.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assessment Recommendation Therapy or Training - Developmental Educator",
    "regionalPricing": {
      "weekday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "saturday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "sunday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "publicHoliday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      }
    }
  },
  "15_615_0128_1_3": {
    "itemNumber": "15_615_0128_1_3",
    "name": "Assessment Recommendation Therapy or Training - Music Therapist",
    "shortName": "Assessment Recommendation Therapy or Training",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 193.99,
    "weekdayEveningRate": 193.99,
    "saturdayRate": 193.99,
    "sundayRate": 193.99,
    "publicHolidayRate": 193.99,
    "nightRate": 193.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assessment Recommendation Therapy or Training - Music Therapist",
    "regionalPricing": {
      "weekday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "saturday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "sunday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "publicHoliday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      }
    }
  },
  "15_617_0118_1_3": {
    "itemNumber": "15_617_0118_1_3",
    "name": "Early Childhood Intervention Professional – Occupational Therapist",
    "shortName": "Early Childhood Intervention Professional – Occupational Therapist",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 193.99,
    "weekdayEveningRate": 193.99,
    "saturdayRate": 193.99,
    "sundayRate": 193.99,
    "publicHolidayRate": 193.99,
    "nightRate": 193.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Early Childhood Intervention Professional – Occupational Therapist",
    "regionalPricing": {
      "weekday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "saturday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "sunday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "publicHoliday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      }
    }
  },
  "15_617_0128_1_3": {
    "itemNumber": "15_617_0128_1_3",
    "name": "Assessment Recommendation Therapy or Training - Occupational Therapist",
    "shortName": "Assessment Recommendation Therapy or Training",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 193.99,
    "weekdayEveningRate": 193.99,
    "saturdayRate": 193.99,
    "sundayRate": 193.99,
    "publicHolidayRate": 193.99,
    "nightRate": 193.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assessment Recommendation Therapy or Training - Occupational Therapist",
    "regionalPricing": {
      "weekday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "saturday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "sunday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "publicHoliday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      }
    }
  },
  "15_618_0128_1_3": {
    "itemNumber": "15_618_0128_1_3",
    "name": "Assessment Recommendation Therapy or Training - Orthoptist",
    "shortName": "Assessment Recommendation Therapy or Training",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 193.99,
    "weekdayEveningRate": 193.99,
    "saturdayRate": 193.99,
    "sundayRate": 193.99,
    "publicHolidayRate": 193.99,
    "nightRate": 193.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assessment Recommendation Therapy or Training - Orthoptist",
    "regionalPricing": {
      "weekday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "saturday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "sunday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "publicHoliday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      }
    }
  },
  "15_619_0128_1_3": {
    "itemNumber": "15_619_0128_1_3",
    "name": "Assessment Recommendation Therapy or Training - Podiatrist",
    "shortName": "Assessment Recommendation Therapy or Training",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 188.99,
    "weekdayEveningRate": 188.99,
    "saturdayRate": 188.99,
    "sundayRate": 188.99,
    "publicHolidayRate": 188.99,
    "nightRate": 188.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assessment Recommendation Therapy or Training - Podiatrist",
    "regionalPricing": {
      "weekday": {
        "national": 188.99,
        "remote": 264.59,
        "veryRemote": 283.49
      },
      "saturday": {
        "national": 188.99,
        "remote": 264.59,
        "veryRemote": 283.49
      },
      "sunday": {
        "national": 188.99,
        "remote": 264.59,
        "veryRemote": 283.49
      },
      "publicHoliday": {
        "national": 188.99,
        "remote": 264.59,
        "veryRemote": 283.49
      }
    }
  },
  "15_620_0128_1_3": {
    "itemNumber": "15_620_0128_1_3",
    "name": "Assessment Recommendation Therapy or Training - Rehabilitation Counsellor",
    "shortName": "Assessment Recommendation Therapy or Training",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 193.99,
    "weekdayEveningRate": 193.99,
    "saturdayRate": 193.99,
    "sundayRate": 193.99,
    "publicHolidayRate": 193.99,
    "nightRate": 193.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assessment Recommendation Therapy or Training - Rehabilitation Counsellor",
    "regionalPricing": {
      "weekday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "saturday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "sunday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "publicHoliday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      }
    }
  },
  "15_621_0118_1_3": {
    "itemNumber": "15_621_0118_1_3",
    "name": "Early Childhood Intervention Professional – Social worker",
    "shortName": "Early Childhood Intervention Professional – Social worker",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 193.99,
    "weekdayEveningRate": 193.99,
    "saturdayRate": 193.99,
    "sundayRate": 193.99,
    "publicHolidayRate": 193.99,
    "nightRate": 193.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Early Childhood Intervention Professional – Social worker",
    "regionalPricing": {
      "weekday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "saturday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "sunday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "publicHoliday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      }
    }
  },
  "15_621_0128_1_3": {
    "itemNumber": "15_621_0128_1_3",
    "name": "Assessment Recommendation Therapy or Training - Social Worker",
    "shortName": "Assessment Recommendation Therapy or Training",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 193.99,
    "weekdayEveningRate": 193.99,
    "saturdayRate": 193.99,
    "sundayRate": 193.99,
    "publicHolidayRate": 193.99,
    "nightRate": 193.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assessment Recommendation Therapy or Training - Social Worker",
    "regionalPricing": {
      "weekday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "saturday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "sunday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "publicHoliday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      }
    }
  },
  "15_622_0118_1_3": {
    "itemNumber": "15_622_0118_1_3",
    "name": "Early Childhood Intervention Professional – Speech Pathologist",
    "shortName": "Early Childhood Intervention Professional – Speech Pathologist",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 193.99,
    "weekdayEveningRate": 193.99,
    "saturdayRate": 193.99,
    "sundayRate": 193.99,
    "publicHolidayRate": 193.99,
    "nightRate": 193.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Early Childhood Intervention Professional – Speech Pathologist",
    "regionalPricing": {
      "weekday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "saturday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "sunday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "publicHoliday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      }
    }
  },
  "15_622_0128_1_3": {
    "itemNumber": "15_622_0128_1_3",
    "name": "Assessment Recommendation Therapy or Training - Speech Pathologist",
    "shortName": "Assessment Recommendation Therapy or Training",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 193.99,
    "weekdayEveningRate": 193.99,
    "saturdayRate": 193.99,
    "sundayRate": 193.99,
    "publicHolidayRate": 193.99,
    "nightRate": 193.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assessment Recommendation Therapy or Training - Speech Pathologist",
    "regionalPricing": {
      "weekday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "saturday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "sunday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "publicHoliday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      }
    }
  },
  "15_625_0118_1_3": {
    "itemNumber": "15_625_0118_1_3",
    "name": "Early Childhood Intervention Professional – Early Childhood Teacher or Educator",
    "shortName": "Early Childhood Intervention Professional – Early Childhood Teacher or Educator",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 193.99,
    "weekdayEveningRate": 193.99,
    "saturdayRate": 193.99,
    "sundayRate": 193.99,
    "publicHolidayRate": 193.99,
    "nightRate": 193.99,
    "ttp": false,
    "quoteRequired": false,
    "description": "Early Childhood Intervention Professional – Early Childhood Teacher or Educator",
    "regionalPricing": {
      "weekday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "saturday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "sunday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      },
      "publicHoliday": {
        "national": 193.99,
        "remote": 271.59,
        "veryRemote": 290.99
      }
    }
  },
  "15_799_0103_6_3": {
    "itemNumber": "15_799_0103_6_3",
    "name": "Provider travel - non-labour costs",
    "shortName": "Provider travel",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provider travel - non-labour costs",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "15_799_0106_1_3": {
    "itemNumber": "15_799_0106_1_3",
    "name": "Provider travel - non-labour costs",
    "shortName": "Provider travel",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provider travel - non-labour costs",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "15_799_0114_1_3": {
    "itemNumber": "15_799_0114_1_3",
    "name": "Provider travel - non-labour costs",
    "shortName": "Provider travel",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provider travel - non-labour costs",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "15_799_0117_1_3": {
    "itemNumber": "15_799_0117_1_3",
    "name": "Provider travel - non-labour costs",
    "shortName": "Provider travel",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provider travel - non-labour costs",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "15_799_0118_1_3": {
    "itemNumber": "15_799_0118_1_3",
    "name": "Provider travel - non-labour costs",
    "shortName": "Provider travel",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provider travel - non-labour costs",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "15_799_0119_1_3": {
    "itemNumber": "15_799_0119_1_3",
    "name": "Provider travel - non-labour costs",
    "shortName": "Provider travel",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provider travel - non-labour costs",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "15_799_0126_1_3": {
    "itemNumber": "15_799_0126_1_3",
    "name": "Provider travel - non-labour costs",
    "shortName": "Provider travel",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provider travel - non-labour costs",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "15_799_0128_1_3": {
    "itemNumber": "15_799_0128_1_3",
    "name": "Provider travel - non-labour costs",
    "shortName": "Provider travel",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provider travel - non-labour costs",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "15_799_0134_1_3": {
    "itemNumber": "15_799_0134_1_3",
    "name": "Provider travel - non-labour costs",
    "shortName": "Provider travel",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provider travel - non-labour costs",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "15_799_0135_1_3": {
    "itemNumber": "15_799_0135_1_3",
    "name": "Provider travel - non-labour costs",
    "shortName": "Provider travel",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provider travel - non-labour costs",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "04_049_0125_1_1": {
    "itemNumber": "04_049_0125_1_1",
    "name": "Establishment Fee for Personal Care/Participation",
    "shortName": "Establishment Fee for Personal Care/Participation",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 702.3,
    "weekdayEveningRate": 702.3,
    "saturdayRate": 702.3,
    "sundayRate": 702.3,
    "publicHolidayRate": 702.3,
    "nightRate": 702.3,
    "ttp": false,
    "quoteRequired": false,
    "description": "Establishment Fee for Personal Care/Participation",
    "regionalPricing": {
      "weekday": {
        "national": 702.3,
        "remote": 983.22,
        "veryRemote": 1053.45
      },
      "saturday": {
        "national": 702.3,
        "remote": 983.22,
        "veryRemote": 1053.45
      },
      "sunday": {
        "national": 702.3,
        "remote": 983.22,
        "veryRemote": 1053.45
      },
      "publicHoliday": {
        "national": 702.3,
        "remote": 983.22,
        "veryRemote": 1053.45
      }
    }
  },
  "04_102_0125_6_1": {
    "itemNumber": "04_102_0125_6_1",
    "name": "Access Community Social and Rec Activ - Standard",
    "shortName": "Access Community Social and Rec Activ",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 156.03,
    "weekdayEveningRate": 156.03,
    "saturdayRate": 156.03,
    "sundayRate": 156.03,
    "publicHolidayRate": 156.03,
    "nightRate": 156.03,
    "ttp": false,
    "quoteRequired": false,
    "description": "Access Community Social and Rec Activ - Standard - Public Holiday",
    "regionalPricing": {
      "weekday": {
        "national": 156.03,
        "remote": 218.44,
        "veryRemote": 234.05
      },
      "saturday": {
        "national": 156.03,
        "remote": 218.44,
        "veryRemote": 234.05
      },
      "sunday": {
        "national": 156.03,
        "remote": 218.44,
        "veryRemote": 234.05
      },
      "publicHoliday": {
        "national": 156.03,
        "remote": 218.44,
        "veryRemote": 234.05
      }
    }
  },
  "04_103_0125_6_1": {
    "itemNumber": "04_103_0125_6_1",
    "name": "Access Community Social and Rec Activ - Standard",
    "shortName": "Access Community Social and Rec Activ",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 77.38,
    "weekdayEveningRate": 77.38,
    "saturdayRate": 77.38,
    "sundayRate": 77.38,
    "publicHolidayRate": 77.38,
    "nightRate": 77.38,
    "ttp": false,
    "quoteRequired": false,
    "description": "Access Community Social and Rec Activ - Standard - Weekday Evening",
    "regionalPricing": {
      "weekday": {
        "national": 77.38,
        "remote": 108.33,
        "veryRemote": 116.07
      },
      "saturday": {
        "national": 77.38,
        "remote": 108.33,
        "veryRemote": 116.07
      },
      "sunday": {
        "national": 77.38,
        "remote": 108.33,
        "veryRemote": 116.07
      },
      "publicHoliday": {
        "national": 77.38,
        "remote": 108.33,
        "veryRemote": 116.07
      }
    }
  },
  "04_104_0125_6_1": {
    "itemNumber": "04_104_0125_6_1",
    "name": "Access Community Social and Rec Activ - Standard",
    "shortName": "Access Community Social and Rec Activ",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 70.23,
    "weekdayEveningRate": 70.23,
    "saturdayRate": 70.23,
    "sundayRate": 70.23,
    "publicHolidayRate": 70.23,
    "nightRate": 70.23,
    "ttp": false,
    "quoteRequired": false,
    "description": "Access Community Social and Rec Activ - Standard - Weekday Daytime",
    "regionalPricing": {
      "weekday": {
        "national": 70.23,
        "remote": 70.23,
        "veryRemote": 98.32
      },
      "saturday": {
        "national": 70.23,
        "remote": 70.23,
        "veryRemote": 98.32
      },
      "sunday": {
        "national": 70.23,
        "remote": 70.23,
        "veryRemote": 98.32
      },
      "publicHoliday": {
        "national": 70.23,
        "remote": 70.23,
        "veryRemote": 98.32
      }
    }
  },
  "04_105_0125_6_1": {
    "itemNumber": "04_105_0125_6_1",
    "name": "Access Community Social and Rec Activ - Standard",
    "shortName": "Access Community Social and Rec Activ",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 98.83,
    "weekdayEveningRate": 98.83,
    "saturdayRate": 98.83,
    "sundayRate": 98.83,
    "publicHolidayRate": 98.83,
    "nightRate": 98.83,
    "ttp": false,
    "quoteRequired": false,
    "description": "Access Community Social and Rec Activ - Standard - Saturday",
    "regionalPricing": {
      "weekday": {
        "national": 98.83,
        "remote": 138.36,
        "veryRemote": 148.25
      },
      "saturday": {
        "national": 98.83,
        "remote": 138.36,
        "veryRemote": 148.25
      },
      "sunday": {
        "national": 98.83,
        "remote": 138.36,
        "veryRemote": 148.25
      },
      "publicHoliday": {
        "national": 98.83,
        "remote": 138.36,
        "veryRemote": 148.25
      }
    }
  },
  "04_106_0125_6_1": {
    "itemNumber": "04_106_0125_6_1",
    "name": "Access Community Social and Rec Activ - Standard",
    "shortName": "Access Community Social and Rec Activ",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 127.43,
    "weekdayEveningRate": 127.43,
    "saturdayRate": 127.43,
    "sundayRate": 127.43,
    "publicHolidayRate": 127.43,
    "nightRate": 127.43,
    "ttp": false,
    "quoteRequired": false,
    "description": "Access Community Social and Rec Activ - Standard - Sunday",
    "regionalPricing": {
      "weekday": {
        "national": 127.43,
        "remote": 178.4,
        "veryRemote": 191.15
      },
      "saturday": {
        "national": 127.43,
        "remote": 178.4,
        "veryRemote": 191.15
      },
      "sunday": {
        "national": 127.43,
        "remote": 178.4,
        "veryRemote": 191.15
      },
      "publicHoliday": {
        "national": 127.43,
        "remote": 178.4,
        "veryRemote": 191.15
      }
    }
  },
  "04_450_0125_1_1": {
    "itemNumber": "04_450_0125_1_1",
    "name": "Intensive and Complex Behaviour Supports",
    "shortName": "Intensive and Complex Behaviour Supports",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 75.98,
    "weekdayEveningRate": 75.98,
    "saturdayRate": 75.98,
    "sundayRate": 75.98,
    "publicHolidayRate": 75.98,
    "nightRate": 75.98,
    "ttp": false,
    "quoteRequired": false,
    "description": "Intensive and Complex Behaviour Supports - Weekday Daytime",
    "regionalPricing": {
      "weekday": {
        "national": 75.98,
        "remote": 106.37,
        "veryRemote": 113.97
      },
      "saturday": {
        "national": 75.98,
        "remote": 106.37,
        "veryRemote": 113.97
      },
      "sunday": {
        "national": 75.98,
        "remote": 106.37,
        "veryRemote": 113.97
      },
      "publicHoliday": {
        "national": 75.98,
        "remote": 106.37,
        "veryRemote": 113.97
      }
    }
  },
  "04_451_0125_1_1": {
    "itemNumber": "04_451_0125_1_1",
    "name": "Intensive and Complex Behaviour Supports",
    "shortName": "Intensive and Complex Behaviour Supports",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 83.72,
    "weekdayEveningRate": 83.72,
    "saturdayRate": 83.72,
    "sundayRate": 83.72,
    "publicHolidayRate": 83.72,
    "nightRate": 83.72,
    "ttp": false,
    "quoteRequired": false,
    "description": "Intensive and Complex Behaviour Supports - Weekday Evening",
    "regionalPricing": {
      "weekday": {
        "national": 83.72,
        "remote": 117.21,
        "veryRemote": 125.58
      },
      "saturday": {
        "national": 83.72,
        "remote": 117.21,
        "veryRemote": 125.58
      },
      "sunday": {
        "national": 83.72,
        "remote": 117.21,
        "veryRemote": 125.58
      },
      "publicHoliday": {
        "national": 83.72,
        "remote": 117.21,
        "veryRemote": 125.58
      }
    }
  },
  "04_452_0125_1_1": {
    "itemNumber": "04_452_0125_1_1",
    "name": "Intensive and Complex Behaviour Supports",
    "shortName": "Intensive and Complex Behaviour Supports",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 106.93,
    "weekdayEveningRate": 106.93,
    "saturdayRate": 106.93,
    "sundayRate": 106.93,
    "publicHolidayRate": 106.93,
    "nightRate": 106.93,
    "ttp": false,
    "quoteRequired": false,
    "description": "Intensive and Complex Behaviour Supports - Saturday",
    "regionalPricing": {
      "weekday": {
        "national": 106.93,
        "remote": 149.7,
        "veryRemote": 160.4
      },
      "saturday": {
        "national": 106.93,
        "remote": 149.7,
        "veryRemote": 160.4
      },
      "sunday": {
        "national": 106.93,
        "remote": 149.7,
        "veryRemote": 160.4
      },
      "publicHoliday": {
        "national": 106.93,
        "remote": 149.7,
        "veryRemote": 160.4
      }
    }
  },
  "04_453_0125_1_1": {
    "itemNumber": "04_453_0125_1_1",
    "name": "Intensive and Complex Behaviour Supports",
    "shortName": "Intensive and Complex Behaviour Supports",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 137.87,
    "weekdayEveningRate": 137.87,
    "saturdayRate": 137.87,
    "sundayRate": 137.87,
    "publicHolidayRate": 137.87,
    "nightRate": 137.87,
    "ttp": false,
    "quoteRequired": false,
    "description": "Intensive and Complex Behaviour Supports - Sunday",
    "regionalPricing": {
      "weekday": {
        "national": 137.87,
        "remote": 193.02,
        "veryRemote": 206.81
      },
      "saturday": {
        "national": 137.87,
        "remote": 193.02,
        "veryRemote": 206.81
      },
      "sunday": {
        "national": 137.87,
        "remote": 193.02,
        "veryRemote": 206.81
      },
      "publicHoliday": {
        "national": 137.87,
        "remote": 193.02,
        "veryRemote": 206.81
      }
    }
  },
  "04_454_0125_1_1": {
    "itemNumber": "04_454_0125_1_1",
    "name": "Intensive and Complex Behaviour Supports",
    "shortName": "Intensive and Complex Behaviour Supports",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 168.81,
    "weekdayEveningRate": 168.81,
    "saturdayRate": 168.81,
    "sundayRate": 168.81,
    "publicHolidayRate": 168.81,
    "nightRate": 168.81,
    "ttp": false,
    "quoteRequired": false,
    "description": "Intensive and Complex Behaviour Supports - Public Holiday",
    "regionalPricing": {
      "weekday": {
        "national": 168.81,
        "remote": 236.33,
        "veryRemote": 253.22
      },
      "saturday": {
        "national": 168.81,
        "remote": 236.33,
        "veryRemote": 253.22
      },
      "sunday": {
        "national": 168.81,
        "remote": 236.33,
        "veryRemote": 253.22
      },
      "publicHoliday": {
        "national": 168.81,
        "remote": 236.33,
        "veryRemote": 253.22
      }
    }
  },
  "04_590_0125_6_1": {
    "itemNumber": "04_590_0125_6_1",
    "name": "Activity Based Transport",
    "shortName": "Activity Based Transport",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Activity Based Transport",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "04_799_0125_6_1": {
    "itemNumber": "04_799_0125_6_1",
    "name": "Provider travel - non-labour costs",
    "shortName": "Provider travel",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provider travel - non-labour costs",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "04_049_0136_1_1": {
    "itemNumber": "04_049_0136_1_1",
    "name": "Establishment Fee for Personal Care/Participation",
    "shortName": "Establishment Fee for Personal Care/Participation",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 702.3,
    "weekdayEveningRate": 702.3,
    "saturdayRate": 702.3,
    "sundayRate": 702.3,
    "publicHolidayRate": 702.3,
    "nightRate": 702.3,
    "ttp": false,
    "quoteRequired": false,
    "description": "Establishment Fee for Personal Care/Participation",
    "regionalPricing": {
      "weekday": {
        "national": 702.3,
        "remote": 983.22,
        "veryRemote": 1053.45
      },
      "saturday": {
        "national": 702.3,
        "remote": 983.22,
        "veryRemote": 1053.45
      },
      "sunday": {
        "national": 702.3,
        "remote": 983.22,
        "veryRemote": 1053.45
      },
      "publicHoliday": {
        "national": 702.3,
        "remote": 983.22,
        "veryRemote": 1053.45
      }
    }
  },
  "04_102_0136_6_1": {
    "itemNumber": "04_102_0136_6_1",
    "name": "Group Activities - Standard",
    "shortName": "Group Activities",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 70.23,
    "weekdayEveningRate": 70.23,
    "saturdayRate": 70.23,
    "sundayRate": 70.23,
    "publicHolidayRate": 70.23,
    "nightRate": 70.23,
    "ttp": false,
    "quoteRequired": false,
    "description": "Group Activities - Standard - Weekday Daytime",
    "regionalPricing": {
      "weekday": {
        "national": 70.23,
        "remote": 70.23,
        "veryRemote": 98.32
      },
      "saturday": {
        "national": 70.23,
        "remote": 70.23,
        "veryRemote": 98.32
      },
      "sunday": {
        "national": 70.23,
        "remote": 70.23,
        "veryRemote": 98.32
      },
      "publicHoliday": {
        "national": 70.23,
        "remote": 70.23,
        "veryRemote": 98.32
      }
    }
  },
  "04_103_0136_6_1": {
    "itemNumber": "04_103_0136_6_1",
    "name": "Group Activities - Standard",
    "shortName": "Group Activities",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 77.38,
    "weekdayEveningRate": 77.38,
    "saturdayRate": 77.38,
    "sundayRate": 77.38,
    "publicHolidayRate": 77.38,
    "nightRate": 77.38,
    "ttp": false,
    "quoteRequired": false,
    "description": "Group Activities - Standard - Weekday Evening",
    "regionalPricing": {
      "weekday": {
        "national": 77.38,
        "remote": 108.33,
        "veryRemote": 116.07
      },
      "saturday": {
        "national": 77.38,
        "remote": 108.33,
        "veryRemote": 116.07
      },
      "sunday": {
        "national": 77.38,
        "remote": 108.33,
        "veryRemote": 116.07
      },
      "publicHoliday": {
        "national": 77.38,
        "remote": 108.33,
        "veryRemote": 116.07
      }
    }
  },
  "04_104_0136_6_1": {
    "itemNumber": "04_104_0136_6_1",
    "name": "Group Activities - Standard",
    "shortName": "Group Activities",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 98.83,
    "weekdayEveningRate": 98.83,
    "saturdayRate": 98.83,
    "sundayRate": 98.83,
    "publicHolidayRate": 98.83,
    "nightRate": 98.83,
    "ttp": false,
    "quoteRequired": false,
    "description": "Group Activities - Standard - Saturday",
    "regionalPricing": {
      "weekday": {
        "national": 98.83,
        "remote": 138.36,
        "veryRemote": 148.25
      },
      "saturday": {
        "national": 98.83,
        "remote": 138.36,
        "veryRemote": 148.25
      },
      "sunday": {
        "national": 98.83,
        "remote": 138.36,
        "veryRemote": 148.25
      },
      "publicHoliday": {
        "national": 98.83,
        "remote": 138.36,
        "veryRemote": 148.25
      }
    }
  },
  "04_105_0136_6_1": {
    "itemNumber": "04_105_0136_6_1",
    "name": "Group Activities - Standard",
    "shortName": "Group Activities",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 127.43,
    "weekdayEveningRate": 127.43,
    "saturdayRate": 127.43,
    "sundayRate": 127.43,
    "publicHolidayRate": 127.43,
    "nightRate": 127.43,
    "ttp": false,
    "quoteRequired": false,
    "description": "Group Activities - Standard - Sunday",
    "regionalPricing": {
      "weekday": {
        "national": 127.43,
        "remote": 178.4,
        "veryRemote": 191.15
      },
      "saturday": {
        "national": 127.43,
        "remote": 178.4,
        "veryRemote": 191.15
      },
      "sunday": {
        "national": 127.43,
        "remote": 178.4,
        "veryRemote": 191.15
      },
      "publicHoliday": {
        "national": 127.43,
        "remote": 178.4,
        "veryRemote": 191.15
      }
    }
  },
  "04_106_0136_6_1": {
    "itemNumber": "04_106_0136_6_1",
    "name": "Group Activities - Standard",
    "shortName": "Group Activities",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 156.03,
    "weekdayEveningRate": 156.03,
    "saturdayRate": 156.03,
    "sundayRate": 156.03,
    "publicHolidayRate": 156.03,
    "nightRate": 156.03,
    "ttp": false,
    "quoteRequired": false,
    "description": "Group Activities - Standard - Public Holiday",
    "regionalPricing": {
      "weekday": {
        "national": 156.03,
        "remote": 218.44,
        "veryRemote": 234.05
      },
      "saturday": {
        "national": 156.03,
        "remote": 218.44,
        "veryRemote": 234.05
      },
      "sunday": {
        "national": 156.03,
        "remote": 218.44,
        "veryRemote": 234.05
      },
      "publicHoliday": {
        "national": 156.03,
        "remote": 218.44,
        "veryRemote": 234.05
      }
    }
  },
  "04_450_0136_1_1": {
    "itemNumber": "04_450_0136_1_1",
    "name": "Intensive and Complex Behaviour Supports",
    "shortName": "Intensive and Complex Behaviour Supports",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 75.98,
    "weekdayEveningRate": 75.98,
    "saturdayRate": 75.98,
    "sundayRate": 75.98,
    "publicHolidayRate": 75.98,
    "nightRate": 75.98,
    "ttp": false,
    "quoteRequired": false,
    "description": "Intensive and Complex Behaviour Supports - Weekday Daytime",
    "regionalPricing": {
      "weekday": {
        "national": 75.98,
        "remote": 106.37,
        "veryRemote": 113.97
      },
      "saturday": {
        "national": 75.98,
        "remote": 106.37,
        "veryRemote": 113.97
      },
      "sunday": {
        "national": 75.98,
        "remote": 106.37,
        "veryRemote": 113.97
      },
      "publicHoliday": {
        "national": 75.98,
        "remote": 106.37,
        "veryRemote": 113.97
      }
    }
  },
  "04_451_0136_1_1": {
    "itemNumber": "04_451_0136_1_1",
    "name": "Intensive and Complex Behaviour Supports",
    "shortName": "Intensive and Complex Behaviour Supports",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 83.72,
    "weekdayEveningRate": 83.72,
    "saturdayRate": 83.72,
    "sundayRate": 83.72,
    "publicHolidayRate": 83.72,
    "nightRate": 83.72,
    "ttp": false,
    "quoteRequired": false,
    "description": "Intensive and Complex Behaviour Supports - Weekday Evening",
    "regionalPricing": {
      "weekday": {
        "national": 83.72,
        "remote": 117.21,
        "veryRemote": 125.58
      },
      "saturday": {
        "national": 83.72,
        "remote": 117.21,
        "veryRemote": 125.58
      },
      "sunday": {
        "national": 83.72,
        "remote": 117.21,
        "veryRemote": 125.58
      },
      "publicHoliday": {
        "national": 83.72,
        "remote": 117.21,
        "veryRemote": 125.58
      }
    }
  },
  "04_452_0136_1_1": {
    "itemNumber": "04_452_0136_1_1",
    "name": "Intensive and Complex Behaviour Supports",
    "shortName": "Intensive and Complex Behaviour Supports",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 106.93,
    "weekdayEveningRate": 106.93,
    "saturdayRate": 106.93,
    "sundayRate": 106.93,
    "publicHolidayRate": 106.93,
    "nightRate": 106.93,
    "ttp": false,
    "quoteRequired": false,
    "description": "Intensive and Complex Behaviour Supports - Saturday",
    "regionalPricing": {
      "weekday": {
        "national": 106.93,
        "remote": 149.7,
        "veryRemote": 160.4
      },
      "saturday": {
        "national": 106.93,
        "remote": 149.7,
        "veryRemote": 160.4
      },
      "sunday": {
        "national": 106.93,
        "remote": 149.7,
        "veryRemote": 160.4
      },
      "publicHoliday": {
        "national": 106.93,
        "remote": 149.7,
        "veryRemote": 160.4
      }
    }
  },
  "04_453_0136_1_1": {
    "itemNumber": "04_453_0136_1_1",
    "name": "Intensive and Complex Behaviour Supports",
    "shortName": "Intensive and Complex Behaviour Supports",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 137.87,
    "weekdayEveningRate": 137.87,
    "saturdayRate": 137.87,
    "sundayRate": 137.87,
    "publicHolidayRate": 137.87,
    "nightRate": 137.87,
    "ttp": false,
    "quoteRequired": false,
    "description": "Intensive and Complex Behaviour Supports - Sunday",
    "regionalPricing": {
      "weekday": {
        "national": 137.87,
        "remote": 193.02,
        "veryRemote": 206.81
      },
      "saturday": {
        "national": 137.87,
        "remote": 193.02,
        "veryRemote": 206.81
      },
      "sunday": {
        "national": 137.87,
        "remote": 193.02,
        "veryRemote": 206.81
      },
      "publicHoliday": {
        "national": 137.87,
        "remote": 193.02,
        "veryRemote": 206.81
      }
    }
  },
  "04_454_0136_1_1": {
    "itemNumber": "04_454_0136_1_1",
    "name": "Intensive and Complex Behaviour Supports",
    "shortName": "Intensive and Complex Behaviour Supports",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 168.81,
    "weekdayEveningRate": 168.81,
    "saturdayRate": 168.81,
    "sundayRate": 168.81,
    "publicHolidayRate": 168.81,
    "nightRate": 168.81,
    "ttp": false,
    "quoteRequired": false,
    "description": "Intensive and Complex Behaviour Supports - Public Holiday",
    "regionalPricing": {
      "weekday": {
        "national": 168.81,
        "remote": 236.33,
        "veryRemote": 253.22
      },
      "saturday": {
        "national": 168.81,
        "remote": 236.33,
        "veryRemote": 253.22
      },
      "sunday": {
        "national": 168.81,
        "remote": 236.33,
        "veryRemote": 253.22
      },
      "publicHoliday": {
        "national": 168.81,
        "remote": 236.33,
        "veryRemote": 253.22
      }
    }
  },
  "04_591_0136_6_1": {
    "itemNumber": "04_591_0136_6_1",
    "name": "Activity Based Transport",
    "shortName": "Activity Based Transport",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Activity Based Transport",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "04_599_0136_6_1": {
    "itemNumber": "04_599_0136_6_1",
    "name": "Centre Capital Cost",
    "shortName": "Centre Capital Cost",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 2.59,
    "weekdayEveningRate": 2.59,
    "saturdayRate": 2.59,
    "sundayRate": 2.59,
    "publicHolidayRate": 2.59,
    "nightRate": 2.59,
    "ttp": false,
    "quoteRequired": false,
    "description": "Centre Capital Cost",
    "regionalPricing": {
      "weekday": {
        "national": 2.59,
        "remote": 3.63,
        "veryRemote": 3.89
      },
      "saturday": {
        "national": 2.59,
        "remote": 3.63,
        "veryRemote": 3.89
      },
      "sunday": {
        "national": 2.59,
        "remote": 3.63,
        "veryRemote": 3.89
      },
      "publicHoliday": {
        "national": 2.59,
        "remote": 3.63,
        "veryRemote": 3.89
      }
    }
  },
  "04_799_0136_6_1": {
    "itemNumber": "04_799_0136_6_1",
    "name": "Provider travel - non-labour costs",
    "shortName": "Provider travel",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provider travel - non-labour costs",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "01_016_0104_1_1": {
    "itemNumber": "01_016_0104_1_1",
    "name": "Specialised Home Based Assistance For A Child",
    "shortName": "Specialised Home Based Assistance For A Child",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 59.06,
    "weekdayEveningRate": 59.06,
    "saturdayRate": 59.06,
    "sundayRate": 59.06,
    "publicHolidayRate": 59.06,
    "nightRate": 59.06,
    "ttp": false,
    "quoteRequired": false,
    "description": "Specialised Home Based Assistance For A Child",
    "regionalPricing": {
      "weekday": {
        "national": 59.06,
        "remote": 82.68,
        "veryRemote": 88.59
      },
      "saturday": {
        "national": 59.06,
        "remote": 82.68,
        "veryRemote": 88.59
      },
      "sunday": {
        "national": 59.06,
        "remote": 82.68,
        "veryRemote": 88.59
      },
      "publicHoliday": {
        "national": 59.06,
        "remote": 82.68,
        "veryRemote": 88.59
      }
    }
  },
  "01_049_0104_1_1": {
    "itemNumber": "01_049_0104_1_1",
    "name": "Establishment Fee for Personal Care/Participation",
    "shortName": "Establishment Fee for Personal Care/Participation",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 702.3,
    "weekdayEveningRate": 702.3,
    "saturdayRate": 702.3,
    "sundayRate": 702.3,
    "publicHolidayRate": 702.3,
    "nightRate": 702.3,
    "ttp": false,
    "quoteRequired": false,
    "description": "Establishment Fee for Personal Care/Participation",
    "regionalPricing": {
      "weekday": {
        "national": 702.3,
        "remote": 983.22,
        "veryRemote": 1053.45
      },
      "saturday": {
        "national": 702.3,
        "remote": 983.22,
        "veryRemote": 1053.45
      },
      "sunday": {
        "national": 702.3,
        "remote": 983.22,
        "veryRemote": 1053.45
      },
      "publicHoliday": {
        "national": 702.3,
        "remote": 983.22,
        "veryRemote": 1053.45
      }
    }
  },
  "01_400_0104_1_1": {
    "itemNumber": "01_400_0104_1_1",
    "name": "Assistance With Self-Care Activities - High Intensity",
    "shortName": "Assistance With Self-Care Activities",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 75.98,
    "weekdayEveningRate": 75.98,
    "saturdayRate": 75.98,
    "sundayRate": 75.98,
    "publicHolidayRate": 75.98,
    "nightRate": 75.98,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assistance With Self-Care Activities - High Intensity - Weekday Daytime",
    "regionalPricing": {
      "weekday": {
        "national": 75.98,
        "remote": 106.37,
        "veryRemote": 113.97
      },
      "saturday": {
        "national": 75.98,
        "remote": 106.37,
        "veryRemote": 113.97
      },
      "sunday": {
        "national": 75.98,
        "remote": 106.37,
        "veryRemote": 113.97
      },
      "publicHoliday": {
        "national": 75.98,
        "remote": 106.37,
        "veryRemote": 113.97
      }
    }
  },
  "01_401_0104_1_1": {
    "itemNumber": "01_401_0104_1_1",
    "name": "Assistance With Self-Care Activities - High Intensity",
    "shortName": "Assistance With Self-Care Activities",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 83.72,
    "weekdayEveningRate": 83.72,
    "saturdayRate": 83.72,
    "sundayRate": 83.72,
    "publicHolidayRate": 83.72,
    "nightRate": 83.72,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assistance With Self-Care Activities - High Intensity - Weekday Evening",
    "regionalPricing": {
      "weekday": {
        "national": 83.72,
        "remote": 117.21,
        "veryRemote": 125.58
      },
      "saturday": {
        "national": 83.72,
        "remote": 117.21,
        "veryRemote": 125.58
      },
      "sunday": {
        "national": 83.72,
        "remote": 117.21,
        "veryRemote": 125.58
      },
      "publicHoliday": {
        "national": 83.72,
        "remote": 117.21,
        "veryRemote": 125.58
      }
    }
  },
  "01_402_0104_1_1": {
    "itemNumber": "01_402_0104_1_1",
    "name": "Assistance With Self-Care Activities - High Intensity",
    "shortName": "Assistance With Self-Care Activities",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 106.93,
    "weekdayEveningRate": 106.93,
    "saturdayRate": 106.93,
    "sundayRate": 106.93,
    "publicHolidayRate": 106.93,
    "nightRate": 106.93,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assistance With Self-Care Activities - High Intensity - Saturday",
    "regionalPricing": {
      "weekday": {
        "national": 106.93,
        "remote": 149.7,
        "veryRemote": 160.4
      },
      "saturday": {
        "national": 106.93,
        "remote": 149.7,
        "veryRemote": 160.4
      },
      "sunday": {
        "national": 106.93,
        "remote": 149.7,
        "veryRemote": 160.4
      },
      "publicHoliday": {
        "national": 106.93,
        "remote": 149.7,
        "veryRemote": 160.4
      }
    }
  },
  "01_403_0104_1_1": {
    "itemNumber": "01_403_0104_1_1",
    "name": "Assistance With Self-Care Activities - High Intensity",
    "shortName": "Assistance With Self-Care Activities",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 137.87,
    "weekdayEveningRate": 137.87,
    "saturdayRate": 137.87,
    "sundayRate": 137.87,
    "publicHolidayRate": 137.87,
    "nightRate": 137.87,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assistance With Self-Care Activities - High Intensity - Sunday",
    "regionalPricing": {
      "weekday": {
        "national": 137.87,
        "remote": 193.02,
        "veryRemote": 206.81
      },
      "saturday": {
        "national": 137.87,
        "remote": 193.02,
        "veryRemote": 206.81
      },
      "sunday": {
        "national": 137.87,
        "remote": 193.02,
        "veryRemote": 206.81
      },
      "publicHoliday": {
        "national": 137.87,
        "remote": 193.02,
        "veryRemote": 206.81
      }
    }
  },
  "01_404_0104_1_1": {
    "itemNumber": "01_404_0104_1_1",
    "name": "Assistance With Self-Care Activities - High Intensity",
    "shortName": "Assistance With Self-Care Activities",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 168.81,
    "weekdayEveningRate": 168.81,
    "saturdayRate": 168.81,
    "sundayRate": 168.81,
    "publicHolidayRate": 168.81,
    "nightRate": 168.81,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assistance With Self-Care Activities - High Intensity - Public Holiday",
    "regionalPricing": {
      "weekday": {
        "national": 168.81,
        "remote": 236.33,
        "veryRemote": 253.22
      },
      "saturday": {
        "national": 168.81,
        "remote": 236.33,
        "veryRemote": 253.22
      },
      "sunday": {
        "national": 168.81,
        "remote": 236.33,
        "veryRemote": 253.22
      },
      "publicHoliday": {
        "national": 168.81,
        "remote": 236.33,
        "veryRemote": 253.22
      }
    }
  },
  "01_405_0104_1_1": {
    "itemNumber": "01_405_0104_1_1",
    "name": "Assistance With Self-Care Activities - High Intensity",
    "shortName": "Assistance With Self-Care Activities",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 85.27,
    "weekdayEveningRate": 85.27,
    "saturdayRate": 85.27,
    "sundayRate": 85.27,
    "publicHolidayRate": 85.27,
    "nightRate": 85.27,
    "ttp": false,
    "quoteRequired": false,
    "description": "Assistance With Self-Care Activities - High Intensity - Weekday Night",
    "regionalPricing": {
      "weekday": {
        "national": 85.27,
        "remote": 119.38,
        "veryRemote": 127.91
      },
      "saturday": {
        "national": 85.27,
        "remote": 119.38,
        "veryRemote": 127.91
      },
      "sunday": {
        "national": 85.27,
        "remote": 119.38,
        "veryRemote": 127.91
      },
      "publicHoliday": {
        "national": 85.27,
        "remote": 119.38,
        "veryRemote": 127.91
      }
    }
  },
  "01_799_0104_1_1": {
    "itemNumber": "01_799_0104_1_1",
    "name": "Provider travel - non-labour costs",
    "shortName": "Provider travel",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provider travel - non-labour costs",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "04_049_0104_1_1": {
    "itemNumber": "04_049_0104_1_1",
    "name": "Establishment Fee for Personal Care/Participation",
    "shortName": "Establishment Fee for Personal Care/Participation",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 702.3,
    "weekdayEveningRate": 702.3,
    "saturdayRate": 702.3,
    "sundayRate": 702.3,
    "publicHolidayRate": 702.3,
    "nightRate": 702.3,
    "ttp": false,
    "quoteRequired": false,
    "description": "Establishment Fee for Personal Care/Participation",
    "regionalPricing": {
      "weekday": {
        "national": 702.3,
        "remote": 983.22,
        "veryRemote": 1053.45
      },
      "saturday": {
        "national": 702.3,
        "remote": 983.22,
        "veryRemote": 1053.45
      },
      "sunday": {
        "national": 702.3,
        "remote": 983.22,
        "veryRemote": 1053.45
      },
      "publicHoliday": {
        "national": 702.3,
        "remote": 983.22,
        "veryRemote": 1053.45
      }
    }
  },
  "04_400_0104_1_1": {
    "itemNumber": "04_400_0104_1_1",
    "name": "Access Community Social and Rec Activ - High Intensity",
    "shortName": "Access Community Social and Rec Activ",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 75.98,
    "weekdayEveningRate": 75.98,
    "saturdayRate": 75.98,
    "sundayRate": 75.98,
    "publicHolidayRate": 75.98,
    "nightRate": 75.98,
    "ttp": false,
    "quoteRequired": false,
    "description": "Access Community Social and Rec Activ - High Intensity - Weekday Daytime",
    "regionalPricing": {
      "weekday": {
        "national": 75.98,
        "remote": 106.37,
        "veryRemote": 113.97
      },
      "saturday": {
        "national": 75.98,
        "remote": 106.37,
        "veryRemote": 113.97
      },
      "sunday": {
        "national": 75.98,
        "remote": 106.37,
        "veryRemote": 113.97
      },
      "publicHoliday": {
        "national": 75.98,
        "remote": 106.37,
        "veryRemote": 113.97
      }
    }
  },
  "04_401_0104_1_1": {
    "itemNumber": "04_401_0104_1_1",
    "name": "Access Community Social and Rec Activ - High Intensity",
    "shortName": "Access Community Social and Rec Activ",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 83.72,
    "weekdayEveningRate": 83.72,
    "saturdayRate": 83.72,
    "sundayRate": 83.72,
    "publicHolidayRate": 83.72,
    "nightRate": 83.72,
    "ttp": false,
    "quoteRequired": false,
    "description": "Access Community Social and Rec Activ - High Intensity - Weekday Evening",
    "regionalPricing": {
      "weekday": {
        "national": 83.72,
        "remote": 117.21,
        "veryRemote": 125.58
      },
      "saturday": {
        "national": 83.72,
        "remote": 117.21,
        "veryRemote": 125.58
      },
      "sunday": {
        "national": 83.72,
        "remote": 117.21,
        "veryRemote": 125.58
      },
      "publicHoliday": {
        "national": 83.72,
        "remote": 117.21,
        "veryRemote": 125.58
      }
    }
  },
  "04_402_0104_1_1": {
    "itemNumber": "04_402_0104_1_1",
    "name": "Access Community Social and Rec Activ - High Intensity",
    "shortName": "Access Community Social and Rec Activ",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 106.93,
    "weekdayEveningRate": 106.93,
    "saturdayRate": 106.93,
    "sundayRate": 106.93,
    "publicHolidayRate": 106.93,
    "nightRate": 106.93,
    "ttp": false,
    "quoteRequired": false,
    "description": "Access Community Social and Rec Activ - High Intensity - Saturday",
    "regionalPricing": {
      "weekday": {
        "national": 106.93,
        "remote": 149.7,
        "veryRemote": 160.4
      },
      "saturday": {
        "national": 106.93,
        "remote": 149.7,
        "veryRemote": 160.4
      },
      "sunday": {
        "national": 106.93,
        "remote": 149.7,
        "veryRemote": 160.4
      },
      "publicHoliday": {
        "national": 106.93,
        "remote": 149.7,
        "veryRemote": 160.4
      }
    }
  },
  "04_403_0104_1_1": {
    "itemNumber": "04_403_0104_1_1",
    "name": "Access Community Social and Rec Activ - High Intensity",
    "shortName": "Access Community Social and Rec Activ",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 137.87,
    "weekdayEveningRate": 137.87,
    "saturdayRate": 137.87,
    "sundayRate": 137.87,
    "publicHolidayRate": 137.87,
    "nightRate": 137.87,
    "ttp": false,
    "quoteRequired": false,
    "description": "Access Community Social and Rec Activ - High Intensity - Sunday",
    "regionalPricing": {
      "weekday": {
        "national": 137.87,
        "remote": 193.02,
        "veryRemote": 206.81
      },
      "saturday": {
        "national": 137.87,
        "remote": 193.02,
        "veryRemote": 206.81
      },
      "sunday": {
        "national": 137.87,
        "remote": 193.02,
        "veryRemote": 206.81
      },
      "publicHoliday": {
        "national": 137.87,
        "remote": 193.02,
        "veryRemote": 206.81
      }
    }
  },
  "04_404_0104_1_1": {
    "itemNumber": "04_404_0104_1_1",
    "name": "Access Community Social and Rec Activ - High Intensity",
    "shortName": "Access Community Social and Rec Activ",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 168.81,
    "weekdayEveningRate": 168.81,
    "saturdayRate": 168.81,
    "sundayRate": 168.81,
    "publicHolidayRate": 168.81,
    "nightRate": 168.81,
    "ttp": false,
    "quoteRequired": false,
    "description": "Access Community Social and Rec Activ - High Intensity - Public Holiday",
    "regionalPricing": {
      "weekday": {
        "national": 168.81,
        "remote": 236.33,
        "veryRemote": 253.22
      },
      "saturday": {
        "national": 168.81,
        "remote": 236.33,
        "veryRemote": 253.22
      },
      "sunday": {
        "national": 168.81,
        "remote": 236.33,
        "veryRemote": 253.22
      },
      "publicHoliday": {
        "national": 168.81,
        "remote": 236.33,
        "veryRemote": 253.22
      }
    }
  },
  "04_592_0104_6_1": {
    "itemNumber": "04_592_0104_6_1",
    "name": "Activity Based Transport",
    "shortName": "Activity Based Transport",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Activity Based Transport",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "04_599_0104_6_1": {
    "itemNumber": "04_599_0104_6_1",
    "name": "Centre Capital Cost",
    "shortName": "Centre Capital Cost",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 2.59,
    "weekdayEveningRate": 2.59,
    "saturdayRate": 2.59,
    "sundayRate": 2.59,
    "publicHolidayRate": 2.59,
    "nightRate": 2.59,
    "ttp": false,
    "quoteRequired": false,
    "description": "Centre Capital Cost",
    "regionalPricing": {
      "weekday": {
        "national": 2.59,
        "remote": 3.63,
        "veryRemote": 3.89
      },
      "saturday": {
        "national": 2.59,
        "remote": 3.63,
        "veryRemote": 3.89
      },
      "sunday": {
        "national": 2.59,
        "remote": 3.63,
        "veryRemote": 3.89
      },
      "publicHoliday": {
        "national": 2.59,
        "remote": 3.63,
        "veryRemote": 3.89
      }
    }
  },
  "04_600_0104_6_1": {
    "itemNumber": "04_600_0104_6_1",
    "name": "Group Activities - High Intensity",
    "shortName": "Group Activities",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 75.98,
    "weekdayEveningRate": 75.98,
    "saturdayRate": 75.98,
    "sundayRate": 75.98,
    "publicHolidayRate": 75.98,
    "nightRate": 75.98,
    "ttp": false,
    "quoteRequired": false,
    "description": "Group Activities - High Intensity - Weekday Daytime",
    "regionalPricing": {
      "weekday": {
        "national": 75.98,
        "remote": 106.37,
        "veryRemote": 113.97
      },
      "saturday": {
        "national": 75.98,
        "remote": 106.37,
        "veryRemote": 113.97
      },
      "sunday": {
        "national": 75.98,
        "remote": 106.37,
        "veryRemote": 113.97
      },
      "publicHoliday": {
        "national": 75.98,
        "remote": 106.37,
        "veryRemote": 113.97
      }
    }
  },
  "04_601_0104_6_1": {
    "itemNumber": "04_601_0104_6_1",
    "name": "Group Activities - High Intensity",
    "shortName": "Group Activities",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 83.72,
    "weekdayEveningRate": 83.72,
    "saturdayRate": 83.72,
    "sundayRate": 83.72,
    "publicHolidayRate": 83.72,
    "nightRate": 83.72,
    "ttp": false,
    "quoteRequired": false,
    "description": "Group Activities - High Intensity - Weekday Evening",
    "regionalPricing": {
      "weekday": {
        "national": 83.72,
        "remote": 117.21,
        "veryRemote": 125.58
      },
      "saturday": {
        "national": 83.72,
        "remote": 117.21,
        "veryRemote": 125.58
      },
      "sunday": {
        "national": 83.72,
        "remote": 117.21,
        "veryRemote": 125.58
      },
      "publicHoliday": {
        "national": 83.72,
        "remote": 117.21,
        "veryRemote": 125.58
      }
    }
  },
  "04_602_0104_6_1": {
    "itemNumber": "04_602_0104_6_1",
    "name": "Group Activities - High Intensity",
    "shortName": "Group Activities",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 106.93,
    "weekdayEveningRate": 106.93,
    "saturdayRate": 106.93,
    "sundayRate": 106.93,
    "publicHolidayRate": 106.93,
    "nightRate": 106.93,
    "ttp": false,
    "quoteRequired": false,
    "description": "Group Activities - High Intensity - Saturday",
    "regionalPricing": {
      "weekday": {
        "national": 106.93,
        "remote": 149.7,
        "veryRemote": 160.4
      },
      "saturday": {
        "national": 106.93,
        "remote": 149.7,
        "veryRemote": 160.4
      },
      "sunday": {
        "national": 106.93,
        "remote": 149.7,
        "veryRemote": 160.4
      },
      "publicHoliday": {
        "national": 106.93,
        "remote": 149.7,
        "veryRemote": 160.4
      }
    }
  },
  "04_603_0104_6_1": {
    "itemNumber": "04_603_0104_6_1",
    "name": "Group Activities - High Intensity",
    "shortName": "Group Activities",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 137.87,
    "weekdayEveningRate": 137.87,
    "saturdayRate": 137.87,
    "sundayRate": 137.87,
    "publicHolidayRate": 137.87,
    "nightRate": 137.87,
    "ttp": false,
    "quoteRequired": false,
    "description": "Group Activities - High Intensity - Sunday",
    "regionalPricing": {
      "weekday": {
        "national": 137.87,
        "remote": 193.02,
        "veryRemote": 206.81
      },
      "saturday": {
        "national": 137.87,
        "remote": 193.02,
        "veryRemote": 206.81
      },
      "sunday": {
        "national": 137.87,
        "remote": 193.02,
        "veryRemote": 206.81
      },
      "publicHoliday": {
        "national": 137.87,
        "remote": 193.02,
        "veryRemote": 206.81
      }
    }
  },
  "04_604_0104_6_1": {
    "itemNumber": "04_604_0104_6_1",
    "name": "Group Activities - High Intensity",
    "shortName": "Group Activities",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 168.81,
    "weekdayEveningRate": 168.81,
    "saturdayRate": 168.81,
    "sundayRate": 168.81,
    "publicHolidayRate": 168.81,
    "nightRate": 168.81,
    "ttp": false,
    "quoteRequired": false,
    "description": "Group Activities - High Intensity - Public Holiday",
    "regionalPricing": {
      "weekday": {
        "national": 168.81,
        "remote": 236.33,
        "veryRemote": 253.22
      },
      "saturday": {
        "national": 168.81,
        "remote": 236.33,
        "veryRemote": 253.22
      },
      "sunday": {
        "national": 168.81,
        "remote": 236.33,
        "veryRemote": 253.22
      },
      "publicHoliday": {
        "national": 168.81,
        "remote": 236.33,
        "veryRemote": 253.22
      }
    }
  },
  "04_799_0104_6_1": {
    "itemNumber": "04_799_0104_6_1",
    "name": "Provider travel - non-labour costs",
    "shortName": "Provider travel",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provider travel - non-labour costs",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "04_049_0133_5_1": {
    "itemNumber": "04_049_0133_5_1",
    "name": "Establishment Fee for Personal Care/Participation",
    "shortName": "Establishment Fee for Personal Care/Participation",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 702.3,
    "weekdayEveningRate": 702.3,
    "saturdayRate": 702.3,
    "sundayRate": 702.3,
    "publicHolidayRate": 702.3,
    "nightRate": 702.3,
    "ttp": false,
    "quoteRequired": false,
    "description": "Establishment Fee for Personal Care/Participation",
    "regionalPricing": {
      "weekday": {
        "national": 702.3,
        "remote": 983.22,
        "veryRemote": 1053.45
      },
      "saturday": {
        "national": 702.3,
        "remote": 983.22,
        "veryRemote": 1053.45
      },
      "sunday": {
        "national": 702.3,
        "remote": 983.22,
        "veryRemote": 1053.45
      },
      "publicHoliday": {
        "national": 702.3,
        "remote": 983.22,
        "veryRemote": 1053.45
      }
    }
  },
  "04_599_0133_5_1": {
    "itemNumber": "04_599_0133_5_1",
    "name": "Centre Capital Cost",
    "shortName": "Centre Capital Cost",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 2.59,
    "weekdayEveningRate": 2.59,
    "saturdayRate": 2.59,
    "sundayRate": 2.59,
    "publicHolidayRate": 2.59,
    "nightRate": 2.59,
    "ttp": false,
    "quoteRequired": false,
    "description": "Centre Capital Cost",
    "regionalPricing": {
      "weekday": {
        "national": 2.59,
        "remote": 3.63,
        "veryRemote": 3.89
      },
      "saturday": {
        "national": 2.59,
        "remote": 3.63,
        "veryRemote": 3.89
      },
      "sunday": {
        "national": 2.59,
        "remote": 3.63,
        "veryRemote": 3.89
      },
      "publicHoliday": {
        "national": 2.59,
        "remote": 3.63,
        "veryRemote": 3.89
      }
    }
  },
  "04_799_0133_5_1": {
    "itemNumber": "04_799_0133_5_1",
    "name": "Provider travel - non-labour costs",
    "shortName": "Provider travel",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provider travel - non-labour costs",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "04_801_0133_5_1": {
    "itemNumber": "04_801_0133_5_1",
    "name": "Supports in Employment",
    "shortName": "Supports in Employment",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 70.23,
    "weekdayEveningRate": 70.23,
    "saturdayRate": 70.23,
    "sundayRate": 70.23,
    "publicHolidayRate": 70.23,
    "nightRate": 70.23,
    "ttp": false,
    "quoteRequired": false,
    "description": "Supports in Employment - Weekday Daytime",
    "regionalPricing": {
      "weekday": {
        "national": 70.23,
        "remote": 70.23,
        "veryRemote": 98.32
      },
      "saturday": {
        "national": 70.23,
        "remote": 70.23,
        "veryRemote": 98.32
      },
      "sunday": {
        "national": 70.23,
        "remote": 70.23,
        "veryRemote": 98.32
      },
      "publicHoliday": {
        "national": 70.23,
        "remote": 70.23,
        "veryRemote": 98.32
      }
    }
  },
  "04_802_0133_5_1": {
    "itemNumber": "04_802_0133_5_1",
    "name": "Supports in Employment",
    "shortName": "Supports in Employment",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 77.38,
    "weekdayEveningRate": 77.38,
    "saturdayRate": 77.38,
    "sundayRate": 77.38,
    "publicHolidayRate": 77.38,
    "nightRate": 77.38,
    "ttp": false,
    "quoteRequired": false,
    "description": "Supports in Employment - Weekday Evening",
    "regionalPricing": {
      "weekday": {
        "national": 77.38,
        "remote": 108.33,
        "veryRemote": 116.07
      },
      "saturday": {
        "national": 77.38,
        "remote": 108.33,
        "veryRemote": 116.07
      },
      "sunday": {
        "national": 77.38,
        "remote": 108.33,
        "veryRemote": 116.07
      },
      "publicHoliday": {
        "national": 77.38,
        "remote": 108.33,
        "veryRemote": 116.07
      }
    }
  },
  "04_803_0133_5_1": {
    "itemNumber": "04_803_0133_5_1",
    "name": "Supports in Employment",
    "shortName": "Supports in Employment",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 98.83,
    "weekdayEveningRate": 98.83,
    "saturdayRate": 98.83,
    "sundayRate": 98.83,
    "publicHolidayRate": 98.83,
    "nightRate": 98.83,
    "ttp": false,
    "quoteRequired": false,
    "description": "Supports in Employment - Saturday",
    "regionalPricing": {
      "weekday": {
        "national": 98.83,
        "remote": 138.36,
        "veryRemote": 148.25
      },
      "saturday": {
        "national": 98.83,
        "remote": 138.36,
        "veryRemote": 148.25
      },
      "sunday": {
        "national": 98.83,
        "remote": 138.36,
        "veryRemote": 148.25
      },
      "publicHoliday": {
        "national": 98.83,
        "remote": 138.36,
        "veryRemote": 148.25
      }
    }
  },
  "04_804_0133_5_1": {
    "itemNumber": "04_804_0133_5_1",
    "name": "Supports in Employment",
    "shortName": "Supports in Employment",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 127.43,
    "weekdayEveningRate": 127.43,
    "saturdayRate": 127.43,
    "sundayRate": 127.43,
    "publicHolidayRate": 127.43,
    "nightRate": 127.43,
    "ttp": false,
    "quoteRequired": false,
    "description": "Supports in Employment - Sunday",
    "regionalPricing": {
      "weekday": {
        "national": 127.43,
        "remote": 178.4,
        "veryRemote": 191.15
      },
      "saturday": {
        "national": 127.43,
        "remote": 178.4,
        "veryRemote": 191.15
      },
      "sunday": {
        "national": 127.43,
        "remote": 178.4,
        "veryRemote": 191.15
      },
      "publicHoliday": {
        "national": 127.43,
        "remote": 178.4,
        "veryRemote": 191.15
      }
    }
  },
  "04_805_0133_5_1": {
    "itemNumber": "04_805_0133_5_1",
    "name": "Supports in Employment",
    "shortName": "Supports in Employment",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 156.03,
    "weekdayEveningRate": 156.03,
    "saturdayRate": 156.03,
    "sundayRate": 156.03,
    "publicHolidayRate": 156.03,
    "nightRate": 156.03,
    "ttp": false,
    "quoteRequired": false,
    "description": "Supports in Employment - Public Holiday",
    "regionalPricing": {
      "weekday": {
        "national": 156.03,
        "remote": 218.44,
        "veryRemote": 234.05
      },
      "saturday": {
        "national": 156.03,
        "remote": 218.44,
        "veryRemote": 234.05
      },
      "sunday": {
        "national": 156.03,
        "remote": 218.44,
        "veryRemote": 234.05
      },
      "publicHoliday": {
        "national": 156.03,
        "remote": 218.44,
        "veryRemote": 234.05
      }
    }
  },
  "10_590_0133_5_3": {
    "itemNumber": "10_590_0133_5_3",
    "name": "Activity Based Transport",
    "shortName": "Activity Based Transport",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Activity Based Transport",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "10_599_0133_5_3": {
    "itemNumber": "10_599_0133_5_3",
    "name": "Centre Capital Cost",
    "shortName": "Centre Capital Cost",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 2.59,
    "weekdayEveningRate": 2.59,
    "saturdayRate": 2.59,
    "sundayRate": 2.59,
    "publicHolidayRate": 2.59,
    "nightRate": 2.59,
    "ttp": false,
    "quoteRequired": false,
    "description": "Centre Capital Cost",
    "regionalPricing": {
      "weekday": {
        "national": 2.59,
        "remote": 3.63,
        "veryRemote": 3.89
      },
      "saturday": {
        "national": 2.59,
        "remote": 3.63,
        "veryRemote": 3.89
      },
      "sunday": {
        "national": 2.59,
        "remote": 3.63,
        "veryRemote": 3.89
      },
      "publicHoliday": {
        "national": 2.59,
        "remote": 3.63,
        "veryRemote": 3.89
      }
    }
  },
  "10_799_0133_5_3": {
    "itemNumber": "10_799_0133_5_3",
    "name": "Provider travel - non-labour costs",
    "shortName": "Provider travel",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "each",
    "weekdayRate": 1,
    "weekdayEveningRate": 1,
    "saturdayRate": 1,
    "sundayRate": 1,
    "publicHolidayRate": 1,
    "nightRate": 1,
    "ttp": false,
    "quoteRequired": false,
    "description": "Provider travel - non-labour costs",
    "regionalPricing": {
      "weekday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "saturday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "sunday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      },
      "publicHoliday": {
        "national": 1,
        "remote": 1,
        "veryRemote": 1
      }
    }
  },
  "10_806_0133_5_1": {
    "itemNumber": "10_806_0133_5_1",
    "name": "Supports in Employment",
    "shortName": "Supports in Employment",
    "category": "core-supports",
    "subcategory": "daily-activities",
    "unit": "hour",
    "weekdayRate": 70.23,
    "weekdayEveningRate": 70.23,
    "saturdayRate": 70.23,
    "sundayRate": 70.23,
    "publicHolidayRate": 70.23,
    "nightRate": 70.23,
    "ttp": false,
    "quoteRequired": false,
    "description": "Supports in Employment - Weekday Daytime",
    "regionalPricing": {
      "weekday": {
        "national": 70.23,
        "remote": 98.32,
        "veryRemote": 105.35
      },
      "saturday": {
        "national": 70.23,
        "remote": 98.32,
        "veryRemote": 105.35
      },
      "sunday": {
        "national": 70.23,
        "remote": 98.32,
        "veryRemote": 105.35
      },
      "publicHoliday": {
        "national": 70.23,
        "remote": 98.32,
        "veryRemote": 105.35
      }
    }
  }
};

// Regional multipliers (for reference, actual regional prices are in regionalPricing)
export const REGIONAL_MULTIPLIERS = {
    metro: 1.0,
    regional: 1.4,  // Approximate based on remote_price
    'very-remote': 1.5  // Approximate based on very_remote_price
};

// Helper function to get regional rate
export function getRegionalRate(baseRate, region) {
    if (!baseRate) return 0;
    const multiplier = REGIONAL_MULTIPLIERS[region] || 1.0;
    return baseRate * multiplier;
}

// Format currency for display
export function formatCurrency(amount) {
    if (amount === null || amount === undefined) return '$0.00';
    return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: 'AUD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(amount);
}

// Search function
export function searchSupportItems(query) {
    const lowerQuery = query.toLowerCase();
    return Object.values(SUPPORT_ITEMS).filter(item => 
        item.itemNumber.toLowerCase().includes(lowerQuery) ||
        item.name.toLowerCase().includes(lowerQuery) ||
        item.description.toLowerCase().includes(lowerQuery)
    );
}

// Get support item by number
export function getSupportItem(itemNumber) {
    return SUPPORT_ITEMS[itemNumber] || null;
}
